import { WEATHER_API_KEY, USER_API_BASE_URL, NEWS_API_KEY, COMPANIESHOUSE_API_KEY, LINKEDIN_CLIENT_ID, SESSION_API, getApiBaseUrl, updateApiBaseUrls, AI_API_URL } from './constant';
import { AVATAR_UPDATED, GET_ERRORS, USER_LOADING, NEW_USER_ADDED, DATA_QUERY, LOGGED_OUT, SUCCESS, HISTORY_RETRIEVED, WEATHER_RETRIEVED, DOWNLOAD_AVATAR, UPDATE_GOOGLE_MAIL_TOKEN, SHOW_AUTH_MODAL, FAILED, REQUEST, REGISTER_SUCCESS, DEFAULT_AVATAR, NEWS_UPDATED, HIDE_LOCK_MODAL, SHOW_REGISTER_MODAL, GOOGLE_AUTH_FAILED, MAIL_RETRIEVED, UPDATE_MAIL_COUNT, UPDATE_ACHIEVEMENTS, BACK_TO_LOGIN, SWITCH_COMPANY_FAILED, SWITCH_COMPANY_SUCCESSFUL, HIDE_PASSWORD_RESET_MODAL, UPDATE_PRODUCTS, HIDE_LOADING_MODAL, SHOW_LOADING_MODAL, PENDING_REFRESH, SET_STICKY_NOTES, SHOW_OTP, UPDATED_INVITATIONS, UPDATED_MESSAGES, UPDATED_CALENDAR_ENTRIES, HIDE_ALL_MODALS, HANDLE_FAVOURITE, REFRESH, UPDATE_MAIL_ACCOUNT, CLEAR_MAIL_AUTH, UPDATE_GOOGLE_ACCOUNT, REMOVE_INTEGRATION, PAYMENT_REQUIRED, UPDATE_USER } from "@reduxLocal/actions/types";
import { store } from '@reduxLocal/persistState';
import axios from 'axios';
import Constants from 'expo-constants';
import { GoogleCalTypes } from '../src/components/Global/types';
import { Alert, Platform } from 'react-native';
import * as Linking from 'expo-linking';
import { saveLog } from '@reduxLocal/persistState';
import { addToQueue, getQueue, clearQueue, removeProcessedRequest } from './utils/requestQueue';
import { monitorNetworkStatus } from './utils/networkManager';
import { addAbortController, removeAbortController } from '@utils/abortController';
import paymentDispatcher from '@utils/paymentDispatcher';
import { content } from 'html2canvas/dist/types/css/property-descriptors/content';
import { apiInterceptor } from './utils/optimizedInterceptor';

//TODO create decentralised redux dispatches for all types
//TODO remove named exports or remove default exports!

let isOnline = true;
let cancelTokenSource = axios.CancelToken.source();
let modalTimeout: NodeJS.Timeout | null = null;

// Centralized error handler to prevent modal conflicts
const dispatchErrorModal = (errorMessage: string, details?: any, delay: number = 2000) => {
	// Clear any existing timeout to prevent conflicts
	if (modalTimeout) {
		clearTimeout(modalTimeout);
	}
	
	// Hide only the loading modal, not all modals
	// This prevents resetting the entire state which can interfere with showing the error modal
	dispatch({ type: HIDE_LOADING_MODAL });

	console.log('🚨 dispatchErrorModal: Scheduling error modal', {
		errorMessage,
		delay,
		timestamp: Date.now(),
	});
	
	// Set new timeout for showing error modal
	modalTimeout = setTimeout(() => {
		console.log('🚨 dispatchErrorModal: Dispatching SHOW_AUTH_MODAL', {
			errorMessage,
			timestamp: Date.now(),
		});
		dispatch({ 
			type: SHOW_AUTH_MODAL, 
			data: { 
				content: errorMessage, 
				type: 'error',
				details: details 
			} 
		});
		modalTimeout = null;
	}, delay);
};

// Centralized success handler
const dispatchSuccessModal = (message: string, delay: number = 500) => {
	// Clear any existing timeout to prevent conflicts
	if (modalTimeout) {
		clearTimeout(modalTimeout);
	}
	
	// Set timeout for success modal (don't hide all modals for success)
	modalTimeout = setTimeout(() => {
		dispatch({ 
			type: SHOW_AUTH_MODAL, 
			data: { 
				content: message, 
				type: 'success' 
			} 
		});
		modalTimeout = null;
	}, delay);
};

// Clear any pending modal operations
const clearModalTimeout = () => {
	if (modalTimeout) {
		clearTimeout(modalTimeout);
		modalTimeout = null;
	}
};


// Replay queued requests
const replayQueue = async () => {
	const queue = await getQueue();

	for (const request of queue) {
		try {
			await axios(request); // Replay the request
			await removeProcessedRequest();
		} catch (err) {
			console.error('Failed to process queued request:', err);
			break;
		}
	}
};


export const cancelAllRequests = async () => {
	// Clear any pending modal timeouts when canceling requests
	clearModalTimeout();
	return new Promise((resolve) => {
		cancelTokenSource.cancel('All requests cancelled');
		cancelTokenSource = axios.CancelToken.source();
		resolve('All requests cancelled');
	});
}

// Monitor network status
monitorNetworkStatus((status) => {
	isOnline = status;
	if (isOnline) {
		replayQueue();
	}
});

function dispatch(action) {
	store.dispatch(action)
}

// Initialize API instances with dynamic URLs
const initializeApiInstances = async () => {
	const baseUrl = await getApiBaseUrl();
	const sessionUrl = baseUrl.replace('/api', '/api/session');

	const sessionApi = axios.create({
		withCredentials: true,
		baseURL: sessionUrl,
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json'
		}
	});

	const api = axios.create({
		withCredentials: true,
		baseURL: baseUrl,
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json'
		}
	});

	const noCreds = axios.create({
		baseURL: baseUrl,
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json'
		}
	});

	const publicApi = axios.create({
		baseURL: baseUrl,
		withCredentials: false,
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json'
		}
	});

	// Register instances globally for dynamic updates
	if (typeof window !== 'undefined') {
		(window as any).apiInstances = {
			sessionApi,
			api,
			noCreds,
			publicApi
		};
	}

	return { sessionApi, api, noCreds, publicApi };
};

// Initialize instances
let apiInstances: any = null;
const getApiInstances = async () => {
	if (!apiInstances) {
		apiInstances = await initializeApiInstances();
	}
	return apiInstances;
};

// Export instances (will be initialized on first use)
export const sessionApi = axios.create({
	withCredentials: true,
	baseURL: SESSION_API,
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json'
	}
});

export const api = axios.create({
	withCredentials: true,
	baseURL: USER_API_BASE_URL,
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json'
	}
});

export const noCreds = axios.create({
	baseURL: USER_API_BASE_URL,
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json'
	}
});

export const publicApi = axios.create({
	baseURL: USER_API_BASE_URL,
	withCredentials: false,
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json'
	}
});

// Universal response interceptor for handling nested data and billing warnings
const createUniversalResponseInterceptor = () => {
	return (response: any) => {
		const requestKey = `${response.config.method}-${response.config.url}`;
		removeAbortController(requestKey);

		// Handle billing warnings globally
		if (response.data?.billingWarning) {
			const routeName = response.config.url.split('/')[1];
			paymentDispatcher(routeName, '', response.data.message);
		}

		// Handle nested data structure globally
		if (response.data?.data && typeof response.data.data === 'object') {
			// If response has nested data structure, flatten it
			response.data = {
				...response.data,
				...response.data.data
			};
		}

		// Show success modal for monitored requests (if configured)
		// Check if this was a monitored request that should show modals
		const isMonitoredRequest = response.config?._monitorType === 'monitored' ||
			response.config?._monitorType === 'form';

		if (isMonitoredRequest) {
			// Always hide loading modal for monitored requests
			dispatch({ type: HIDE_LOADING_MODAL });

			// Show success message if configured
			if (response.config?._shouldShowSuccessModal) {
				const successMessage = response.data?.message ||
					response.data?.success ||
					'Operation completed successfully';

				dispatchSuccessModal(successMessage);
			}
		}

		return response;
	};
};

// Universal error interceptor
const createUniversalErrorInterceptor = () => {
	return (error: any) => {
		const requestKey = `${error.config?.method}-${error.config?.url}`;
		removeAbortController(requestKey);

		// Hide loading modal for monitored requests on error
		const isMonitoredRequest = error.config?._monitorType === 'monitored' ||
			error.config?._monitorType === 'form';

		if (isMonitoredRequest) {
			// Hide only the loading modal, not all modals
			dispatch({ type: HIDE_LOADING_MODAL });

			// Show error modal for monitored requests
			if (error.config?._shouldShowModal) {
				console.log('🚨 Universal interceptor: Showing error modal', {
					hasConfig: !!error.config,
					shouldShowModal: error.config?._shouldShowModal,
					monitorType: error.config?._monitorType,
					errorMessage: error.response?.data?.message || error.response?.data?.error || error.message
				});

				// Use centralized error handler to prevent modal conflicts
				const errorMessage =
					error.response?.data?.message ||
					error.response?.data?.error ||
					error.message ||
					'An error occurred while processing your request';

				dispatchErrorModal(errorMessage, error.response?.data);
			} else {
				console.log('🚨 Universal interceptor: NOT showing error modal', {
					hasConfig: !!error.config,
					shouldShowModal: error.config?._shouldShowModal,
					monitorType: error.config?._monitorType
				});
			}
		}

		return Promise.reject(error);
	};
};

// Function to update all instances with new base URL
export const updateApiInstances = async (newHost?: string) => {
	const baseUrl = await updateApiBaseUrls(newHost);
	const sessionUrl = baseUrl.replace('/api', '/api/session');

	// Update all instances
	api.defaults.baseURL = baseUrl;
	noCreds.defaults.baseURL = baseUrl;
	publicApi.defaults.baseURL = baseUrl;
	sessionApi.defaults.baseURL = sessionUrl; 

	console.log('Updated all API instances to use:', baseUrl);
};

export const monitoredRequest = axios.create({
	withCredentials: true,
	timeout: 600000, // 10 minutes default (increased for resume processing and other long-running AI operations, especially large documents)
	baseURL: USER_API_BASE_URL,
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json'
	}
})

export const formRequest = axios.create({
	withCredentials: true,
	baseURL: USER_API_BASE_URL,
	headers: {
		'Content-Type': 'multipart/form-data'
	}
})

const logOut = axios.create({
	withCredentials: true,
	baseURL: USER_API_BASE_URL,
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json'
	},
	timeout: 5000
})

export const monitorFail = axios.create({
	withCredentials: true,
	baseURL: USER_API_BASE_URL,
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json'
	}
})

export const monitorPass = axios.create({
	withCredentials: true,
	baseURL: USER_API_BASE_URL,
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json'
	}
})

export const noMonitor = axios.create({
	withCredentials: true,
	baseURL: USER_API_BASE_URL,
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json'
	},
	// Allow long-running AI operations (image gen, large reports) to complete.
	// On local, default to 10 minutes; override via REACT_APP_API_TIMEOUT_MS if needed.
	timeout: Number(process.env.REACT_APP_API_TIMEOUT_MS || 600000)
});

// Universal interceptors setup for all API instances
const universalResponseInterceptor = createUniversalResponseInterceptor();
const universalErrorInterceptor = createUniversalErrorInterceptor();

// Apply universal interceptors to all API instances
[api, noCreds, publicApi, sessionApi, monitoredRequest, formRequest, monitorFail, noMonitor].forEach(instance => {
	// Request interceptors (keep existing optimized ones)
	if (instance === api) {
		instance.interceptors.request.use(
			apiInterceptor.createRequestInterceptor({
				shouldLog: true,
				shouldQueue: true,
				shouldShowModal: false,
				monitorType: 'api'
			}),
			apiInterceptor.createErrorInterceptor({ monitorType: 'api' })
		);
	} else if (instance === monitoredRequest) {
		instance.interceptors.request.use(
			apiInterceptor.createRequestInterceptor({
				shouldLog: true,
				shouldQueue: true,
				shouldShowModal: true,
				monitorType: 'monitored'
			}),
			apiInterceptor.createErrorInterceptor({ monitorType: 'monitored' })
		);
		// Add response error interceptor for monitored requests to show error modals
		instance.interceptors.response.use(
			universalResponseInterceptor,
			apiInterceptor.createErrorInterceptor({
				monitorType: 'monitored',
				shouldShowErrorModal: true
			})
		);
	} else if (instance === formRequest) {
		instance.interceptors.request.use(
			apiInterceptor.createRequestInterceptor({
				shouldLog: true,
				shouldQueue: true,
				shouldShowModal: true,
				monitorType: 'form'
			}),
			apiInterceptor.createErrorInterceptor({ monitorType: 'form' })
		);
		// Add response error interceptor for form requests to show error modals
		instance.interceptors.response.use(
			universalResponseInterceptor,
			apiInterceptor.createErrorInterceptor({
				monitorType: 'form',
				shouldShowErrorModal: true
			})
		);
	} else if (instance === noMonitor) {
		instance.interceptors.request.use(
			apiInterceptor.createRequestInterceptor({
				shouldLog: true,
				shouldQueue: false,
				shouldShowModal: false,
				monitorType: 'noMonitor'
			}),
			apiInterceptor.createErrorInterceptor({ monitorType: 'noMonitor' })
		);
		// Apply universal response interceptor to noMonitor
		instance.interceptors.response.use(universalResponseInterceptor, universalErrorInterceptor);
	} else {
		// Apply universal response interceptor to all other instances
		instance.interceptors.response.use(universalResponseInterceptor, universalErrorInterceptor);
	}
});

// Old interceptor removed - handled by universal interceptor

// Old interceptor removed - handled by universal interceptor

// Old interceptor removed - handled by universal interceptor

// Old interceptor removed - handled by universal interceptor


//External APIs______________________________________________________________________________________________________________________________________________
const weatherApi = axios.create({
	baseURL: 'https://api.openweathermap.org/data/2.5',
	withCredentials: false
})


const options = {
	method: 'GET',
	url: 'https://newsapi.org/v2/top-headlines?country=gb&apiKey=97b1c15122cc4974baa5b31c69a08490'
};

const companiesHouseAPI = axios.create({
	baseURL: 'https://api.company-information.service.gov.uk',
	auth: {
		username: COMPANIESHOUSE_API_KEY,
		password: ''
	}
})

const linkedInAPI = axios.create({
	baseURL: 'https://www.linkedin.com/oauth/v2',
	params: {
		response_type: 'code',
		client_id: LINKEDIN_CLIENT_ID,
		redirect_uri: 'https://smartsaas.pro',
		scope: 'r_liteprofile%20r_emailaddress%20w_member_social'
	}
})

// const quoteAPI = axios.create({
//     baseURL: 'https://zenquotes.io/api'
// })

export const authoriseLinkedIn = async () => {
	await linkedInAPI
		.get('/authorization').then(res => console.log(res))
};

export const getRemainingProfile = async () => {
	try {
		const invitations = await noMonitor.get('/login/get-new-invitations');
		console.log(invitations.data);

		dispatch({ type: UPDATED_INVITATIONS, payload: invitations.data.count });

		const messages = await noMonitor.get('/login/get-new-messages');

		dispatch({ type: UPDATED_MESSAGES, payload: messages.data.count });

		const calendarEntries = await noMonitor.get('/login/get-new-calendar-entries');

		dispatch({ type: UPDATED_CALENDAR_ENTRIES, payload: calendarEntries.data.count });

		return true;
	} catch (err) {
		throw err; // Re-throw the error to be caught by the caller if needed.
	}
};

function delay(ms) {
	return new Promise(resolve => setTimeout(resolve, ms));
}


// export const quoteToday = async () => {
//     await quoteAPI
//         .get('/today')
// }

export const getCompanyInformation = async (companyNumber) => {
	await companiesHouseAPI
		.get(`/company/${companyNumber}`)
}


export const getCompanyInformationName = async (company) => {
	await companiesHouseAPI
		.get(`/search/companies`, { params: { q: company } })
		.then(res => console.log(res))
}

//Obtain weather information
export const getLocalWeather = async (userLocation) => {
	if (!userLocation) {
		return
	}
	await weatherApi
		.get(`/weather?q=${userLocation}&appId=${WEATHER_API_KEY}&units=metric`)
		.then(res => dispatch({ type: WEATHER_RETRIEVED, payload: res.data }))
		.catch(err => console.log(err))
}

export const refreshUser = async () => {
	await noMonitor.get('/user/refresh-user').then(res => {
		if (__DEV__) {
			console.log('✅ User data refreshed');
		}
		dispatch({ type: REFRESH, payload: res.data.data })
	})
}

export const getLocalNews = async (language, country) => {
	await axios
		.request(options)
		.then(res => { dispatch({ type: NEWS_UPDATED, payload: res.data }) })
		.catch(err => console.log(err))
}

function newAbortSignal(timeoutMs) {
	const abortController = new AbortController();
	setTimeout(() => abortController.abort(), timeoutMs || 0);

	return abortController.signal;
}

export const getSession = async () => {
	console.log('Requesting session');
	return await sessionApi.post(null, {
		signal: newAbortSignal(3000)
	});
}

export const getUserCompanies = async () => {
	console.log('📋 Fetching user companies');
	try {
		const response = await api.get('/user/companies');
		return response.data;
	} catch (error) {
		console.error('❌ Error fetching user companies:', error);
		throw error;
	}
}

//Google APIs______________________________________________________________________________________________________________________________________________
const googleAnalyticsAPI = axios.create({
	baseURL: 'https://analyticsdata.googleapis.com/v1beta/properties',
})

const googleMailApi = axios.create({
	baseURL: 'https://gmail.googleapis.com'
})

googleMailApi.interceptors.response.use(function (response) {
	return response;
}, async function (error) {
	const originalRequest = error.config;

	// Check if error is 401 and we haven't retried yet
	if (error.response?.status === 401 && !originalRequest._retry) {
		originalRequest._retry = true;

		try {
			await renewToken();
			// Retry the original request with updated token from Redux state
			const { google } = store.getState().promiseData.data.userData.authentications;
			if (google?.accessToken) {
				// Update the authorization header with new token
				originalRequest.headers.Authorization = `Bearer ${google.accessToken}`;
				if (originalRequest.url.includes('access_token=')) {
					// Update access_token in URL if present
					originalRequest.url = originalRequest.url.replace(/access_token=[^&]*/, `access_token=${google.accessToken}`);
				}
				return googleMailApi.request(originalRequest);
			}
		} catch (refreshError) {
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Failed to re-authenticate Google Account, services such as Google Mail and Google Drive may not be available. \n \n Try signing in again from the Emails Screen. \n \n', type: 'error' } });
			return Promise.reject(refreshError);
		}
	}
	return Promise.reject(error);
}
);

googleAnalyticsAPI.interceptors.response.use(function (response) {
	return response;
}, async function (error) {
	const originalRequest = error.config;

	// Check if error is 401 and we haven't retried yet
	if (error.response?.status === 401 && !originalRequest._retry) {
		originalRequest._retry = true;

		try {
			await renewToken();
			// Retry the original request with updated token from Redux state
			const { google } = store.getState().promiseData.data.userData.authentications;
			if (google?.accessToken) {
				// Update the authorization header with new token
				originalRequest.headers.Authorization = `Bearer ${google.accessToken}`;
				return googleAnalyticsAPI.request(originalRequest);
			}
		} catch (refreshError) {
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Failed to re-authenticate Google Account, services such as Google Mail and Google Drive may not be available. \n Try signing in again', type: 'error' } });
			return Promise.reject(refreshError);
		}
	}
	return Promise.reject(error);
}
);

const googleMaps = axios.create({
	baseURL: 'https://maps.googleapis.com/maps/api'
})

const googleMailCount = axios.create({
	baseURL: 'https://gmail.googleapis.com',
})

// Add interceptor to googleMailCount for token refresh
googleMailCount.interceptors.response.use(function (response) {
	return response;
}, async function (error) {
	const originalRequest = error.config;

	// Check if error is 401 and we haven't retried yet
	if (error.response?.status === 401 && !originalRequest._retry) {
		originalRequest._retry = true;

		try {
			await renewToken();
			// Retry the original request with updated token from Redux state
			const { google } = store.getState().promiseData.data.userData.authentications;
			if (google?.accessToken) {
				// Update the authorization header with new token
				originalRequest.headers.Authorization = `Bearer ${google.accessToken}`;
				if (originalRequest.url.includes('access_token=')) {
					// Update access_token in URL if present
					originalRequest.url = originalRequest.url.replace(/access_token=[^&]*/, `access_token=${google.accessToken}`);
				}
				return googleMailCount.request(originalRequest);
			}
		} catch (refreshError) {
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Failed to re-authenticate Google Account, services such as Google Mail and Google Drive may not be available. \n \n Try signing in again from the Emails Screen. \n \n', type: 'error' } });
			return Promise.reject(refreshError);
		}
	}
	return Promise.reject(error);
})

const googleAPI = axios.create({
	baseURL: 'https://www.googleapis.com/'
})

const googleMailBatch = axios.create({
	baseURL: 'https://www.googleapis.com/batch',
	headers: {
		'Content-Type': 'multipart/mixed; boundary="foo_bar"'
	}
})

const googleOAuthEndpoint = axios.create({
	baseURL: 'https://oauth2.googleapis.com/token'
})

const googleDriveAPi = axios.create({
	baseURL: 'https://www.googleapis.com/drive/v3'
})

googleDriveAPi.interceptors.response.use(function (response) {
	return response;
}, async function (error) {
	const originalRequest = error.config;

	// Check if error is 401 and we haven't retried yet
	if (error.response?.status === 401 && !originalRequest._retry) {
		originalRequest._retry = true;

		try {
			await renewToken();
			// Retry the original request with updated token from Redux state
			const { google } = store.getState().promiseData.data.userData.authentications;
			if (google?.accessToken) {
				// Update the authorization header with new token
				originalRequest.headers.Authorization = `Bearer ${google.accessToken}`;
				return googleDriveAPi.request(originalRequest);
			}
		} catch (refreshError) {
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Failed to re-authenticate Google Account, services such as Google Mail and Google Drive may not be available. \n Try signing in again', type: 'error' } });
			return Promise.reject(refreshError);
		}
	}
	return Promise.reject(error);
}
);

const googleSendApi = axios.create({ baseURL: 'https://www.googleapis.com/upload/gmail/v1/users/me/messages/send' })

const renewToken = async () => {
	await noMonitor.get('/google/refresh-token').then(res => {
		if (res.status === 200) {
			dispatch({ type: UPDATE_GOOGLE_MAIL_TOKEN, payload: res.data.newToken })
		}
	})
}

export const getDefaultWebStats = async (accessToken, payload, propertyId, reportType) => {
	const headers = {
		"Authorization": `Bearer ${accessToken}`,
		"Content-Type": "application/json"
	}
	const googleWebAnalytics = await googleAnalyticsAPI
		.post(`/${propertyId}:${reportType}`, payload, { headers })
		.then(res => {
			if (res.status != 200) {
				dispatch({ type: GOOGLE_AUTH_FAILED })
			}
			return res
		}).catch()
	return googleWebAnalytics.data.rows
}

//GMAIL parse function
async function parseBatchResponse(response) {
	// Not the same delimiter in the response as we specify ourselves in the request,
	// so we have to extract it.
	const delimiter = response.substr(0, response.indexOf('\n'));
	const parts = response.split(delimiter);
	// The first part will always be an empty string. Just remove it.
	parts.shift();
	// The last part will be the "--". Just remove it.
	parts.pop();
	const result = [];
	for (let i = 0; i < parts.length; i++) {
		let part = parts[i];
		let p = part.substring(part.indexOf("{"), part.lastIndexOf("}") + 1);
		try {
			result.push(JSON.parse(p));
		} catch (e) {
			continue;
		}
	}
	return result;
}

export const searchMail = async (accessToken, query, filter) => {
	let dateFilter = ''; // Initialize date filter string

	// Check if filter object has date information
	if (filter && filter.startDate && filter.endDate) {
		// Format dates in RFC 3339 format

		// Add date filter to query string
		dateFilter = `after:${filter.startDate} before:${filter.endDate}`;
	}

	// Concatenate date filter with original query
	const fullQuery = `${query} ${dateFilter}`;
	return await googleMailApi.get(`/gmail/v1/users/me/messages?q=${fullQuery}&access_token=${accessToken}`)
		.then(async res => {
			let boundary = 'foo_bar'
			let messageIdArr = res.data.messages;
			let header = '';
			messageIdArr.forEach((element: { id: any; }) => {
				header += '\n' + `--${boundary}` + '\n' + `Content-Type: application/http` + '\n' + '\n' + `GET /gmail/v1/users/me/messages/${element.id}?access_token=${accessToken}`
			});
			header += '\n' + `--${boundary}--`;
			return googleMailBatch.post(`/gmail/v1`, header)
				.then(async res => {
					const parsedResponse = await parseBatchResponse(res.data);
					// dispatch({ type: MAIL_RETRIEVED })
					return parsedResponse;
				})
		})
}

export const geoCodeAddress = async (address) => {
	const url = '/geocode/json';
	const params = {
		address: address,
		key: 'AIzaSyAznGh_h4mH0meG5V6hlTlLVhVithzlUJM'
	}
	try {
		const response = await googleMaps.get(url, { params });
		return response;
	} catch (error) {
		console.error("Error fetching data from Google Maps API", error);
		throw error;
	}
}

export const googleMapSearch = async (query, options: any = {}) => {
	const url = 'https://places.googleapis.com/v1/places:searchText';
	const headers = {
		'Content-Type': 'application/json',
		'X-Goog-Api-Key': 'AIzaSyAznGh_h4mH0meG5V6hlTlLVhVithzlUJM',
		'X-Goog-FieldMask': 'places.businessStatus,places.iconBackgroundColor,places.types,places.iconMaskBaseUri,places.rating,places.displayName,places.formattedAddress,places.websiteUri'
	};
	const data: any = {
		textQuery: query,
		maxResultCount: options.maxResultCount || 20
	}

	if (options.locationBias) {
		data.locationBias = options.locationBias;
	}

	if (options.pageToken) {
		data.pageToken = options.pageToken;
	}

	try {
		const response = await axios.post(url, data, { headers });
		return response.data;
	} catch (error) {
		console.error("Error fetching data from Google Maps API", error);
		throw error;
	}
}

// Helper function to fetch multiple pages of Google Places results
export const googleMapSearchPaginated = async (query, options: any = {}) => {
	const maxResults = options.maxResultCount || 20;
	const maxPages = Math.ceil(maxResults / 20); // Google Places API returns max 20 per page
	const allPlaces = [];
	let nextPageToken = null;
	let pageCount = 0;

	const locationBias = options.locationBias;

	do {
		const response = await googleMapSearch(query, {
			maxResultCount: 20, // Always request 20 per page (API limit)
			locationBias: pageCount === 0 ? locationBias : undefined, // Only include locationBias on first request
			pageToken: nextPageToken || undefined
		});

		if (response.places && Array.isArray(response.places)) {
			allPlaces.push(...response.places);
		}

		nextPageToken = response.nextPageToken;
		pageCount++;

		// Stop if we have enough results or no more pages
		if (allPlaces.length >= maxResults || !nextPageToken || pageCount >= maxPages) {
			break;
		}
	} while (nextPageToken && pageCount < maxPages);

	// Return only the requested number of results
	return {
		places: allPlaces.slice(0, maxResults),
		nextPageToken: allPlaces.length >= maxResults ? nextPageToken : null
	};
}


export const requestNextPage = async (accessToken, nextPageToken) => {
	return await googleMailApi.get(`/gmail/v1/users/me/messages?access_token=${accessToken}&pageToken=${nextPageToken}`)
		.then(async res => {
			console.log(res.data, 'page token');
			let boundary = 'foo_bar'
			let messageIdArr = res.data.messages;
			let pageToken = res.data.nextPageToken;
			let header = '';
			messageIdArr.forEach((element: { id: any; }) => {
				header += '\n' + `--${boundary}` + '\n' + `Content-Type: application/http` + '\n' + '\n' + `GET /gmail/v1/users/me/messages/${element.id}?access_token=${accessToken}`
			});
			header += '\n' + `--${boundary}--`;
			return googleMailBatch.post(`/gmail/v1`, header)
				.then(async res => {
					const parsedResponse = await parseBatchResponse(res.data);
					dispatch({ type: MAIL_RETRIEVED })
					return { messages: parsedResponse, nextPageToken: pageToken };
				})
		})
}

export const requestLastPage = async (accessToken, lastPageToken) => {
	return await googleMailApi.get(`/gmail/v1/users/me/messages?access_token=${accessToken}&pageToken=${lastPageToken}`)
		.then(async res => {
			console.log(res.data, 'page token');
			let boundary = 'foo_bar'
			let messageIdArr = res.data.messages;
			let pageToken = res.data.nextPageToken;
			let header = '';
			messageIdArr.forEach((element: { id: any; }) => {
				header += '\n' + `--${boundary}` + '\n' + `Content-Type: application/http` + '\n' + '\n' + `GET /gmail/v1/users/me/messages/${element.id}?access_token=${accessToken}`
			});
			header += '\n' + `--${boundary}--`;
			return googleMailBatch.post(`/gmail/v1`, header)
				.then(async res => {
					const parsedResponse = await parseBatchResponse(res.data);
					dispatch({ type: MAIL_RETRIEVED })
					return { messages: parsedResponse, nextPageToken:pageToken };
				})
		})
}

export const searchGmail = async (accessToken, query) => {
	return await googleMailApi.get(`/gmail/v1/users/me/messages?q=${query}&access_token=${accessToken}`)
		.then(res => {
			let boundary = 'foo_bar'
			let messageIdArr = res.data.messages;
			let header = '';
			messageIdArr.forEach((element: { id: any; }) => {
				header += '\n' + `--${boundary}` + '\n' + `Content-Type: application/http` + '\n' + '\n' + `GET /gmail/v1/users/me/messages/${element.id}?access_token=${accessToken}`
			});
			header += '\n' + `--${boundary}--`;
			return googleMailBatch.post(`/gmail/v1`, header)
				.then(async res => {
					const parsedResponse = await parseBatchResponse(res.data);
					dispatch({ type: MAIL_RETRIEVED })
					return parsedResponse;
				})
		})
}

export const getAllGmail = async (gId, accessToken, tag) => {
	return await googleMailApi.get(`/gmail/v1/users/me/messages?access_token=${accessToken}`, { params: tag ? { q: `label:${tag}` } : {} })
		.then(res => {
			let boundary = 'foo_bar'
			let messageIdArr = res.data.messages;
			let nextPageToken = res.data.nextPageToken;
			let estimateSize = res.data.resultSizeEstimate;
			let header = '';
			try {
				messageIdArr.forEach((element: { id: any; }) => {
					header += '\n' + `--${boundary}` + '\n' + `Content-Type: application/http` + '\n' + '\n' + `GET /gmail/v1/users/me/messages/${element.id}?access_token=${accessToken}`
				});
				header += '\n' + `--${boundary}--`;
				return googleMailBatch.post(`/gmail/v1`, header)
					.then(async res => {
						const parsedResponse = await parseBatchResponse(res.data);
						dispatch({ type: MAIL_RETRIEVED })
						return { messages: parsedResponse, nextPageToken, estimateSize };
					})
			} catch (e) {
				console.log(e);
				return { messages: [], nextPageToken, estimateSize };
			}
		})
}

export const sendGoogleMail = async (token: string, mail: {}) => {
	const headers = {
		'Authorization': `Bearer ${token}`,
		'Content-Type': "message/rfc822"
	}
	return await googleMailCount.post(`/gmail/v1/users/me/messages/send`, mail, { headers })
}

export const markRead = async (mailId: string, accessToken: string) => {

	const headers = {
		'Authorization': `Bearer ${accessToken}`,
		'Content-Type': 'application/json'
	};

	return await googleMailCount.post(`/gmail/v1/users/me/threads/${mailId}/modify`, { addLabelIds: [], removeLabelIds: ['UNREAD'] }, { headers }).then(res => { return res })
}

export const refreshGoogleAccess = async (refreshToken, userId, urlScheme) => {
	return await googleOAuthEndpoint.post(``, { "client_secret": "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", "client_id": "530616870665-sn6qm81jg6eh4pr6dm2ov2n6guovrgna.apps.googleusercontent.com", "refresh_token": refreshToken, "grant_type": 'refresh_token' })
		.then(res => { 
			dispatch({ type: UPDATE_GOOGLE_ACCOUNT, payload: res.data })
			return res.data
		 })
		.catch(e => {
			console.error(e);
			dispatch({ type: CLEAR_MAIL_AUTH })
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: "Failed to re-authenticate Google Account, services such as Google Mail and Google Drive may not be available. \n Try signing in again by going to the mail screen", type: 'error' } });
			return false
		})
}

export const googleOAuth = async (googleAuth) => {
	return await monitoredRequest.post('/google/code', googleAuth)
}

export const getGmailCount = async (gId, accessToken) => {
	if (!gId || !accessToken) {
		throw new Error('No user ID or access token provided');
	}
	const headers = {
		'Authorization': `Bearer ${accessToken}`,
		'Content-Type': 'application/json'
	};
	// Let the interceptor handle 401 errors and retry logic
	return await googleMailApi.get(`/gmail/v1/users/${gId}/labels/INBOX?access_token=${accessToken}`, { headers: headers })
}

export const getGDriveItemsList = async (accessToken: string) => {
	const header = { 'authorization': 'Bearer ' + accessToken }
	return await googleDriveAPi.get(`/files`, { headers: header })
		.then(res => {
			if (res.status != 200) {
				console.log('Failed to retrieve Google Drive File List')
			}
			else
				return res
		})
		.catch(err => {
			console.log(err)
		})
}

export const getGDriveFolderContent = async (accessToken: string, file: string) => {
	const header = { 'authorization': 'Bearer ' + accessToken }
	return await googleDriveAPi.get(`/files?q=%27${file}%27%20in%20parents`, { headers: header })
		.then(res => {
			if (res.status != 200) {
				console.log('Failed to retrieve Google Drive File List')
			} else
				return res
		})
		.catch(err => {
			console.log(err)
		})
}


export const getGDriveItem = async (accessToken: string, file: string, mimeType: string) => {
	const header = { 'authorization': 'Bearer ' + accessToken }
	return await googleDriveAPi.get(`/files/${file}`, { headers: header, params: { alt: 'media' } })
		.then(res => {
			if (res.status != 200) {
				console.log(`Failed to retrieve item "${file}"`)
			}
			return res
		})
		.catch(err => console.log(err))
}

//Get list of calendar events


export const getGoogleCalendarList = async (accessToken: string, calendarIds: string[], params: GoogleCalTypes) => {
	const header = { 'authorization': 'Bearer ' + accessToken };


	const responses = await Promise.all(
		calendarIds.map(async calendarId => {
			try {
				const response = await googleAPI.get(`/calendar/v3/calendars/${calendarId}/events`, { headers: header, params: params });
				return response.data;
			} catch (error) {
				return null; // Return null for failed requests
			}
		})
	);

	return responses;
}



//Stripe APIs
//Generate Stripe APIs

export const createPaymentIntent = async (body) => {
	return await api.post(`/payments/create-payment-intent`, body, { withCredentials: true })
		.then(res => {
			if (res.status != 200) {
				console.log('FAILED!!', res)
			}
			else return res
		})
		.catch((err) => {
			console.log(err)
		})
}
//Internal APIs______________________________________________________________________________________________________________________________________________
//Retrieve user query history
export const retrieveQueriesByID = async (id) => {
	await api.get(`/userActionHistory/${id}`, { withCredentials: true })
		.then(res => {
			const queryHistory = res.data.data;
			dispatch({ type: HISTORY_RETRIEVED, payload: queryHistory });
		})
		.catch(err => (dispatch({ type: GET_ERRORS, payload: err?.response || 'Query history not retrieved' })))
}
export const insertQueryHistory = (id, payload) => { api.put(`/userActionHistory/${id}`, payload) }

//Get User Queries
export const quoteToday = async () => await noMonitor.get('/quotes')
export const addTwofa = (id: String, payload) => api.post(`/${id}/add2fa`, payload, { withCredentials: true }).catch(err => dispatch({ type: GET_ERRORS, payload: err.response }))

export const retrieveData = responseData => {
	return {
		type: DATA_QUERY,
		payload: responseData
	};
};

//Check if company exists
export const companyCheck = async (companyData, formValues) => {
				await noCreds.get('/get-product-list').then(res => {
					dispatch({ type: UPDATE_PRODUCTS, payload: res.data.data })
				})
					.catch(e => {
						dispatch({ type: SHOW_AUTH_MODAL, data: { content: e.data.data, type: 'error' } })
					});
				dispatch({ type: SHOW_REGISTER_MODAL, payload: formValues });
				return
		}

export const getTags = async (id) => {
	console.log('requested tags')
	return await noMonitor.get(`/get-tags/${id}`)
}

export const generateBusinessCard = async (data, platform = null) => {
	console.log(data);
	// Detect platform if not provided
	const detectedPlatform = platform || (typeof Platform !== 'undefined' ? Platform.OS : 'ios');
	return await noMonitor.post('/user/generate-business-card', {
		barcodeValue: data,
		platform: detectedPlatform // Send platform: 'ios' or 'android'
	})
}

//Register User
export const registerUser = async (userData: any) => {
	return await api
		.post("/register", userData, { withCredentials: true })
		.then((res) => {
			if (res.status !== 201) {
				return () => {
					(
						dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.data.error, type: 'error' } })
					)
				}
			} else {
				dispatch({ type: BACK_TO_LOGIN })
				dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.data.message, type: 'success' } })
			}
		})
		.catch((err) => {
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: err?.response.data, type: 'error' } })
		});
}

export const addUserTags = async (payload) => {
	return await noMonitor
		.post('/user/add-tags', payload).then(() => { return { success: true } })
}

export const addFavourite = async (id, type, body) => {
	let url = body?.content;
	return await monitorFail.post(`/set-favourite/${type}/${id}`, body).then(async res => {
		console.log(url);
	}).catch(()=>{
		dispatch({ type: HANDLE_FAVOURITE, payload: { id, type, body: url } })
	})
}

export const getDataFavorites = async () => {
	return await noMonitor.get('/data/get-favourites').then(res => {
		if (res.status === 200) {
			return res.data.data
		}
	}
	)
}

export const getProjectFavorites = async () => {
	return await noMonitor.get('/projects/get-favourites').then(res => {
		if (res.status === 200) {
			return res.data.data
		}
	}
	)
}


export const updateUserProfile = async (payload) => {
	const body = { content: payload }
	return await monitorFail
		.post('/user/update-profile', body).then((res: {
			data: { changes: any; success?: boolean; };
		}) => {
			console.log('formik api response', res.data);
			// Return the full response data including changes so components can update Redux
			return { 
				success: res.data.success !== false, // Default to true if not explicitly false
				data: res.data // Include the full response data with changes
			}
		})
		.catch((error) => {
			console.error('Error updating user profile:', error);
			return { success: false, error: error.message || 'Failed to update profile' };
		})
}

export const updateUserDepartment = async (targetUserId: string, department: string) => {
	const body = { targetUserId, department }
	return await monitorFail
		.post('/user/update-department', body).then((res: {
			data: { success: boolean; message?: string; };
		}) => {
			console.log('✅ User department updated:', res.data);
			return res.data
		})
		.catch((error) => {
			console.error('❌ Error updating user department:', error);
			throw error;
		})
}

/**
 * Save dismissed save prompts to backend
 * @param dismissedPrompts Array of chat fingerprints that user dismissed save prompts for
 */
export const saveDismissedSavePrompts = async (dismissedPrompts: string[]) => {
	try {
		const body = { content: { easyModeDismissedSavePrompts: dismissedPrompts } };
		return await monitorFail
			.post('/user/update-profile', body)
			.then((res: { data: { success?: boolean; changes?: any } }) => {
				console.log('✅ Dismissed save prompts saved:', res.data);
				return {
					success: res.data.success !== false,
					data: res.data
				};
			})
			.catch((error) => {
				console.error('❌ Error saving dismissed save prompts:', error);
				return { success: false, error: error.message || 'Failed to save dismissed prompts' };
			});
	} catch (error) {
		console.error('❌ Error saving dismissed save prompts:', error);
		return { success: false, error: error?.message || 'Failed to save dismissed prompts' };
	}
}

/**
 * Get dismissed save prompts from user profile
 * Returns empty array if not found or on error
 */
export const getDismissedSavePrompts = async (): Promise<string[]> => {
	try {
		const session = await getSession();
		if (session?.data?.userData?.easyModeDismissedSavePrompts) {
			const dismissed = session.data.userData.easyModeDismissedSavePrompts;
			console.log('✅ Loaded dismissed save prompts from backend:', dismissed?.length || 0);
			return Array.isArray(dismissed) ? dismissed : [];
		}
		return [];
	} catch (error) {
		console.error('❌ Error loading dismissed save prompts:', error);
		return [];
	}
}

export const assignUserToDepartment = async (companyId: string, payload: { user: string; departmentId: string }) => {
	const body = { 
		data: { user: payload.user },
		departmentId: payload.departmentId 
	}
	// CompanyId comes from session, not URL params
	return await monitorFail
		.post(`/company/assign-department`, body).then((res: {
			data: { success: boolean; };
		}) => {
			console.log('✅ User assigned to department:', res.data);
			return res.data
		})
		.catch((error) => {
			console.error('❌ Error assigning user to department:', error);
			throw error;
		})
}

export const updateUserContext = async (contextData) => {
	const body = { context: contextData }
	return await monitorFail
		.post('/user/update-context', body).then((res: {
			data: { context: any; };
			success?: boolean;
		}) => {
			return { success: true, data: res.data.context }
		})
		.catch((error) => {
			console.error('Error updating user context:', error);
			throw error;
		})
}

// User branding functions
export const getUserBranding = async () => {
	return await api.get('/user/branding')
		.then((res) => {
			console.log('branding get api response', res.data);
			return { success: true, data: res.data }
		})
		.catch((error) => {
			console.error('Error fetching user branding:', error);
			throw error;
		})
}

export const updateUserBranding = async (brandingData) => {
	return await monitorFail
		.put('/user/branding', brandingData).then((res: {
			data: any;
			success?: boolean;
		}) => {
			console.log('branding update api response', res.data);
			return { success: true, data: res.data }
		})
		.catch((error) => {
			console.error('Error updating user branding:', error);
			throw error;
		})
}

export const uploadBrandingImage = async (imageType: 'logo' | 'favicon', imageData: string, fileName?: string, mimeType?: string) => {
	return await monitorFail
		.post(`/user/branding/upload/${imageType}`, {
			imageData,
			fileName,
			mimeType
		}).then((res: {
			data: any;
			success?: boolean;
		}) => {
			console.log('branding image upload api response', res.data);
			return { success: true, data: res.data }
		})
		.catch((error) => {
			console.error('Error uploading branding image:', error);
			throw error;
		})
}

export const handleOAuth = async (request: any) => {
	return await monitorFail
		.post('/register/oauth', request)
}

export const authoriseGMail = async (gmailUser) => {
	return await monitorFail
		.post(`/authorisations/gmail`, gmailUser)
		.then(res => {
					return res
				})
}

export const updateNotificationToken = async (payload, userId) => {
	const body = { userId: userId, token: payload }
	await noMonitor.post('/user/update-notification-token', body)
}

export const passwordReset = async (formValues) => {
	const request = formValues.toLowerCase()
	return await monitoredRequest.post(`/password-reset/${request}`)
		.then((res => {
			if (res.status !== 201) {
				dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Oh snap!', type: 'error' } })
				return;
			}
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.data.message, type: 'success' } })
			return { success: true };
		}))
		.catch(e => {
			console.error(e)
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Please check the provided email address', type: 'error' } })
		})
};

export const validatePasswordReset = async (code: { code: string, newPassword: string }) => {
	if (code.code.length > 6) {
		return dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Please ensure that you have entered the correct code', type: 'error' } })
	}
	const payload = JSON.stringify({ code: code.code, newPassword: code.newPassword });
	return await api.post(`/validatePasswordReset`, payload)
		.then(res => {
			if (res.status !== 200) {
				console.error(res)
				dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.data.error, type: 'error' } })
			} else {
				dispatch({ type: HIDE_PASSWORD_RESET_MODAL })
				dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.data.message, type: 'success' } })
			}
		})
}

export const unlockUser = async (formValues) => {
	await api.post('/unlock', formValues)
		.then((res => {
			if (res.status !== 200) {
				dispatch({ type: FAILED, data: 'Request failed, please check your password and try again' })
				dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.data, type: 'error' } });
			}
			else {
				dispatch({ type: HIDE_LOCK_MODAL });
			}
		}))
		.catch((err => {
			console.log(err);
		}))
};


//Set logged in user
export const setCurrentUser = decoded => {
	dispatch({
		type: SUCCESS,
		loading: false,
		error: null,
		data: decoded,
		isAuthenticated: true
	});
};

//Messaging APIs
export const getAllUsers = async (page?: number, pageSize?: number) => {
	const params: any = {};
	if (page !== undefined) params.page = page;
	if (pageSize !== undefined) params.limit = pageSize;
	return await noMonitor.get(`/users`, { params }).then(res => res.data.data);
}

export const getAllMessagingUsers = async (page?: number, pageSize?: number, filterExternal?: boolean) => {
	const params: any = {};
	if (page !== undefined) params.page = page;
	if (pageSize !== undefined) params.limit = pageSize;
	if (filterExternal !== undefined) params.external = filterExternal;
	return await noMonitor.get(`/users/messaging`, { params }).then(res => res.data);
}

// Message request functions
export const sendMessageRequest = async (queryClient: any, targetUserId: string, message?: string) => {
	queryClient.invalidateQueries({ queryKey: ['all-users'] });
	return await api.post(`/message-request/send`, { targetUserId, message }).then(res => res.data);
}

export const getMessageRequests = async () => {
	return await noMonitor.get(`/message-request/list`).then(res => res.data.data);
}

export const acceptMessageRequest = async (invitationId: string) => {
	return await api.post(`/message-request/accept`, { invitationId }).then(res => res.data);
}

export const rejectMessageRequest = async (invitationId: string) => {
	return await api.post(`/message-request/reject`, { invitationId }).then(res => res.data);
}

export const getUserHierarchy = async () => {
	return await noMonitor.get(`/users/hierarchy`).then(res => { return res.data })
}

export const getLargeProfile = async (selectedId) => await api.post(`/users/profile`, selectedId).then(res => { if (res.status !== 200) { return null } let avatar = res.data.data.toString?.(); let prepend = 'data:image/png;base64,'; let selectedAvatar = prepend + avatar; return selectedAvatar });
export const getUserConversations = async () => await noMonitor.get(`/conversations/recent-conversations`).then(res => res.data.data);
export const getConversation = async (data) => await monitorFail.post(`/conversations/messages`, data);
export const postMessage = async (userId, data) => await api.post(`/conversations/messages/${userId}/${data.participantId}`, data);

// User loading 
export const setUserLoading = () => {
	return {
		type: USER_LOADING
	};
};

// @PaymentIntents
export const paymentSheet = async (req) => { return await noMonitor.post('/payments/payment-sheet', req).then(res => { return res.data.data }).catch(() => { throw new Error("No response") }) }

// Generate payment link for product (data item)
export const generateProductPaymentLink = async (params: {
	dataFolderId: string;
	dataItemId: string;
	paymentProvider: 'stripe' | 'paystack';
	recipientId?: string;
	businessPhoneNumberId?: string;
}) => {
	return await monitoredRequest.post('/payments/generate-product-payment-link', params).then(res => res.data);
}

/**
 * Switch company or user context
 * 
 * @param options - Switch options (supports multiple formats for backward compatibility)
 *   - String: Legacy format - companyNumber as string
 *   - Object: New format - { companyNumber?: string, targetUserId?: string }
 *   - Undefined/null: Switch to personal account
 * 
 * @returns Promise that resolves with switch response data
 * 
 * @example
 * // Switch to company (legacy format - still supported)
 * await switchCompany('companyId123')
 * 
 * @example
 * // Switch to company (new format)
 * await switchCompany({ companyNumber: 'companyId123' })
 * 
 * @example
 * // Switch to another user (premium feature)
 * await switchCompany({ targetUserId: 'userId456' })
 * 
 * @example
 * // Switch to personal account
 * await switchCompany() // or switchCompany({ companyNumber: null })
 */
export const switchCompany = async (options?: { companyNumber?: string | null, targetUserId?: string | null } | string | null) => {
	// Handle legacy string parameter for backward compatibility
	let companyNumber: string | null = null;
	let targetUserId: string | null = null;

	if (typeof options === 'string') {
		// Legacy usage: switchCompany(companyNumber)
		companyNumber = options && options.trim() !== '' ? options.trim() : null;
	} else if (options && typeof options === 'object') {
		// New usage: switchCompany({ companyNumber, targetUserId })
		companyNumber = options.companyNumber !== undefined
			? (options.companyNumber && typeof options.companyNumber === 'string' && options.companyNumber.trim() !== ''
				? options.companyNumber.trim()
				: null)
			: null;

		targetUserId = options.targetUserId !== undefined
			? (options.targetUserId && typeof options.targetUserId === 'string' && options.targetUserId.trim() !== ''
				? options.targetUserId.trim()
				: null)
			: null;
	}
	// If options is undefined or null, both remain null (switch to personal account)

	// Determine switch type for logging and error handling
	const switchType = targetUserId ? 'user' : (companyNumber ? 'company' : 'personal');
	console.log(`🔄 [Frontend] Switching context:`, { switchType, companyNumber, targetUserId });

	try {
		const response = await noMonitor.post(`/user/switch-company`, {
			companyNumber: companyNumber,
			targetUserId: targetUserId
		});

		const responseData = response.data;

		// Handle different response types based on what was switched
		if (targetUserId) {
			// User switch response - premium user switched to another user
			console.log('✅ [Frontend] User switched successfully:', {
				originalUserId: responseData.originalUserId || responseData.userId,
				targetUserId: responseData.targetUserId,
				targetUser: responseData.targetUser
			});
			dispatch({
				type: SWITCH_COMPANY_SUCCESSFUL,
				payload: {
					userData: responseData,
					switchType: 'user',
					originalUserId: responseData.originalUserId || responseData.userId,
					userId: responseData.targetUserId, // The target user ID
					targetUserId: responseData.targetUserId,
					targetUser: responseData.targetUser // User profile info for display
				}
			});
		} else if (companyNumber) {
			// Company switch response - user switched to a company
			console.log('✅ [Frontend] Company switched successfully:', {
				companyNumber: responseData.companyNumber,
				companyName: responseData.companyName
			});
			dispatch({
				type: SWITCH_COMPANY_SUCCESSFUL,
				payload: {
					companyData: responseData,
					switchType: 'company',
					companyNumber: responseData.companyNumber,
					companyName: responseData.companyName
				}
			});
		} else {
			// Personal account switch response - switched back to personal account
			console.log('✅ [Frontend] Switched to personal account');
			dispatch({
				type: SWITCH_COMPANY_SUCCESSFUL,
				payload: {
					companyData: responseData,
					switchType: 'personal',
					companyNumber: null
				}
			});
		}

		return responseData;
	} catch (e) {
		console.error(`❌ [Frontend] Error switching ${switchType}:`, e);
		dispatch({ type: SWITCH_COMPANY_FAILED });
		throw e; // Re-throw to allow caller to handle error
	}
}

export const postAvatar = async (userId, image) => { return await monitoredRequest.post(`/upload/user/${userId}/avatar`, image) }
export const postDeskImage = async (userId, image) => await monitoredRequest.post(`/upload/user/${userId}/deskImage`, image);
export const postLogo = async (companyId, image) => await api.post(`/upload/company/${companyId}`, image);

export const sendUserReg = async (userDetails) => {
	await api.post(`/adduser`, userDetails, { withCredentials: true })
		.then(res => {
			return
		})
}

export const logoutUser = async () => {
	return await logOut.get('/logout').then(() => {
		return true
	}).catch(() => {
		return true
	})
}

export const addData = async (formValues, companyId, userId, dataId) => {
	console.log(formValues);
	await api.post(`/${companyId}/${userId}/post-data/${dataId}`, formValues, { withCredentials: true })
		.then(() => {
			dispatch({ type: PENDING_REFRESH })
		})
}

export const refreshAchievements = async (userId) => {
	return await api.get(`/achievements/${userId}`, { withCredentials: true })
		.then(res => {
			return res.data.data
		})
}

export const getProducts = async () => {
	// Using noCreds instance to bypass auth and CORS
	return await noCreds.get('/get-product-list')
		.then(res => {
			if (res.status != 200) {
				dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.data.error, type: 'error' } })
			} else {
				dispatch({ type: UPDATE_PRODUCTS, payload: res.data.data })
				return res.data.data
			}
		})
}

export const getProductsInternal = async () => {
	// Using noCreds instance to bypass auth and CORS
	return await noMonitor.get('/get-products-list')
		.then(res => {
			if (res.status != 200) {
				dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.data.error, type: 'error' } })
			} else {
				dispatch({ type: UPDATE_PRODUCTS, payload: res.data.data })
				return res.data.data
			}
		})
}

export const getProductById = async (productId: string) => {
	if (!productId) {
		throw new Error('Product ID is required');
	}
	
	return await monitorFail.get(`/stripe/plan-product/${productId}`)
		.then(res => {
				return res.data;
		})
		.catch(error => {
			console.error('Error fetching product by ID:', error);
			throw error;
		})
}

export const getSubscriptions = async () => {
	return await noMonitor.get('/payment/get-user-subscription')
		.then(res => {
			if (res.status == 204) {
				return 'No subscriptions active'
			} else {
				return res.data
			}
		})
}

export const mailLogin = async (formValues, id) => {
	await api.post('/mail')
		.then(res => { console.log(res) })
}

export const getStream = async (companyId, userId) => {
	return await api.get(`/${companyId}/${userId}/eventStream`)
}

export const getActivityStreamLog = async (companyId, userId) => {
	return await api.get(`/${companyId}/${userId}/eventLog`)
}

export const getDataTypes = async (id) => {
	return await noMonitor.get(`/user/get-data-count`)
}
export const getCompanySchema = async (companyId) => {
	return await noMonitor.post(`/company/company-schema`, { companyId: companyId })
}

export const retrieveCompanyBillingInfo = async (companyId) => {
	return await api.get(`/billing/company/${companyId}`);
}

export const retrieveUserBillingInfo = async (userId) => {
	return await api.get(`/billing/${userId}`)
}

export const getIntegrations = async () => {
	return await noMonitor.get('/integrations').then(res => { return res.data.data })
}

export const getIntegrationSuggestions = async () => {
	return await monitoredRequest.post('/integrations/suggest', {}).then(res => { return res.data })
}

// Integration Request API functions
export const createIntegrationRequest = async (requestData) => {
	return await monitoredRequest.post('/integrations/requests', requestData);
}

export const getIntegrationRequests = async (params = {}) => {
	const queryString = new URLSearchParams(params).toString();
	return await noMonitor.get(`/integrations/requests?${queryString}`).then(res => res.data);
}

export const getPopularIntegrationRequests = async (category = null) => {
	const queryString = category ? `?category=${category}` : '';
	return await noMonitor.get(`/integrations/requests/popular${queryString}`).then(res => res.data);
}

export const getIntegrationRequestStats = async () => {
	return await noMonitor.get('/integrations/requests/stats').then(res => res.data);
}

export const voteOnIntegrationRequest = async (requestId, voteType) => {
	return await monitoredRequest.post(`/integrations/requests/${requestId}/vote`, { voteType });
}

export const updateIntegrationRequestStatus = async (requestId, status, adminNotes = null) => {
	return await monitoredRequest.put(`/integrations/requests/${requestId}/status`, { status, adminNotes });
}

export const addCompany = async (payload) => {
	await monitoredRequest.post(`/add-new-company`, payload);
}

export const getCalendarSchema = async () => {
	return await noMonitor.get('/calendar/get-calendar-schema')
		.then(res => {
			if (res.status != 203) {
				return dispatch({ type: SHOW_AUTH_MODAL, data: { error: 'Could not retrieve schema', type: 'error' } })
			} else {
				return res.data.data;
			}
		})
		.catch(e => {
			dispatch({ type: SHOW_AUTH_MODAL, data: { error: e, type: 'error' } })
		})
}

export const getProjects = async () => {
	return await noMonitor.get(`/projects/get-projects`)
}

export const getProjectsPaginated = async (options: {
	page?: number;
	limit?: number;
	sortBy?: string;
	sortOrder?: 'asc' | 'desc';
}) => {
	console.log("getting paginated projects with options:", options);
	return await monitorFail.get(`/projects/get-projects-paginated`, {
		params: {
			page: options.page || 1,
			limit: options.limit || 12,
			sortBy: options.sortBy || 'updatedAt',
			sortOrder: options.sortOrder || 'desc'
		}
	});
}

export const addProjects = async (project, id) => {
	const dateRanges = project.date_range;
	const newProj = { ...project, start_date: dateRanges.startDate, end_date: dateRanges.endDate }
	return await monitorFail.post('/projects/add-project', { userId: id, project: newProj }).then(() => { return { success: true } })
}

export const addLocalCalendars = async (calendars) => {
	return await noMonitor.post('/user/set-local-calendars', calendars)
}

export const getCalendarContent = async (date) => {
	return await noMonitor.post('/calendar/get-calendar', { date: date })
}

export const addCalendarEntry = async (calendarId, id) => {
	if (!calendarId) {
		return dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Please ensure you have selected a calendar first', type: 'error' } })
	}
	return await monitoredRequest.post('/calendar/add-calendar-entry', { userId: id, calendar: calendarId }).then(() => { return { success: true } })
}

export const updateCalendarEntry = async (entryId: string, calendar: any) => {
	if (!entryId || !calendar) {
		return dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Missing required fields for updating calendar entry', type: 'error' } })
	}
	return await monitoredRequest.put('/calendar/update-calendar-entry', { entryId, calendar })
}

export const removeCalendarEntry = async (entryId: string) => {
	if (!entryId) {
		return dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Missing entry ID for deletion', type: 'error' } })
	}
	return await monitoredRequest.post('/calendar/remove-calendar-entry', { entryId })
}

export const updateProject = async (project) => {
	if (!project) {
		return dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'No project data in request ', type: 'error ' } })
	}
}

export const getProjectSchema = async () => {
	return await noMonitor.get('/projects/get-project-schema')
		.then(res => {
			return res.data.data;
		})
}

export const getAddNetworkSchema = async () => {
	return await noMonitor.get('/user/get-networking-schema')
		.then(res => {
			return res.data.data;
		})
}

export const changeWPStatus = async (id, status) => {
	return await api.post('/projects/change-wp-status', { userId: id.globalId, projectId: id.dataId, workPackageId: id.extraId, status: status })
		.then(res => {
			if (!res) {
				return dispatch({ type: SHOW_AUTH_MODAL, data: { error: 'No project data in request ', type: 'error ' } })
			} else return { success: true }
		})
		.catch(e => {
			dispatch({ type: SHOW_AUTH_MODAL, data: { error: e, type: 'error ' } })
		})
}

export const changeWorkPackageTitle = async (wpId, projectId, title) => {
	return await noMonitor.post('/projects/change-wp-title', { projectId, wpId, title })
}


export const changeTaskTitle = async (id, title) => {
	return await noMonitor.post('/projects/change-task-title', { userId: id.globalId, projectId: id.dataId, workPackageId: id.extraId, taskId: id.taskId, title: title })
}

export const changeProjectTitle = async (projectId, value) => {
	return await noMonitor.post('/projects/change-project-title', { projectId: projectId, title: value })
}

export const changeProjectDescription = async (projectId, value) => {
	return await noMonitor.post('/projects/change-project-description', { projectId: projectId, description: value })
}

export const changeTaskDescription = async (id, description) => {
	return await noMonitor.post('/projects/change-task-description', { userId: id.globalId, projectId: id.dataId, workPackageId: id.extraId, taskId: id.taskId, description: description })
		.then(res => {
			if (!res) {
				return dispatch({ type: SHOW_AUTH_MODAL, data: { error: 'No project data in request ', type: 'error ' } })
			} else return { success: true }
		})
		.catch(e => {
			dispatch({ type: SHOW_AUTH_MODAL, data: { error: e, type: 'error ' } })
		})
}

export const deleteTask = async (id) => {
	// Backend expects: { projectId, workPackageId, taskId }
	return await noMonitor.post(`/projects/delete-task`, {
		projectId: id.dataId,
		workPackageId: id.extraId,
		taskId: id._id
	})
}

export const duplicateTask = async (id) => {
	return await noMonitor.post(`/projects/duplicate-task`, id)
}

export const duplicateEmailTemplate = async (id) => {
	return await monitorFail.post(`/templates/email/duplicate-template`, id)
}

export const duplicateChildTemplate = async (id: string, title: string) => {
	return await monitorFail.post(`/templates/email/duplicate-child-template`, { id, title })
}

export const saveEmailTemplateCopy = async (template) => {
	return await monitorFail.post(`/templates/email/save-template-copy`, template)
}

export const changeTaskStatus = async (id, status) => {
	// Backend expects: { id: { projectId, workPackageId, taskId }, status }
	return await api.post('/projects/change-task-status', { 
		id: {
			projectId: id.dataId,
			workPackageId: id.extraId, 
			taskId: id._id
		},
		status
	})
}

// Enhanced kanban drag and drop APIs
export const moveTask = async (projectId, taskId, moveData) => {
	const { sourceStatus, destinationStatus, sourceIndex, destinationIndex, workPackageId } = moveData;

	return await api.post(`/projects/${projectId}/tasks/${taskId}/move`, {
		sourceStatus,
		destinationStatus,
		sourceIndex,
		destinationIndex,
		workPackageId
	});
}

export const bulkMoveTasks = async (projectId, tasks) => {
	return await api.post(`/projects/${projectId}/tasks/bulk-move`, {
		tasks
	});
}

export const debugProjectData = async (projectId, userId) => {
	return await noMonitor.get(`/projects/${userId}/debug/${projectId}`);
}

export const changeTaskNotes = async (id, notes) => {
	return await api.post('/projects/change-task-notes', { id, notes })
}

export const deleteWorkPackage = async (id) => {
	return await monitoredRequest.post(`/projects/delete-wp`, id)
}

export const addTask = async (taskData, id) => {
	const { dateRange, ...rest } = taskData;
	rest.start_date = dateRange?.startDate;
	rest.end_date = dateRange?.endDate;

	const newTask = {
		...rest,
		dateRange: undefined
	}

	return await api.post('/projects/add-task', { userId: id.globalId, projectId: id.dataId, workPackageId: id.extraId, task: newTask })
}

export const addWorkPackage = async (workPackage, id) => {
	const { dateRange, ...rest } = workPackage;
	rest.start_date = dateRange?.startDate;
	rest.end_date = dateRange?.endDate;

	return await monitorFail.post('/projects/add-workpackage', { userId: id.globalId, projectId: id.dataId || id, workPackage: rest })
		.then(res => {
			return { data: { _id: res.data.data, ...rest, start_date: new Date(rest.start_date).toLocaleString(), end_date: new Date(rest.end_date).toLocaleDateString(), task_list: [], team: [] } }
		})
}

export const getTaskSchema = async () => {
	return await noMonitor.get('/projects/get-task-schema')
		.then(res => {
			if (!res) {
				return dispatch({ type: SHOW_AUTH_MODAL, data: { error: 'No project data in request ', type: 'error ' } })
			} else {
				return res.data.data;
			}
		})
		.catch(e => {
			dispatch({ type: SHOW_AUTH_MODAL, data: { error: e, type: 'error ' } })
		})
}

export const getWPSchema = async () => {
	return await noMonitor.get('/projects/get-workpackage-schema')
		.then(res => {
			return res.data.data;
		})
}

export const dispatchAppAction = async (action) => {
	//TODO Destructure userId and determine if user is logged into a company, then it would be best to assign company values and user values independently, i.e. increment user count individually to company account. Hide details of the action from the user and instad use vague metrics to give them more useful information

	let payload = { actionType: action.type, payload: action.payload, appVersion: Constants.manifest.version }

	await noMonitor.put(`/userAppHistory/${'uhkj'}`, payload)
		.then((res) => {
			if (!res) {
				//TODO add to local storage until connection is established
				return;
			}
			dispatch({ type: UPDATE_ACHIEVEMENTS, payload: res.data.points })
		}).catch(e => {
			console.error(e)
		});
	return;
}

export const getLoginMessages = async () => {
	return await noMonitor.get(`/login-messages`).then(res => {
		if (res.status != 200) {
			console.error(res)
		}
		return res.data.data
	})
}

export const getData = async (userId) => {
	return await noMonitor.post(`/data/get-data`, { id: userId }).then(res => {
		return res.data.data
	})
}

type DataFolderType = {
	userId: string,
	dataFilter?: string
}

export const getDataFolders = async ({ userId, dataFilter }: DataFolderType) => {
	try {
		const res = await noMonitor.post(`/data/get-data-folders`, { id: userId, dataFilter }).then(res => res.data);
		// Handle the nested response structure
		if (res && res.data && Array.isArray(res.data)) {
			console.log('🔍 Found data array with', res.data.length, 'items');
			return res.data;
		} else if (res && Array.isArray(res)) {
			console.log('🔍 Found direct array with', res.length, 'items');
			return res;
		} else {
			return [];
		}
	} catch (error) {
		console.error("Error fetching data folders:", error);
		return [];
	}
};

export const getFolderStats = async (folderId: string, month?: string | Date) => {
	return await noMonitor.post(`/data/get-folder-stats`, { id: folderId, month }).then(res => {
		return res.data
	})
}

export const getAllFolderStats = async () => {
	return await noMonitor.get(`/user/get-all-folder-stats`).then(res => {
		return res.data.data
	})
}

// POI (Points of Interest) lead generation
export const addPOIsToDataModel = async (
	selectedPOIs: any[],
	dataModelId: string | null,
	createNew: boolean,
	modelName?: string,
	modelTags?: string[]
) => {
	try {
		const res = await api.post('/data/add-pois-to-model', {
			selectedPOIs,
			dataModelId,
			createNew,
			modelName,
			modelTags
		});
		return res.data;
	} catch (error) {
		console.error('Error adding POIs to data model:', error);
		throw error;
	}
};

// Article scraping for knowledge base
export const scrapeArticleToKnowledge = async (
	url: string,
	title?: string,
	type: 'internal' | 'external' = 'internal'
) => {
	try {
		console.log('📤 [API] Sending scrape article request:', { url, title, type });
		const res = await api.post('/ai/scrape-article', {
			url,
			title,
			type
		}, { timeout: 120000 });
		console.log('✅ [API] Scrape article response received:', res.data);
		return res.data;
	} catch (error) {
		console.error('❌ [API] Error scraping article:', error);
		console.error('❌ [API] Error response:', error.response?.data);
		throw error;
	}
};

// Advanced data analytics
export const getDataInsights = async (params: {
	timeRange?: '7d' | '30d' | '90d' | '1y';
	groupBy?: 'hour' | 'day' | 'week' | 'month' | 'year';
	includeMetrics?: 'all' | 'basic';
}) => {
	try {
		const res = await noMonitor.post('/data/insights', params);
		return res.data;
	} catch (error) {
		console.error('Error fetching data insights:', error);
		return { success: false, data: null };
	}
};

export const getFolderComparison = async (folderIds: string[], metrics: string[] = ['items', 'views', 'interactions']) => {
	try {
		const res = await noMonitor.post('/data/folder-comparison', { folderIds, metrics });
		return res.data;
	} catch (error) {
		console.error('Error fetching folder comparison:', error);
		return { success: false, data: [] };
	}
};

export const getPredictiveAnalytics = async (analysisType: 'growth' | 'usage' = 'growth', period: '30d' | '90d' | '1y' = '30d') => {
	try {
		const res = await noMonitor.post('/data/predictive-analytics', { analysisType, period });
		return res.data;
	} catch (error) {
		console.error('Error fetching predictive analytics:', error);
		return { success: false, data: null };
	}
};

export const getProductList = async (userId) => {
	return await api.get(`/get-product-list/${userId}`, { withCredentials: true }).then(res => {
		if (res.status != 200) {
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.data.error, type: 'error' } })
			return;
		}
		return res.data.data
	})
}

export const getSampleSchemas = async () => {
	return await noMonitor.get('/get-schemas', { withCredentials: true }).then(res => {
		return res.data.data
	})
}

// Database Connection API Methods
export const createDatabaseConnection = async (connectionData) => {
	return await monitoredRequest.post('/database-connections', connectionData)
		.then(res => {
			if (res.data.success) {
				return res.data.data;
			}
			throw new Error(res.data.error || 'Failed to create database connection');
		})
		.catch(error => {
			console.error('Error creating database connection:', error);
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: error.response?.data?.error || error.message || 'Failed to create database connection', type: 'error' } });
			throw error;
		});
}

export const getDatabaseConnections = async (scope) => {
	const params = scope ? { scope } : {};
	return await monitoredRequest.get('/database-connections', { params })
		.then(res => {
			if (res.data.success) {
				return res.data.data;
			}
			return [];
		})
		.catch(error => {
			console.error('Error fetching database connections:', error);
			return [];
		});
}

export const getDatabaseConnection = async (id) => {
	return await monitoredRequest.get(`/database-connections/${id}`)
		.then(res => {
			if (res.data.success) {
				return res.data.data;
			}
			throw new Error(res.data.error || 'Database connection not found');
		})
		.catch(error => {
			console.error('Error fetching database connection:', error);
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: error.response?.data?.error || error.message || 'Failed to fetch database connection', type: 'error' } });
			throw error;
		});
}

export const updateDatabaseConnection = async (id, connectionData) => {
	return await monitoredRequest.put(`/database-connections/${id}`, connectionData)
		.then(res => {
			if (res.data.success) {
				return res.data.data;
			}
			throw new Error(res.data.error || 'Failed to update database connection');
		})
		.catch(error => {
			console.error('Error updating database connection:', error);
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: error.response?.data?.error || error.message || 'Failed to update database connection', type: 'error' } });
			throw error;
		});
}

export const deleteDatabaseConnection = async (id) => {
	return await monitoredRequest.delete(`/database-connections/${id}`)
		.then(res => {
			if (res.data.success) {
				dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.data.message || 'Database connection deleted successfully', type: 'success' } });
				return true;
			}
			throw new Error(res.data.error || 'Failed to delete database connection');
		})
		.catch(error => {
			console.error('Error deleting database connection:', error);
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: error.response?.data?.error || error.message || 'Failed to delete database connection', type: 'error' } });
			throw error;
		});
}

export const testDatabaseConnection = async (id) => {
	return await monitoredRequest.post(`/database-connections/${id}/test`)
		.then(res => {
			if (res.data.success) {
				dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.data.message || 'Connection test successful', type: 'success' } });
				return res.data.data;
			}
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.data.message || 'Connection test failed', type: 'error' } });
			return res.data;
		})
		.catch(error => {
			console.error('Error testing database connection:', error);
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: error.response?.data?.error || error.message || 'Connection test failed', type: 'error' } });
			throw error;
		});
}

export const getDatabaseTables = async (connectionId) => {
	return await monitoredRequest.get(`/database-connections/${connectionId}/tables`)
		.then(res => {
			if (res.data.success) {
				return res.data.data;
			}
			return [];
		})
		.catch(error => {
			console.error('Error fetching database tables:', error);
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: error.response?.data?.error || error.message || 'Failed to fetch database tables', type: 'error' } });
			return [];
		});
}

export const importDatabaseSchema = async (connectionId, tableNames, tags) => {
	return await monitoredRequest.post(`/database-connections/${connectionId}/import-schema`, {
		tableNames,
		tags
	})
		.then(res => {
			if (res.data.success) {
				dispatch({ type: SHOW_AUTH_MODAL, data: { content: res.data.message || `Successfully imported ${res.data.data?.length || 0} table schema(s)`, type: 'success' } });
				return res.data.data;
			}
			throw new Error(res.data.error || 'Failed to import database schema');
		})
		.catch(error => {
			console.error('Error importing database schema:', error);
			dispatch({ type: SHOW_AUTH_MODAL, data: { content: error.response?.data?.error || error.message || 'Failed to import database schema', type: 'error' } });
			throw error;
		});
}

export const saveData = async (payload) => {
	const { id } = payload;
	return await monitoredRequest.post(`/data/add-new-data/${id}`, payload)
		.then(() => {
			return { success: true }
		})
}

export const uploadData = async (payload, onProgress?: (data: any) => void) => {
	const { id } = payload;
	
	return new Promise((resolve, reject) => {
		// Show initial loading modal
		dispatchSSEProgress({
			status: 'starting',
			message: 'Preparing data upload...',
			progress: 0
		});

		// Use fetch with SSE support for progress tracking
		fetch(`${USER_API_BASE_URL}/data/upload-new-data/${id}`, {
			method: 'POST',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
				'Accept': 'text/event-stream'
			},
			body: JSON.stringify(payload)
		}).then(response => {
			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const reader = response.body?.getReader();
			if (!reader) {
				throw new Error('No response body reader available');
			}

			const decoder = new TextDecoder();
			let buffer = '';

			const processStream = () => {
				return reader.read().then(({ done, value }) => {
					if (done) {
						dispatchSSEProgress({
							status: 'completed',
							message: 'Data upload completed successfully!',
							progress: 100
						});

						// Hide modal after a brief delay
						setTimeout(() => {
							const { dispatch } = store;
							dispatch({ type: HIDE_ALL_MODALS });
						}, 1500);

						resolve({ success: true });
						return;
					}

					buffer += decoder.decode(value, { stream: true });
					const lines = buffer.split('\n');
					buffer = lines.pop() || ''; // Keep incomplete line in buffer

					lines.forEach(line => {
						if (line.trim() && line.startsWith('data: ')) {
							try {
								const data = JSON.parse(line.slice(6));
								
								// Update progress in modal
								dispatchSSEProgress(data);
								
								// Call optional progress callback
								if (onProgress) {
									onProgress(data);
								}
							} catch (e) {
								console.warn('Failed to parse SSE data:', line);
							}
						}
					});

					return processStream();
				});
			};

			return processStream();
		}).catch(error => {
			console.error('Upload error:', error);
			
			// Show error in modal
			dispatchSSEProgress({
				status: 'error',
				message: error.message || 'Upload failed',
				progress: 0
			});

			// Hide modal after error display
			setTimeout(() => {
				const { dispatch } = store;
				dispatch({ type: HIDE_ALL_MODALS });
			}, 3000);

			reject(error);
		});
	});
}

export const getDataContent = async (dataId: string, type: string) => {
	return await monitorFail.get(`/data/get-data-content/${dataId}?type=${type}`)
}

export const getDataContentPaginated = async (dataId: string, options: {
	page?: number;
	limit?: number;
	sortBy?: string;
	sortOrder?: 'asc' | 'desc';
}) => {
	console.log("getting paginated data content for dataId:", dataId, "with options:", options);
	return await monitorFail.post(`/data/get-data-content-paginated`, {
		id: dataId,
		page: options.page || 1,
		limit: options.limit || 10,
		sortBy: options.sortBy || 'updatedAt',
		sortOrder: options.sortOrder || 'desc'
	});
}

export const getDataFoldersPaginated = async (userId: string, options: {
	page: number;
	pageSize: number;
	sortBy?: string;
	sortOrder?: 'asc' | 'desc';
	dataFilter?: string;
}) => {
	console.log("getting paginated data folders for userId:", userId, "with options:", options);
	const response = await monitorFail.post(`/data/get-data-folders-paginated`, {
		id: userId,
		page: options.page,
		limit: options.pageSize,
		sortBy: options.sortBy || 'updatedAt',
		sortOrder: options.sortOrder || 'desc',
		dataFilter: options.dataFilter
	});
	return response.data;
}
export const addDataItem = async (payload, ids) => {
	const { globalId, dataId } = ids;
	return await noMonitor.post(`/data/add-data-content/${globalId}/${dataId}`, payload).then(res => {
		return res
	})
}

export const getDataSchema = async (userId, dataId) => {
	return await noMonitor.get(`/data/get-data-schema/${userId}/${dataId}`)
}

export const getProfileSchema = async () => {
	return await noMonitor.get('/user/get-profile-schema')
}

export const getUserSocialMedia = async (targetUserId = null) => {
	const endpoint = targetUserId
		? `/user/social-media/${targetUserId}`
		: '/user/social-media';
	return await noMonitor.get(endpoint);
}

export const updateUserSocialMedia = async (socialMediaData) => {
	return await noMonitor.post('/user/social-media/update', { socialMedia: socialMediaData });
}

// Custom Views API functions
export const getCustomViews = async (dataId) => {
	if (!dataId) {
		throw new Error('dataId is required');
	}
	return await noMonitor.get(`/custom-views?dataId=${dataId}`);
}

export const getCustomView = async (viewId, dataId) => {
	if (!dataId) {
		throw new Error('dataId is required');
	}
	return await noMonitor.get(`/custom-views/${viewId}?dataId=${dataId}`);
}

export const saveCustomView = async (viewData) => {
	if (!viewData.dataId) {
		throw new Error('dataId is required in viewData');
	}
	return await noMonitor.post('/custom-views', viewData);
}

export const deleteCustomView = async (viewId, dataId) => {
	if (!dataId) {
		throw new Error('dataId is required');
	}
	return await noMonitor.delete(`/custom-views/${viewId}?dataId=${dataId}`);
}

export const duplicateCustomView = async (viewId, dataId, newName = null) => {
	if (!dataId) {
		throw new Error('dataId is required');
	}
	return await noMonitor.post(`/custom-views/${viewId}/duplicate`, { newName, dataId });
}

export const getCustomViewTemplates = async () => {
	return await noMonitor.get('/custom-views/templates');
}

// AI Project Creation
export const createAIProject = async (projectData) => {
	return await noMonitor.post('/ai/create-project', projectData, { timeout: 120000 });
}

export const generateAIProjectFromDescription = async (projectDescription: string, options: {
	industry?: string;
	projectType?: string;
	timeline?: string;
	teamSize?: string;
} = {}) => {
	return await noMonitor.post('/ai/create-project', {
		projectDescription,
		industry: options.industry || 'general',
		projectType: options.projectType || 'standard',
		timeline: options.timeline || 'medium',
		teamSize: options.teamSize || 'small'
	}, { timeout: 120000 });
}

export const getAIProjectTemplates = async () => {
	return await noMonitor.get('/ai/project-templates', { timeout: 120000 });
}

export const getAddInvoiceTemplateSchema = async () => {
	return await noMonitor.get('/templates/invoices/add-template-schema');
}

export const addTagsToData = async (dataId, tag) => {
	return await noMonitor.post('/data/add-tag', { dataId: dataId, tags: tag })
}

export const searchUsers = async (text) => {
	return await noMonitor.post('/search-users', { text: text }).then(res => { return res.data.records })
}

export const getReport = async (dataId) => {
	return await api.post('/data/get-report-data', { dataId: dataId })
}

export const deleteDataItem = async (dataId) => {
	return await noMonitor.delete(`/data/delete-item/${dataId}`)
}

export const deleteData = async (dataId) => {
	console.log('deleting data', dataId)
	return await noMonitor.delete(`/data/delete-data/${dataId}`)
}

export const moveDataItem = async (dataId, folderId) => {
	return await noMonitor.patch(`/data/move-item/${dataId}`, { targetId: folderId })
}

export const deleteProject = async (globalId, projectId) => {
	return await monitoredRequest.delete(`/projects/delete-project/${globalId}/${projectId}`)
}

export const getProfileCompletion = async (globalId) => {
	return await noMonitor.get(`/user/profile-calculator`)
}

export const getProjectCount = async (globalId) => {
	return await noMonitor.get(`/${globalId}/get-project-count`)
}

export const getDataCount = async () => {
	return await noMonitor.get(`/user/get-data-count`)
}

export const saveNewsItem = async (globalId, news) => {
	return await noMonitor.post(`/${globalId}/post-saved-news`, news)
}

export const getSavedNews = async (globalId) => {
	return await noMonitor.get(`/${globalId}/get-saved-news`);
}

export const getProject = async (projectId, globalId) => {
	return await noMonitor.get(`/projects/${globalId}/get-project/${projectId}`)
}

export const addCalendar = async (globalId, calendar) => {
	return await monitoredRequest.post(`/calendar/${globalId}/add-calendar`, calendar)
}

export const addStickyNote = async (note) => {
	return await noMonitor.post(`/notes/post-sticky-note`, note)
}

export const pinNote = async (noteId) => {
	return await noMonitor.put(`/notes/pin-note/${noteId}`)
}

export const unPinNote = async (noteId) => {
	return await noMonitor.put(`/notes/unpin-note/${noteId}`)
}

export const modifyStickyNote = async (note) => {
	return await noMonitor.post(`/notes/modify-sticky-note/${note._id}`, note)
}

export const removeStickyNote = async (globalId, noteId) => {
	return await noMonitor.delete(`/notes/remove-sticky-note/${noteId}`)
}

export const getStickyNotes = async () => {
	return await noMonitor.get(`/notes/get-sticky-notes`).then(res => {
		return res.data.data.sticky_notes;
	})
}

export const markNoteComplete = async (globalId, note) => {
	return await noMonitor.post(`/notes/mark-note-complete/${note._id}`, note)
}

export const setNoteColor = async (globalId, note) => {
	return await noMonitor.post(`/notes/change-note-color/${note._id || note.id}`, note)
}

export const getInitialModels = async (req) => {
	return await noMonitor.post(`/get-initial-models`, req);
}

export const identifyOnboardingModels = async (businessDescription, tags = [], userId, companyId) => {
	return await noMonitor.post(`/ai/onboarding/identify-models`, {
		businessDescription,
		tags,
		userId,
		companyId
	});
}

export const manipulateHierarchy = async (query, hierarchy, dataSchema = null) => {
	return await noMonitor.post(`/ai/hierarchy/manipulate`, {
		query,
		hierarchy,
		dataSchema
	});
}

export const postInitialModel = async (modelList, userId) => {
	return await noMonitor.post(`/data/${userId}/add-initial-data`, modelList);
}

export const getStandardisedReports = async (userId, previousList) => {
	return await noMonitor.post(`/data/post-standardised-reports/${userId}`, previousList)
}

export const validate2FA = async (userId, token) => {
	return await monitoredRequest.post(`/security/${userId}/validate-two-fa`, { token: token })

}

export const validateOTP = async (storedCred) => {
	return await monitoredRequest.post(`/validate-otp`, storedCred)
}

export const setTutorial = async () => {
	return await noMonitor.get(`/user/set-tutorial-modal`)
}

export const setPublicDataSwipeTutorial = async () => {
	return await noMonitor.get(`/user/set-public-swipe`)
}
export const setInvitationSwipeTutorial = async () => {
	return await noMonitor.get('/user/set-invite-swipe')
}
export const getAvatarById = async (id) => {
	return await noMonitor.get(`/get-avatar-image/${id}`).then(res => { return res.data.data.profile_information.avatar.img })
}

export const getRoomCounts = async () => {
	return await noMonitor.get('/chat-rooms')
}
export const getRoom = async (room) => {
	return await noMonitor.get(`/networking-room/${room}`)
}

export const updateTC = async (userId, tcVersion) => {
	return await noMonitor.post('/user/terms/update', { user: userId, version: tcVersion })
}

export const getActivityLog = async (userId) => {
	return await noMonitor.get(`/activity-log/${userId}`);
}

export const addLocation = async (location) => {
	return await noMonitor.post('/company/add-networking', location)
}

export const getAchievementHistory = async () => {
	return await noMonitor.get(`/achievements-history`).then(res => { return res.data.data })
}

export const shareDataItem = async (body) => {
	return await monitoredRequest.post(`/share/data/invite`, body).then(res => res.data.data)
}

export const shareProject = async (body) => {
	return await monitoredRequest.post(`/share/project/invite`, body).then(res => res.data.data)
};

export const acceptCompanyInvitation = async (invitationId) => {
	return await monitoredRequest.post(`/company/accept-invitation`, invitationId).then(res => res.data.data)
}

export const addUserToCompany = async (data) => {
	return await monitoredRequest.post('/users/invite-user', data).then(res => res.data)
}

export const getUser = async (userId) => {
	return await noMonitor.get(`/user/${userId}/get-small-profile`).then(res => res.data.data)
}

export const getUserById = async (userId) => {
	return await noMonitor.get(`/users/getUser/${userId}`).then(res => res.data).catch(e => console.error(e))
}

export const getPublicProfile = async (userId) => {
	return await noMonitor.get(`/users/public-profile/${userId}`).then(res => res.data).catch(e => console.error(e))
}

// Engagement tracking methods
export const trackFormView = async (formId, source = 'unknown') => {
	try {
		return await noMonitor.post('/engagement/track-form-view', { formId, source }).then(res => res.data).catch(e => {
			console.error('Error tracking form view:', e);
			return { success: false };
		});
	} catch (error) {
		console.error('Error tracking form view:', error);
		return { success: false };
	}
}

export const trackFormSubmission = async (formId, source = 'unknown', completed = false) => {
	try {
		return await noMonitor.post('/engagement/track-form-submission', { formId, source, completed }).then(res => res.data).catch(e => {
			console.error('Error tracking form submission:', e);
			return { success: false };
		});
	} catch (error) {
		console.error('Error tracking form submission:', error);
		return { success: false };
	}
}

export const trackDataContribution = async (dataId, actionType = 'create') => {
	try {
		return await noMonitor.post('/engagement/track-data-contribution', { dataId, actionType }).then(res => res.data).catch(e => {
			console.error('Error tracking data contribution:', e);
			return { success: false };
		});
	} catch (error) {
		console.error('Error tracking data contribution:', error);
		return { success: false };
	}
}

export const trackDataInteraction = async (dataId, interactionType, targetUserId = null) => {
	try {
		return await noMonitor.post('/engagement/track-data-interaction', { dataId, interactionType, targetUserId }).then(res => res.data).catch(e => {
			console.warn('Error tracking data interaction:', e);
			return { success: false };
		});
	} catch (error) {
		console.warn('Error tracking data interaction:', error);
		return { success: false };
	}
}

export const getInvitations = async () => {
	console.log('getting invites');
	return await noMonitor.get(`/share/data/invitations`).then(res => res.data.data)
}

export const getInvitationCount = async () => {
	console.log('calling invitation count')
	return await noMonitor.get(`/share/data/invitation-count`).then(res => res.data.data)
}

export const acceptInvitation = async (invitationId) => {
	return await monitoredRequest.post(`/share/data/accept-invitation/${invitationId}`).then(res => res.data.data)
}

export const rejectInvitation = async (invitationId) => {
	return await noMonitor.post(`/share/data/reject-invitation/${invitationId}`).then(res => res.data.data)
}

export const revokeInvitation = async (invitationId) => {
	return await monitoredRequest.post(`/share/data/revoke-invitation/${invitationId}`).then(res => res.data.data)
}

export const getPublicData = async (sort, page = 1, limit = 20, search = '', filters = {}) => {
	const params = new URLSearchParams();
	if (sort) params.append('sortBy', sort);
	if (page) params.append('page', page.toString());
	if (limit) params.append('limit', limit.toString());
	if (search) params.append('search', search);

	if (filters?.minSize) params.append('minSize', filters.minSize);
	if (filters?.maxSize) params.append('maxSize', filters.maxSize);
	if (filters?.location) params.append('location', filters.location);
	
	const queryString = params.toString();
	const url = `/data/public-data${queryString ? `?${queryString}` : ''}`;
	
	return await noMonitor.get(url).then(res => res.data)
}

export const getCompanyList = async () => {
	return await noMonitor.get(`/get-company-list`).then(res => res.data.data)
}

export const updateDataItem = async (dataId, values) => {
	return await monitorFail.post(`/data/update-item`, {dataId, values}).then(res => res.data.data)
}

export const hideFirstTutorial = async () => {
	await noMonitor.get(`/user/hide-first-tutorial`)
}

export const getPreviewData = async () => {
	await noMonitor.post('/data/get-preview-data',)
}
export const saveEmailTemplate = async (payload) => {
	return await monitoredRequest.post('/templates/post-email-template', payload).then(() => { return { success: true } })
}

export const updateEmailTemplate = async (payload) => {
	return await monitoredRequest.post('/templates/update-email-template', payload)
}

export const getInitialTemplates = async () => {

}

export const getEmailTemplates = async () => {
	return await noMonitor.get('/templates/get-email-templates').then(res => { return res.data.data })
}

export const getInvoiceTemplates = async () => {
	return await noMonitor.get('/templates/invoices/get-templates').then(res => { return res.data.data })
}

export const getSentInvoices = async (pagination = {}) => {
	const queryParams = new URLSearchParams();
	if (pagination.page) queryParams.append('page', pagination.page);
	if (pagination.limit) queryParams.append('limit', pagination.limit);
	if (pagination.sortBy) queryParams.append('sortBy', pagination.sortBy);
	if (pagination.sortOrder) queryParams.append('sortOrder', pagination.sortOrder);

	const queryString = queryParams.toString();
	const url = `/invoices/sent${queryString ? `?${queryString}` : ''}`;
	return await noMonitor.get(url).then(res => { return res.data })
}

export const getInvoiceById = async (invoiceId) => {
	return await noMonitor.get(`/invoices/${invoiceId}`).then(res => { return res.data.data })
}

export const markInvoiceAsViewed = async (invoiceId) => {
	return await noMonitor.patch(`/invoices/${invoiceId}/viewed`).then(res => { return res.data })
}

export const getEmailTemplate = async (templateId) => {
	return await noMonitor.get(`/templates/get-single-email-template/${templateId}`).then(res => { return res.data.data })
}

export const editEmailTemplate = async (templatedId, content) => {
	return await noMonitor.post(`/templates/edit-email-template/${templatedId}`, content)
}

export const deleteEmailTemplate = async (templateId) => {
	return await noMonitor.delete(`/templates/delete-email-template/${templateId}`)
}

export const getServiceList = async (userId) => {
	return await noCreds.get(`/get-service-list/${userId}`).then(res => { return res.data.data })
}

export const getStoreInfo = async (storeId) => {
	return await noCreds.get(`/get-store-info/${storeId}`).then(res => { return res.data.data })
} 

// Helper function to dispatch SSE progress to AuthModal
export const dispatchSSEProgress = (progressData: any) => {
	const { dispatch } = store;
	dispatch({
		type: SHOW_AUTH_MODAL,
		data: {
			modalType: 'progress',
			modalContent: progressData.message || 'Processing...',
			sseData: progressData
		}
	});
};

export const addDataImage = async (dataId: string, contentId: string, image: any, onProgress?: (data: any) => void) => {
	return new Promise((resolve, reject) => {
		// Show initial loading modal
		dispatchSSEProgress({
			status: 'starting',
			message: 'Preparing upload...',
			progress: 0
		});

		// Create the correct payload structure for the server
		let payload;

		// Handle different image formats and create the expected structure
		if (typeof image === 'string') {
			// If image is a base64 string
			if (image.startsWith('data:')) {
				// Extract MIME type from data URL
				const mimeType = image.match(/data:([^;]+)/)?.[1] || 'image/png';
				const fileName = `image.${mimeType.split('/')[1] || 'png'}`;

				payload = {
					img: {
						uri: image,
						fileName: fileName,
						mimeType: mimeType
					}
				};
			} else {
				// Plain string, assume it's base64 without data URL prefix
				payload = {
					img: {
						uri: `data:image/png;base64,${image}`,
						fileName: 'image.png',
						mimeType: 'image/png'
					}
				};
			}
		} else if (image instanceof File) {
			// Convert File to base64 data URL
			const reader = new FileReader();
			return new Promise((resolve, reject) => {
				reader.onload = () => {
					const dataUrl = reader.result as string;
					const imagePayload = {
						uri: dataUrl,
						fileName: image.name || 'image.png',
						mimeType: image.type || 'image/png'
					};

					// Recursive call with the converted data
					addDataImage(dataId, contentId, imagePayload, onProgress)
						.then(resolve)
						.catch(reject);
				};
				reader.onerror = () => reject(new Error('Failed to read file'));
				reader.readAsDataURL(image);
			});
		} else if (image instanceof Blob) {
			// Convert Blob to base64 data URL
			const reader = new FileReader();
			return new Promise((resolve, reject) => {
				reader.onload = () => {
					const dataUrl = reader.result as string;
					const imagePayload = {
						uri: dataUrl,
						fileName: 'image.png',
						mimeType: image.type || 'image/png'
					};

					// Recursive call with the converted data
					addDataImage(dataId, contentId, imagePayload, onProgress)
						.then(resolve)
						.catch(reject);
				};
				reader.onerror = () => reject(new Error('Failed to read blob'));
				reader.readAsDataURL(image);
			});
		} else if (image && typeof image === 'object') {
			// If image is already an object, check if it has the right structure
			if (image.uri || image.url) {
				payload = {
					img: {
						uri: image.uri || image.url,
						fileName: image.fileName || image.name || 'image.png',
						mimeType: image.mimeType || image.type || 'image/png'
					}
				};
			} else if (image.base64) {
				// Object with base64 property
				const dataUrl = image.base64.startsWith('data:') ? image.base64 : `data:${image.type || 'image/png'};base64,${image.base64}`;
				payload = {
					img: {
						uri: dataUrl,
						fileName: image.fileName || image.name || 'image.png',
						mimeType: image.type || 'image/png'
					}
				};
			} else {
				// Assume it's already in the correct format or fallback
				payload = { img: image };
			}
		} else {
			throw new Error('Unsupported image format');
		}

		// Use fetch with JSON payload (not FormData)
		fetch(`${USER_API_BASE_URL}/upload/add-data-image/${dataId}/${contentId}`, {
			method: 'POST',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
				'Accept': 'text/event-stream'
			},
			body: JSON.stringify(payload)
		}).then(response => {
			console.log('Response received:', response.status, response.headers.get('content-type'));

			if (!response.ok) {
				dispatchSSEProgress({
					status: 'error',
					message: `HTTP error! status: ${response.status}`,
					progress: 0
				});
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			// Check if response is actually SSE
			const contentType = response.headers.get('content-type');
			if (!contentType || !contentType.includes('text/event-stream')) {
				console.warn('Response is not SSE format, content-type:', contentType);
				// Try to read as regular response
				return response.text().then(text => {
					try {
						const data = JSON.parse(text);
						resolve(data);
					} catch (e) {
						reject(new Error('Invalid response format'));
					}
				});
			}

			const reader = response.body?.getReader();
			if (!reader) {
				dispatchSSEProgress({
					status: 'error',
					message: 'Response body is not readable',
					progress: 0
				});
				throw new Error('Response body is not readable');
			}

			let buffer = ''; // Buffer for incomplete chunks

			function readStream() {
				reader.read().then(({ done, value }) => {
					if (done) {
						console.log('Stream ended, buffer:', buffer);
						// Check if we have unprocessed data in buffer
						if (buffer.trim()) {
							processBuffer(buffer);
						}
						return;
					}

					try {
						const chunk = new TextDecoder().decode(value);
						buffer += chunk;

						// Process complete lines
						const lines = buffer.split('\n');
						// Keep the last incomplete line in buffer
						buffer = lines.pop() || '';

						lines.forEach(processLine);

						// Continue reading the stream
						readStream();

					} catch (streamError) {
						console.error('Stream reading error:', streamError);
						dispatchSSEProgress({
							status: 'error',
							message: streamError.message || 'Stream error occurred',
							progress: 0
						});
						reject(streamError);
					}
				}).catch(error => {
					console.error('Reader error:', error);
					dispatchSSEProgress({
						status: 'error',
						message: error.message || 'Stream reading failed',
						progress: 0
					});
					reject(error);
				});
			}

			function processLine(line) {
				line = line.trim();
				if (line.startsWith('data: ')) {
					try {
						const jsonData = line.substring(6).trim();
						if (jsonData) {
							const data = JSON.parse(jsonData);
							console.log('SSE data received:', data);

							// Dispatch to AuthModal
							dispatchSSEProgress(data);

							// Call progress callback if provided
							if (onProgress) {
								onProgress(data);
							}

							// Handle different status types
							switch (data.status) {
								case 'starting':
								case 'uploading':
								case 'processing':
								case 'progress':
									console.log(`Upload ${data.status}: ${data.message}`);
									break;
								case 'completed':
									console.log('Upload completed successfully');
									resolve(data.data || data.url || data); // Return the result
									return;
								case 'error':
									console.error('Upload error:', data.message);
									reject(new Error(data.message || 'Upload failed'));
									return;
							}
						}
					} catch (parseError) {
						console.warn('Failed to parse SSE data:', line, parseError);
					}
				} else if (line === '' || line.startsWith(':')) {
					// Empty line or comment, ignore
				} else {
					console.log('Non-data SSE line:', line);
				}
			}

			function processBuffer(bufferData) {
				bufferData.split('\n').forEach(processLine);
			}

			// Start reading the stream
			readStream();

		}).catch(error => {
			console.error('Fetch error:', error);
			dispatchSSEProgress({
				status: 'error',
				message: error.message || 'Upload failed',
				progress: 0
			});
			reject(error);
		});
	});
}

export const getDataContentById = async (dataId, contentId) => {
	const response = await noMonitor.get(`/data/content/${dataId}/${contentId}`);
	// Return both data and schema from the response
	return {
		data: response.data.data,
		schema: response.data.schema,
		...response.data.data // Spread data properties for backward compatibility
	};
}

export const postInvoiceTemplate = async (data) => {
	return await noMonitor.post('/templates/invoices/post-template', data).then((res) => { if (res.status === 200) { return { success: true } } });
}

export const postDocumentTemplate = async (data) => {
	return await noMonitor.post('/templates/documents/post-template', data);
}

export const getFeedbackSchema = async () => {
	return await noMonitor.get('/feedback/get-feedback-schema').then(res => { return res.data.data })
}

export const getFeedback = async (userId) => {
	return await noMonitor.get(`/feedback/get-feedback/${userId}`).then(res => { return res.data.data })
}

export const postFeedback = async (data) => {
	return await noMonitor.post('/feedback/post-feedback', data);
}

export const generateAIFunction = async (data) => {
	// Check if this is a data-specific function generation request
	const endpoint = data.dataId ? '/ai/generate-data-function' : '/ai/generate-function';
	// Support both description (structured) and textContent (natural language)
	const payload = {
		...data,
		// If textContent is provided, use it; otherwise use description
		textContent: data.textContent || undefined,
		description: data.textContent ? undefined : data.description
	};
	return await noMonitor.post(endpoint, payload, { timeout: 120000 }).then(res => { return res.data })
}

// Specific function for generating data utilities
export const generateDataUtility = async (data) => {
	return await noMonitor.post('/ai/generate-data-function', data, { timeout: 120000 }).then(res => { return res.data })
}

export const getProductServiceList = async () => {
	return await noMonitor.get('/data/product-and-service').then(res => { return res.data })
}

export const getAllDataItems = async () => {
	return await noMonitor.get('/data/all-items').then(res => { return res.data })
}

export const searchRegEx = async (text) => {
	return await noMonitor.get('/user/searchall?q=' + text).then(res => { return res.data })
}

export const getIntegrationApiAction = async (action) => {
	return await noMonitor.post(`${action}`, { redirectUrl: Linking.createURL('/integrations'), refreshUrl: Linking.createURL('/integrations') })
}

export const createStorePaymentIntent = async () => {
	return await noMonitor.post('/stripe/connect/create-payment-intent').then((res) => { console.log(res); return res })
}

export const getStripeId = async () => {
	return await noMonitor.post('/stripe/get-stripe-id').then(res => { return res.data.stripeId });
}

export const registerStripeLocation = async (data) => {
	return await noMonitor.post('/stripe/update-user-location', data)
}

export const registerStripeReader = async (data) => {
	return await noMonitor.post('/stripe/register-reader', data).then(res => { return res.data })
}

export const confirmPaymentIntent = async (data) => {
	return await noMonitor.post('/stripe/confirm-intent', data).then(res => { return res.data })
}

export const getAIForm = async (data) => {
	return await monitoredRequest.post('/ai/get-form', data, { timeout: 120000 }).then(res => { return res.data })
}

export const getFeed = async (distance, lat?: string, lon?: string, pageParam?: number) => {
	return await noMonitor.get(`/feed?distance=${distance}&lat=${lat}&lon=${lon}&page=${pageParam}`).then(res => { return res.data })
}

export const getExternalAds = async (count: number = 3, lat?: string, lon?: string, distance?: number) => {
	try {
		const response = await noMonitor.get(`/feed/external-ads?count=${count}&lat=${lat}&lon=${lon}&distance=${distance}`);

		// Handle different response structures
		const ads = response?.data?.data || response?.data || [];
		return Array.isArray(ads) ? ads : [];
	} catch (error) {
		console.error('Failed to fetch external ads:', error);
		return [];
	}
}

export const trackAdImpression = async (adId: string, adNetwork?: string, adCampaignId?: string, adCreativeId?: string) => {
	return await noMonitor.post('/feed/external-ads/track-impression', {
		adId,
		adNetwork,
		adCampaignId,
		adCreativeId
	}).then(res => { return res.data })
}

export const trackAdClick = async (adId: string, clickUrl: string, adNetwork?: string, adCampaignId?: string, adCreativeId?: string) => {
	return await noMonitor.post('/feed/external-ads/track-click', {
		adId,
		clickUrl,
		adNetwork,
		adCampaignId,
		adCreativeId
	}).then(res => { return res.data })
}

export const postToFeed = async (data) => {
	return await monitoredRequest.post('/feed/post', data).then(res => { return res.data })
}

export const deleteComment = async (commentId, feedId) => {
	return await noMonitor.post('/feed/remove-comment', { commentId: commentId, feedId: feedId })
}

export const addLikeToPost = async (postId) => {
	return await noMonitor.post(`/feed/like/${postId}`).then(res => { return res.data })
}

export const addShareToPost = async (postId) => {
	return await noMonitor.post(`/feed/share/${postId}`).then(res => { return res.data })
}

export const removeLikeFromPost = async (postId) => {
	return await noMonitor.post(`/feed/unlike/${postId}`).then(res => { return res.data })
}

export const removeShareFromPost = async (postId) => {
	return await monitoredRequest.post(`/feed/unshare/${postId}`).then(res => { return res.data })
}

export const addCommentToPost = async (postId, comment) => {
	return await noMonitor.post(`/feed/comment/${postId}`, comment).then(res => { return res.data })
}

export const addNestedComment = async (feedId, commentId, comment) => {
	return await noMonitor.post(`/feed/comment/add-comment/${feedId}`, { comment, commentId: commentId }).then(res => { return res.data })
}

export const getAICommentAnalysis = async (comment) => {
	return await noMonitor.get(`/ai/comment-analysis/${comment}`, { timeout: 120000 }).then(res => { return res.data })
}

export const getExtendedData = async (dataId, localUserId) => {
	return await noMonitor.post(`/data/extended/${dataId}`, { localUserId: localUserId }).then(res => { return res.data })
}

export const storeDeviceToken = async (token) => {
	await noMonitor.post('/user/store-device-token', token)
}

export const replaceSchema = async (schema) => {
	const { id } = schema;
	return await monitoredRequest.post(`/data/replace-schema/${id}`, schema)
}

export const getDataItemById = async (dataId) => {
	return await noMonitor.get(`/data/item/getItem/${dataId}`).then(res => { return res.data })
}

export const retrieveProduct = async (productId) => {
	return await noMonitor.get(`/stripe/product/${productId}`).then(res => { return res.data })
}

export const getPlanProducts = async () => {
	return await noMonitor.get('/stripe/plan-products').then(res => { 
		if (res.status !== 200) {
			throw new Error(res.data?.error || 'Failed to fetch plan products');
		}
		return res.data;
	}).catch(error => {
		console.error('Error fetching plan products:', error);
		throw error;
	});
}

export const getPlanSubscriptions = async () => {
	return await noMonitor.get('/stripe/plan-subscriptions').then(res => { 
		if (res.status !== 200) {
			throw new Error(res.data?.error || 'Failed to fetch plan subscriptions');
		}
		return res.data;
	}).catch(error => {
		console.error('Error fetching plan subscriptions:', error);
		throw error;
	});
}

export const createNewAppSubscription = async (receiptData?: any) => {
	return await noMonitor.post('/payments/app-subscription', { receiptData }).then(res => {
		if (res.status !== 200) {
			throw new Error(res.data?.error || 'Failed to create app subscription');
		}
		return res.data;
	}).catch(error => {
		console.error('Error creating app subscription:', error);
		throw error;
	});
}

export const getCustomerPortal = async (accountId) => {
	return await noMonitor.post(`/stripe/customer/portal`, accountId).then(res => { return res.data })
}

export const getCheckoutSession = async (productDetails: { productId: string }) => {
	return await noMonitor.post(`/stripe/checkouts/session`, productDetails).then(res => { return res.data })
}

export const createSubscriptionPaymentSheet = async (params: { priceId: string; productId?: string; billingId?: string }) => {
	return await noMonitor.post('/payments/subscription-payment-sheet', params).then(res => { return res.data.data }).catch(() => { throw new Error("No response") })
}

export const changeSubscription = async (params: { productId?: string; priceId?: string; prorationBehavior?: string }) => {
	return await noMonitor.post('/stripe/change-subscription', params).then(res => { return res.data }).catch(() => { throw new Error("No response") })
}

export const syncCalendars = async (calendars, deviceInfo) => {
	return await monitoredRequest.post('/calendar/sync-calendars', { calendars, deviceInfo })
}
export const syncCalendarEvents = async (events) => {
	return await monitorFail.post('/calendar/sync-events', events)
}

export const getCalendars = async () => {
	return await noMonitor.get('/calendar/get-calendars').then(res => res.data);
}

export const makeDataPublic = async (dataId: string) => {
	return await noMonitor.post(`/data/make-public/${dataId}`)
}
export const updateWorkPackage = async (id, workPackage) => {
	return await monitoredRequest.post(`/projects/update-workpackage/${id}`, workPackage)
}

export const getKnowledgeBase = async (linkedId, options: any = {}) => {
	const { page = 1, limit = 10, sortBy = 'createdAt', sortOrder = 'desc' } = options;
	const params = new URLSearchParams({
		page: page.toString(),
		limit: limit.toString(),
		sortBy,
		sortOrder
	});

	console.log('📤 [API-getKnowledgeBase] Requesting knowledge with:', {
		linkedId,
		params: params.toString(),
		url: `/knowledge?${params.toString()}`
	});

	const response = await noMonitor.get(`/knowledge?${params.toString()}`);

	console.log('✅ [API-getKnowledgeBase] Response received:', {
		status: response.status,
		dataKeys: Object.keys(response.data || {}),
		internal: response.data?.internal?.length || 0,
		external: response.data?.external?.length || 0
	});

	return response;
}

export const getInternalKnowledgePaginated = async (linkedId, options: any = {}) => {
	const { page = 1, limit = 10, sortBy = 'createdAt', sortOrder = 'desc' } = options;
	const params = new URLSearchParams({
		page: page.toString(),
		limit: limit.toString(),
		sortBy,
		sortOrder
	});

	return await noMonitor.get(`/knowledge/get-internal/?${params.toString()}`)
}

export const genEmail = async (data) => {
	return await noMonitor.post(`/ai/generate-email`, { data: data }, { timeout: 120000 })
}

export const genKnowledge = async (data: { prompt?: string, url?: string }) => {
	return await noMonitor.post(`/ai/generate-knowledge`, data, { timeout: 120000 })
}

export const getContributionChart = async (reportId) => {
	return await noMonitor.get(`/reports/contribution-chart/${reportId}`).then(res => res.data)
}

export const getAIReportQuery = async (reportId, query) => {
	return await noMonitor.post(`/ai/report-query/${reportId}`, { query: query }, { timeout: 120000 }).then(res => res.data)
}

export const googleAuthCallback = async (code) => {
	return await monitoredRequest.post(`/authorisations/oauth2callback`, { code: code })
}

export const getAddRoomSchema = async () => {
	return await noMonitor.get('/messaging/get-room-schema').then(res => res.data.schema)
}

export const buildLink = async (data: {
	targetUser: string,
	sourceUser: string,
	linkType: string,
	direction: string
}) => {
	return await monitoredRequest.post('/company/build-link', { targetUser: data.targetUser, sourceUser: data.sourceUser, linkType: data.linkType, direction: data.direction })
}

export const getSalesStats = () => {
	return noMonitor.get('/sales/get-stats')
}

export const sendInvoice = (invoice: any, mail: { message: string, subject: string }, target: any[] | string) => {
	//Attach invoice to email and send to target

}

export const getInventoryData = async (dataId) => {
	return await noMonitor.get(`/data/inventory/${dataId}`).then(res => res.data)
}

export const getCrmCount = async () => {
	return await noMonitor.get('/sales/get-crm-count').then(res => res.data)
}

export const getActiveCampaigns = async () => {
	console.log('getting campaigns ')
	return await monitorFail.get('/sales/active-campaigns').then(res => res.data)
}

export const postNewCampaign = async (campaign, id) => {
	return await noMonitor.post('/sales/create-campaign', { campaign: campaign, id }).then(res => res.data)
}

export const getDefaultEmailTemplates = async () => {
	return await noMonitor.get('/templates/get-default-email-templates').then(res => res.data)
}

export const postInternalKnowledge = async (values, onProgress?: (data: any) => void) => {
	return new Promise((resolve, reject) => {
		// Show initial loading modal
		dispatchSSEProgress({
			status: 'starting',
			message: 'Creating knowledge article...',
			progress: 0
		});

		// Use fetch with JSON payload
		fetch(`${USER_API_BASE_URL}/knowledge/add`, {
			method: 'POST',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
				'Accept': 'text/event-stream'
			},
			body: JSON.stringify(values)
		}).then(response => {
			console.log('Knowledge response received:', response.status, response.headers.get('content-type'));

			if (!response.ok) {
				dispatchSSEProgress({
					status: 'error',
					message: `HTTP error! status: ${response.status}`,
					progress: 0
				});
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			// Check if response is actually SSE
			const contentType = response.headers.get('content-type');
			if (!contentType || !contentType.includes('text/event-stream')) {
				console.warn('Response is not SSE format, content-type:', contentType);
				// Try to read as regular response
				return response.text().then(text => {
					try {
						const data = JSON.parse(text);
						resolve(data._id || data.id || data);
					} catch (e) {
						reject(new Error('Invalid response format'));
					}
				});
			}

			// React Native doesn't support response.body.getReader(), so fallback to text()
			const reader = response.body?.getReader?.();
			if (!reader || Platform.OS !== 'web') {
				// For React Native, read entire response as text and process SSE manually
				return response.text().then(text => {
					function processLine(line) {
						line = line.trim();
						if (line.startsWith('data: ')) {
							try {
								const jsonData = line.substring(6).trim();
								if (jsonData) {
									const data = JSON.parse(jsonData);
									console.log('Knowledge SSE data received:', data);

									// Dispatch to AuthModal
									dispatchSSEProgress(data);

									// Call progress callback if provided
									if (onProgress) {
										onProgress(data);
									}

									// Handle different status types
									switch (data.status) {
										case 'starting':
										case 'processing':
										case 'saving':
										case 'progress':
											console.log(`Knowledge creation ${data.status}: ${data.message}`);
											break;
										case 'completed':
											console.log('Knowledge creation completed successfully');
											resolve(data.data?._id || data.data?.id || data._id || data.id || data.data || data);
											return;
										case 'error':
											console.error('Knowledge creation error:', data.message);
											reject(new Error(data.message || 'Knowledge creation failed'));
											return;
									}
								}
							} catch (parseError) {
								console.warn('Failed to parse Knowledge SSE data:', line, parseError);
							}
						}
					}

					// Process all lines from the text response
					const lines = text.split('\n');
					let foundCompleted = false;
					let foundError = false;
					
					for (const line of lines) {
						if (foundCompleted || foundError) break;
						const trimmedLine = line.trim();
						if (trimmedLine.startsWith('data: ')) {
							try {
								const jsonData = trimmedLine.substring(6).trim();
								if (jsonData) {
									const data = JSON.parse(jsonData);
									dispatchSSEProgress(data);
									if (onProgress) {
										onProgress(data);
									}
									if (data.status === 'completed') {
										resolve(data.data?._id || data.data?.id || data._id || data.id || data.data || data);
										foundCompleted = true;
									} else if (data.status === 'error') {
										reject(new Error(data.message || 'Knowledge creation failed'));
										foundError = true;
									}
								}
							} catch (parseError) {
								console.warn('Failed to parse Knowledge SSE data:', trimmedLine, parseError);
							}
						}
					}
					
					// If no completed/error status found, try to parse as regular JSON
					if (!foundCompleted && !foundError) {
						try {
							const data = JSON.parse(text);
							resolve(data._id || data.id || data);
						} catch (e) {
							reject(new Error('Invalid response format'));
						}
					}
				}).catch(error => {
					console.error('Knowledge fetch error:', error);
					dispatchSSEProgress({
						status: 'error',
						message: error.message || 'Knowledge creation failed',
						progress: 0
					});
					reject(error);
				});
			}

			let buffer = ''; // Buffer for incomplete chunks

			function readStream() {
				reader.read().then(({ done, value }) => {
					if (done) {
						console.log('Knowledge stream ended, buffer:', buffer);
						// Check if we have unprocessed data in buffer
						if (buffer.trim()) {
							processBuffer(buffer);
						}
						return;
					}

					try {
						const chunk = new TextDecoder().decode(value);
						buffer += chunk;

						// Process complete lines
						const lines = buffer.split('\n');
						// Keep the last incomplete line in buffer
						buffer = lines.pop() || '';

						lines.forEach(processLine);

						// Continue reading the stream
						readStream();

					} catch (streamError) {
						console.error('Knowledge stream reading error:', streamError);
						dispatchSSEProgress({
							status: 'error',
							message: streamError.message || 'Stream error occurred',
							progress: 0
						});
						reject(streamError);
					}
				}).catch(error => {
					console.error('Knowledge reader error:', error);
					dispatchSSEProgress({
						status: 'error',
						message: error.message || 'Stream reading failed',
						progress: 0
					});
					reject(error);
				});
			}

			function processLine(line) {
				line = line.trim();
				if (line.startsWith('data: ')) {
					try {
						const jsonData = line.substring(6).trim();
						if (jsonData) {
							const data = JSON.parse(jsonData);
							console.log('Knowledge SSE data received:', data);

							// Dispatch to AuthModal
							dispatchSSEProgress(data);

							// Call progress callback if provided
							if (onProgress) {
								onProgress(data);
							}

							// Handle different status types
							switch (data.status) {
								case 'starting':
								case 'processing':
								case 'saving':
								case 'progress':
									console.log(`Knowledge creation ${data.status}: ${data.message}`);
									break;
								case 'completed':
									console.log('Knowledge creation completed successfully');
									resolve(data.data?._id || data.data?.id || data._id || data.id || data.data || data); // Return the result
									return;
								case 'error':
									console.error('Knowledge creation error:', data.message);
									reject(new Error(data.message || 'Knowledge creation failed'));
									return;
							}
						}
					} catch (parseError) {
						console.warn('Failed to parse Knowledge SSE data:', line, parseError);
					}
				} else if (line === '' || line.startsWith(':')) {
					// Empty line or comment, ignore
				} else {
					console.log('Non-data Knowledge SSE line:', line);
				}
			}

			function processBuffer(bufferData) {
				bufferData.split('\n').forEach(processLine);
			}

			// Start reading the stream
			readStream();

		}).catch(error => {
			console.error('Knowledge fetch error:', error);
			dispatchSSEProgress({
				status: 'error',
				message: error.message || 'Knowledge creation failed',
				progress: 0
			});
			reject(error);
		});
	});
}

export const postExternalKnowledge = async (values, onProgress?: (data: any) => void) => {
	return new Promise((resolve, reject) => {
		// Show initial loading modal
		dispatchSSEProgress({
			status: 'starting',
			message: 'Creating external knowledge article...',
			progress: 0
		});

		// Use fetch with JSON payload
		fetch(`${USER_API_BASE_URL}/knowledge/add`, {
			method: 'POST',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
				'Accept': 'text/event-stream'
			},
			body: JSON.stringify(values)
		}).then(response => {
			console.log('External Knowledge response received:', response.status, response.headers.get('content-type'));

			if (!response.ok) {
				dispatchSSEProgress({
					status: 'error',
					message: `HTTP error! status: ${response.status}`,
					progress: 0
				});
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			// Check if response is actually SSE
			const contentType = response.headers.get('content-type');
			if (!contentType || !contentType.includes('text/event-stream')) {
				console.warn('Response is not SSE format, content-type:', contentType);
				// Try to read as regular response
				return response.text().then(text => {
					try {
						const data = JSON.parse(text);
						resolve(data._id || data.id || data);
					} catch (e) {
						reject(new Error('Invalid response format'));
					}
				});
			}

			// React Native doesn't support response.body.getReader(), so fallback to text()
			const reader = response.body?.getReader?.();
			if (!reader || Platform.OS !== 'web') {
				// For React Native, read entire response as text and process SSE manually
				return response.text().then(text => {
					// Process all lines from the text response
					const lines = text.split('\n');
					let foundCompleted = false;
					let foundError = false;
					
					for (const line of lines) {
						if (foundCompleted || foundError) break;
						const trimmedLine = line.trim();
						if (trimmedLine.startsWith('data: ')) {
							try {
								const jsonData = trimmedLine.substring(6).trim();
								if (jsonData) {
									const data = JSON.parse(jsonData);
									console.log('External Knowledge SSE data received:', data);
									dispatchSSEProgress(data);
									if (onProgress) {
										onProgress(data);
									}
									if (data.status === 'completed') {
										resolve(data.data?._id || data.data?.id || data._id || data.id || data.data || data);
										foundCompleted = true;
									} else if (data.status === 'error') {
										reject(new Error(data.message || 'External Knowledge creation failed'));
										foundError = true;
									}
								}
							} catch (parseError) {
								console.warn('Failed to parse External Knowledge SSE data:', trimmedLine, parseError);
							}
						}
					}
					
					// If no completed/error status found, try to parse as regular JSON
					if (!foundCompleted && !foundError) {
						try {
							const data = JSON.parse(text);
							resolve(data._id || data.id || data);
						} catch (e) {
							reject(new Error('Invalid response format'));
						}
					}
				}).catch(error => {
					console.error('External Knowledge fetch error:', error);
					dispatchSSEProgress({
						status: 'error',
						message: error.message || 'External Knowledge creation failed',
						progress: 0
					});
					reject(error);
				});
			}

			let buffer = ''; // Buffer for incomplete chunks

			function readStream() {
				reader.read().then(({ done, value }) => {
					if (done) {
						console.log('External Knowledge stream ended, buffer:', buffer);
						// Check if we have unprocessed data in buffer
						if (buffer.trim()) {
							processBuffer(buffer);
						}
						return;
					}

					try {
						const chunk = new TextDecoder().decode(value);
						buffer += chunk;

						// Process complete lines
						const lines = buffer.split('\n');
						// Keep the last incomplete line in buffer
						buffer = lines.pop() || '';

						lines.forEach(processLine);

						// Continue reading the stream
						readStream();

					} catch (streamError) {
						console.error('External Knowledge stream reading error:', streamError);
						dispatchSSEProgress({
							status: 'error',
							message: streamError.message || 'Stream error occurred',
							progress: 0
						});
						reject(streamError);
					}
				}).catch(error => {
					console.error('External Knowledge reader error:', error);
					dispatchSSEProgress({
						status: 'error',
						message: error.message || 'Stream reading failed',
						progress: 0
					});
					reject(error);
				});
			}

			function processLine(line) {
				line = line.trim();
				if (line.startsWith('data: ')) {
					try {
						const jsonData = line.substring(6).trim();
						if (jsonData) {
							const data = JSON.parse(jsonData);
							console.log('External Knowledge SSE data received:', data);

							// Dispatch to AuthModal
							dispatchSSEProgress(data);

							// Call progress callback if provided
							if (onProgress) {
								onProgress(data);
							}

							// Handle different status types
							switch (data.status) {
								case 'starting':
								case 'processing':
								case 'saving':
								case 'validating':
								case 'progress':
									console.log(`External Knowledge creation ${data.status}: ${data.message}`);
									break;
								case 'completed':
									console.log('External Knowledge creation completed successfully');
									resolve(data.data?._id || data.data?.id || data._id || data.id || data.data || data); // Return the result
									return;
								case 'error':
									console.error('External Knowledge creation error:', data.message);
									reject(new Error(data.message || 'External Knowledge creation failed'));
									return;
							}
						}
					} catch (parseError) {
						console.warn('Failed to parse External Knowledge SSE data:', line, parseError);
					}
				} else if (line === '' || line.startsWith(':')) {
					// Empty line or comment, ignore
				} else {
					console.log('Non-data External Knowledge SSE line:', line);
				}
			}

			function processBuffer(bufferData) {
				bufferData.split('\n').forEach(processLine);
			}

			// Start reading the stream
			readStream();

		}).catch(error => {
			console.error('External Knowledge fetch error:', error);
			dispatchSSEProgress({
				status: 'error',
				message: error.message || 'External Knowledge creation failed',
				progress: 0
			});
			reject(error);
		});
	});
}

export const getInternalKnowledge = async () => {
	return await noMonitor.get('/knowledge/get-internal')
}

export const deleteKnowledgeArticle = async (id) => {
	return await noMonitor.delete(`/knowledge/delete/${id}`)
}

export const saveKnowledgeContent = async (id: string, content: string) => {
	await monitoredRequest.post(`/knowledge/update/${id}`, { knowledge: content })
}

export const publishKnowledgeArticle = async (id: string, republish?: boolean) => {
	await monitoredRequest.post(`/knowledge/publish/${id}`, {
		republish: republish || false
	})
}

export const getKnowledgeContent = async (id) => {
	return await noMonitor.get(`/knowledge/get/${id}`).then(res => res.data)
}

export const getTeam = async (companyId) => {
	return await apisObject.getAllUsers(companyId).then(res => res.users)
}

export const getCRMFolders = async () => {
	return await noMonitor.get('/sales/get-crm-folders').then(res => res.data)
}

export const getTemplates = async () => {
	return await noMonitor.get('/templates/get-templates').then(res => res.data)
}

export const getTemplateById = async (type, id) => {
	return await noMonitor.get(`/templates/get-template/${type}/${id}`).then(res => res.data)
}

export const updateWorkPackageDue = async (wpId: string, projectId: string, date: Object) => {
	return await noMonitor.post(`/projects/${projectId}/change-task-due-date`, { wpId: wpId, date })
}

export const getCampaign = async (campaignId: string, page: number = 1, limit: number = 50, stageIndex?: number) => {
	return await noMonitor.post(`/sales/get-campaign`, {
		campaignId,
		page,
		limit,
		stageIndex
	}).then(res => res.data)
}

// Sales Funnel API Functions
export const moveLeadToStage = async (leadId: string, fromStage: string, toStage: string, campaignId: string) => {
	return await monitoredRequest.post(`/sales/funnel/move-lead`, {
		leadId,
		fromStage,
		toStage,
		campaignId
	}).then(res => res.data);
};

export const updateLeadData = async (leadId: string, leadData: any, campaignId: string) => {
	return await monitoredRequest.post(`/sales/funnel/update-lead`, {
		leadId,
		leadData,
		campaignId
	}).then(res => res.data);
};

export const deleteLeadFromCampaign = async (leadId: string, campaignId: string) => {
	return await monitoredRequest.delete(`/sales/funnel/remove-lead`, {
		data: { leadId, campaignId }
	}).then(res => res.data);
};

export const addLeadToCampaign = async (leadData: any, campaignId: string, stageId?: string) => {
	return await monitoredRequest.post(`/sales/funnel/add-lead`, {
		leadData,
		campaignId,
		stageId
	}).then(res => res.data);
};

export const updateCampaignStage = async (stageId: string, stageData: any, campaignId: string) => {
	return await monitoredRequest.post(`/sales/funnel/update-stage`, {
		stageId,
		stageData,
		campaignId
	}).then(res => res.data);
};

export const addCampaignStage = async (stageData: any, campaignId: string) => {
	return await monitoredRequest.post(`/sales/funnel/add-stage`, {
		stageData,
		campaignId
	}).then(res => res.data);
};

export const deleteCampaignStage = async (stageId: string, campaignId: string) => {
	return await monitoredRequest.delete(`/sales/funnel/remove-stage`, {
		data: { stageId, campaignId }
	}).then(res => res.data);
};

export const getCampaignAnalytics = async (campaignId: string, dateRange?: { start: string, end: string }) => {
	return await noMonitor.post(`/sales/funnel/analytics`, {
		campaignId,
		dateRange
	}).then(res => res.data);
};

export const getLeadHistory = async (leadId: string, campaignId: string) => {
	return await noMonitor.post(`/sales/funnel/lead-history`, {
		leadId,
		campaignId
	}).then(res => res.data);
};

export const bulkMoveLeads = async (leadIds: string[], fromStage: string, toStage: string, campaignId: string) => {
	return await monitoredRequest.post(`/sales/funnel/bulk-move-leads`, {
		leadIds,
		fromStage,
		toStage,
		campaignId
	}).then(res => res.data);
};

export const exportCampaignData = async (campaignId: string, format: 'csv' | 'excel' | 'json' = 'csv') => {
	return await noMonitor.post(`/sales/funnel/export`, {
		campaignId,
		format
	}).then(res => res.data);
};

export const importLeadsToCampaign = async (campaignId: string, leadsData: any[], stageId?: string) => {
	return await monitoredRequest.post(`/sales/funnel/import-leads`, {
		campaignId,
		leadsData,
		stageId
	}).then(res => res.data);
};

export const updateCampaignSettings = async (campaignId: string, settings: any) => {
	return await monitoredRequest.post(`/sales/funnel/update-settings`, {
		campaignId,
		settings
	}).then(res => res.data);
};

export const searchDataRegEx = async (dataId, query: string, keys) => {
	return await noMonitor.post('/data/search-folder', { dataId, query: query, keys })
}

export const handleChangeTaskDescription = async (id, description) => {
	return await noMonitor.post(`/projects/change-task-description`, { id, description })
}

export const handleChangeTaskTitle = async (id, title) => {
	return await noMonitor.post(`/projects/change-task-title`, { id, title })
}

export const changeTaskPosition = async (id, status) => {
	return await monitorFail.post(`/projects/change-task-position`, { id, status })
}

export const modifyTask = async (id, task) => {
	const { dateRange, ...rest } = task;
	rest.start_date = dateRange?.startDate;
	rest.end_date = dateRange?.endDate;
	const newTask = {
		...rest,
		dateRange: undefined
	}

	// Backend expects: { id: { _id: taskId, ...taskObject }, task: { dataId: projectId, extraId: workPackageId } }
	return await monitorFail.post(`/projects/modify-task`, { 
		id: { _id: id._id || id, ...newTask }, 
		task: { dataId: id.dataId, extraId: id.extraId } 
	})
}

// ===========================================
// MODERN PROJECT MANAGEMENT API FUNCTIONS
// ===========================================

// Kanban Board APIs
export const moveKanbanTask = async (projectId: string, taskData: { taskId: string; fromStatus: string; toStatus: string; position: number }) => {
	return await api.post(`/projects/${projectId}/kanban/move-task`, taskData)
}

export const bulkKanbanActions = async (projectId: string, actionData: { taskIds: string[]; action: string; newStatus?: string; assigneeId?: string }) => {
	return await api.post(`/projects/${projectId}/kanban/bulk-actions`, actionData)
}

export const getKanbanColumns = async (projectId: string) => {
	return await api.get(`/projects/${projectId}/kanban/columns`)
}

// Gantt Chart APIs
export const getGanttData = async (projectId: string) => {
	return await api.get(`/projects/${projectId}/gantt/data`)
}

export const updateGanttTimeline = async (projectId: string, timelineData: any) => {
	return await api.post(`/projects/${projectId}/gantt/update-timeline`, timelineData)
}

export const manageTaskDependencies = async (projectId: string, dependencyData: any) => {
	return await api.post(`/projects/${projectId}/gantt/dependencies`, dependencyData)
}

// Analytics APIs
export const getProjectAnalytics = async (projectId: string) => {
	return await api.get(`/projects/${projectId}/analytics`)
}

export const getProgressAnalytics = async (projectId: string) => {
	return await api.get(`/projects/${projectId}/analytics/progress`)
}

export const getTeamPerformanceAnalytics = async (projectId: string) => {
	return await api.get(`/projects/${projectId}/analytics/team-performance`)
}

export const getTimeTrackingAnalytics = async (projectId: string) => {
	return await api.get(`/projects/${projectId}/analytics/time-tracking`)
}

// Team Management APIs
export const addTeamMember = async (projectId: string, teamMembers: any[]) => {
	return await api.post('/projects/add-team-member', { projectId, teamMembers })
}

export const inviteTeamMember = async (projectId: string, inviteData: { email: string; role: string }) => {
	return await api.post(`/projects/${projectId}/team/invite`, inviteData)
}

export const updateTeamMemberRole = async (projectId: string, memberId: string, roleData: { role: string }) => {
	return await api.patch(`/projects/${projectId}/team/${memberId}/role`, roleData)
}

export const removeTeamMember = async (projectId: string, memberId: string) => {
	return await api.delete(`/projects/${projectId}/team/${memberId}`)
}

// File Management APIs  
export const uploadProjectFiles = async (projectId: string, fileData: {
	name: string;
	type: string;
	size?: number;
	fileData: string; // base64 encoded
}) => {
	return await monitoredRequest.post(`/projects/${projectId}/files/upload`, fileData).then(res => res.data)
}

export const getProjectFiles = async (projectId: string) => {
	return await monitoredRequest.get(`/projects/${projectId}/files`).then(res => {
		// Handle both response formats: { success: true, data: [...] } or direct array
		// The interceptor may flatten nested data, so check both locations
		if (Array.isArray(res.data)) {
			return { data: res.data };
		}
		return res.data;
	})
}

export const deleteProjectFile = async (projectId: string, documentId: string) => {
	return await monitoredRequest.delete(`/projects/${projectId}/files/${documentId}`).then(res => res.data)
}

export const downloadProjectFile = async (projectId: string, fileId: string) => {
	return await api.get(`/projects/${projectId}/files/${fileId}/download`, { responseType: 'blob' })
}

// Project Settings APIs
export const updateProjectSettings = async (projectId: string, settings: any) => {
	return await api.patch(`/projects/${projectId}/settings`, settings)
}

// Enhanced Task Management APIs
export const bulkCreateTasks = async (projectId: string, tasksData: any[]) => {
	return await api.post(`/projects/${projectId}/tasks/bulk-create`, { tasks: tasksData })
}

export const addTaskComment = async (projectId: string, taskId: string, commentData: { content: string }) => {
	return await api.post(`/projects/${projectId}/tasks/${taskId}/comments`, commentData)
}

export const getTaskComments = async (projectId: string, taskId: string) => {
	return await api.get(`/projects/${projectId}/tasks/${taskId}/comments`)
}

export const logTaskTime = async (projectId: string, taskId: string, timeData: { hours: number; description?: string }) => {
	return await api.post(`/projects/${projectId}/tasks/${taskId}/time-log`, timeData)
}

export const getDataTemplateCount = async () =>{
	return await noMonitor.get('/templates/count').then(res => res.data)
}

export const setEmailGlobal = async (id: string) => {
	return await monitorFail.post('/templates/make-email-global', { id: id })
}

export const getGlobalAssets = async (includeMetadata = true) => {
	const url = '/templates/get-media?metadata=true';
	return await monitorFail.get(url).then(res => {
		const data = res.data;
		// When includeMetadata is false, return just URL strings for GrapesJS Asset Manager
		if (!includeMetadata && Array.isArray(data)) {
			return data.map(asset => typeof asset === 'string' ? asset : asset.url).filter(Boolean);
		}
		// Otherwise return full metadata
		return data;
	})
}

export const sendDocumentEmail = async (documentId: string, recipientEmail: string, message = '', includeData = false) => {
	return await api.post('/data/send-document-email', {
		documentId,
		recipientEmail,
		message,
		includeData
	}).then(res => res.data);
};

export const getDocumentShareHistory = async (documentId: string) => {
	return await api.get(`/data/share-history/${documentId}`).then(res => res.data);
};

export const uploadGlobalAsset = async (asset) => {
	return await monitorFail.post('/templates/upload-media', { files: [asset] }).then(res => {
		const data = res.data;
		// Transform to array of URL strings for GrapesJS Asset Manager
		if (Array.isArray(data)) {
			return data.map(asset => typeof asset === 'string' ? asset : asset.url).filter(Boolean);
		} else if (typeof data === 'object' && data.url) {
			return [data.url];
		}
		return data;
	})
}

export const deletePost = async (id) => {
	return await monitoredRequest.delete(`/feed/${id}`)
}

export const getTargetAudience = async (id) => {
}

export const revokeGoogleScope = async (scope: string) => {
	return await monitorFail.post(`/google/revoke-access`, { revokeTarget: scope }).then(res => {
		console.log(res.data, 'from revoke');
		//Open authURL in new window
		window.location.href = res.data.authURL;
		
	})
}

export const authoriseInstagram = async (data) => {
	return await monitorFail.post(`/instagram/authorise`, data)
}

export const authCallback = async (code: string, integration: string, scopes: string[], verifier: any) => {
	return await monitoredRequest.post('/integrations/auth/callback', { scopes, code: code, integration: integration, codeVerifier: verifier }).then(res => res.data)
}

export const searchCompanies = async (query: string) => {
	return await monitorFail.get(`/companies/search/${query}`).then(res => res.data)
}

export const getUserRecents = async () => {
	return await noMonitor.get('/user/recents').then(res => res.data)
}

export const addRecent = async (id, dataType) => {
	return await noMonitor.post('/user/recent/add', { itemId: id, itemType: dataType })
}

export const getTrending = async () => {
	return await noMonitor.get('/trends/GB').then(res => res.data)
}

export const aiCommentAnalysis = async (comments, postContent) => {
	return await noMonitor.post('/ai/comment-analysis', { comments, postContent }, { timeout: 120000 })
}

export const getUserStats = async () => {
	// Backend getAllUserStatistics returns { allStats: count } which is just a number
	// We need categorizedData format, so we'll try to get stats without routeType filter
	// If backend doesn't support this, we may need to aggregate multiple routeTypes
	// For now, try omitting routeType - backend query may handle undefined differently
	try {
		// Try without routeType first - if backend supports it
		const res = await noMonitor.get('/user/get-statistics?filterType=all').then(res => res.data);
		// If response has categorizedData, return it
		if (res && res.categorizedData) {
			return res;
		}
	// Otherwise fallback to get-all-statistics (though it returns wrong format)
		return await noMonitor.get('/user/get-all-statistics').then(res => res.data);
	} catch (error) {
		console.error('Error in getUserStats:', error);
		// Fallback to get-all-statistics
		return await noMonitor.get('/user/get-all-statistics').then(res => res.data);
	}
}

export const getUserStatsData = async (routeType, startDate, endDate, filterType) => {
	// Properly encode query parameters
	const params = new URLSearchParams({
		routeType: routeType || 'user',
		startDate: startDate || '',
		endDate: endDate || '',
		filterType: filterType || 'month'
	});
	return await noMonitor.get(`/user/get-statistics?${params.toString()}`).then(res => res.data);
}

export const getAIUsageStats = async () => {
	return await noMonitor.get('/ai/usage-stats', { timeout: 120000 }).then(res => res.data);
}

export const getApiCallHistory = async (startDate?: string, endDate?: string, limit = 100, offset = 0) => {
	const params = new URLSearchParams();
	if (startDate) params.append('startDate', startDate);
	if (endDate) params.append('endDate', endDate);
	params.append('limit', limit.toString());
	params.append('offset', offset.toString());
	return await noMonitor.get(`/user/api-call-history?${params.toString()}`).then(res => res.data);
}

export const getApiCallsOverTime = async (startDate?: string, endDate?: string, interval = 'day', routeType?: string) => {
	const params = new URLSearchParams();
	if (startDate) params.append('startDate', startDate);
	if (endDate) params.append('endDate', endDate);
	params.append('interval', interval);
	if (routeType) params.append('routeType', routeType);
	return await noMonitor.get(`/user/api-calls-over-time?${params.toString()}`).then(res => res.data);
}

export const getConnectedIntegrationsCount = async () => {
	return await noMonitor.get('/user/connected-integrations-count').then(res => res.data);
}

export const getKnowledgeByViews = async () => {
	return await noMonitor.get('/knowledge/by-views').then(res => res.data)
}

export const cloneData = async (dataId, targetId) => {
	return await noMonitor.post(`/data/clone`, { dataId, targetId }).then(res => res.data)
}

export const transferData = async (dataId, targetId) => {
	return await noMonitor.post(`/data/transfer`, { dataId, targetId }).then(res => res.data)
}

export const cancelUserCreation = async (userId: string) => {
	return await noMonitor.post("/user/cancel-creation", { userId })
}

export const userLoginHistory = async () => {
	return await noMonitor.post("/user/login-history").then(res => res.data)
}

export const getCrmItems = async (id: string) => {
	return await noMonitor.post(`/sales/crm/individual`, { id }).then(res => res.data)
}

export const uploadDataImage = async () => {
	return await noMonitor.post(`/data/upload-image`).then(res => res.data)
}

export const getMainCharts = async (startDate, endDate) => {
	return await noMonitor.get(`/reports/combined-data?startDate=${startDate}&endDate=${endDate}`).then(res => res.data)
}

export const requestDataDownload = async (dataId: string) => {
	return await monitoredRequest.post(`/data/request-download`, { dataId })
		//Interpret pipe
		.then(res => {
			//Open download link
			console.log(res.data, 'from download');
		}).catch(e => console.error(e))
}

export const getStartupSchemas = async () => {
	return await noMonitor.get('/company/startup-schemas').then(res => { console.log(res.data); return res.data })
}

export const startCampaign = async (campaignId: string) => {
	return await monitoredRequest.post(`/campaigns/schedule/start-campaign`, { campaignId }).then(res => res.data)
}

export const changeDataTitle = async (dataId: string, title: string) => {
	return await noMonitor.post(`/data/change-title`, { dataId, title }).then(res => res.data)
}

export const createAffiliateCode = async () => {
	return await noMonitor.post('/affiliate/create-code').then(res => res.data)
}

export const getAffiliateHistory = async (code, dateRange) => {
	return await noMonitor.post('/user/affiliate/activity', { code, dateRange }).then(res => res.data)
}

export const deleteAccount = async () => {
	return await monitoredRequest.delete('/user/delete-user')
}

export const addDepartment = async (data) => {
	return await noMonitor.post('/company/add-department', data).then(res => res.data)
}

export const setCompanyName = async (companyId: string, companyName: string) => {
	return await noMonitor.post('/company/set-name', { companyId, company_name: companyName }).then(res => res.data)
}

export const addCompanyLocation = async (companyId: string, location: string) => {
	return await noMonitor.patch('/company/add-location', { companyId, location }).then(res => res.data)
}

export const resetCompanyPositions = async (companyId: string) => {
	return await noMonitor.post('/company/reset-positions', { companyId }).then(res => res.data)
}

export const addUserToDepartment = async (data: any, departmentId: string) => {
	return await noMonitor.post('/company/assign-department', {data, departmentId}).then(res => res.data)
}

export const addDataDescription = async (dataId: string, description: string) => {
	return await noMonitor.post(`/data/add-description`, { dataId, description }).then(res => res.data)
}

export const updateCalendarSettings = async (data: any, calendarId: string) => {
	return await noMonitor.post('/calendar/update-settings', { data, calendarId })
}

export const postDataNote = async (dataId: any, message: any) => {
	return await noMonitor.post('/data/add-notes', { dataId, message })
}

export const updateDataActions = async (dataId: any, actions: any) => {
	if(!dataId){
		throw new Error('Data ID is required');
	}
	return await monitoredRequest.post(`/data/update-actions/${dataId}`, { actions })
}

// Workflow Management API functions
export const getWorkflows = async (params?: { active?: boolean; category?: string; tags?: string }) => {
	const queryParams = new URLSearchParams();
	if (params?.active !== undefined) queryParams.append('active', String(params.active));
	if (params?.category) queryParams.append('category', params.category);
	if (params?.tags) queryParams.append('tags', params.tags);
	const query = queryParams.toString();
	return await api.get(`/workflows${query ? `?${query}` : ''}`).then(res => res.data);
}

export const getWorkflow = async (workflowId: string) => {
	return await api.get(`/workflows/${workflowId}`).then(res => res.data);
}

export const createWorkflow = async (workflowData: any) => {
	return await api.post('/workflows', workflowData).then(res => res.data);
}

export const updateWorkflow = async (workflowId: string, workflowData: any) => {
	return await api.put(`/workflows/${workflowId}`, workflowData).then(res => res.data);
}

export const deleteWorkflow = async (workflowId: string) => {
	return await api.delete(`/workflows/${workflowId}`).then(res => res.data);
}

export const executeWorkflow = async (workflowId: string, triggerData?: any) => {
	return await api.post(`/workflows/${workflowId}/execute`, { triggerData }).then(res => res.data);
}

export const getWorkflowExecutions = async (workflowId: string, params?: { limit?: number; status?: string }) => {
	const queryParams = new URLSearchParams();
	if (params?.limit) queryParams.append('limit', String(params.limit));
	if (params?.status) queryParams.append('status', params.status);
	const query = queryParams.toString();
	return await api.get(`/workflows/${workflowId}/executions${query ? `?${query}` : ''}`).then(res => res.data);
}

export const retryWorkflowExecution = async (workflowId: string, executionId: string) => {
	return await api.post(`/workflows/${workflowId}/retry/${executionId}`).then(res => res.data);
}

export const scheduleWorkflow = async (workflowId: string, cronExpression: string, timezone?: string) => {
	return await api.post(`/workflows/${workflowId}/schedule`, { cronExpression, timezone }).then(res => res.data);
}

export const unscheduleWorkflow = async (workflowId: string) => {
	return await api.delete(`/workflows/${workflowId}/schedule`).then(res => res.data);
}

export const getWorkflowWebhooks = async (workflowId: string) => {
	return await api.get(`/workflows/${workflowId}/webhooks`).then(res => res.data);
}

// Agentic Flow API functions
export const createAgenticFlow = async (dataId: string, flowConfig: any) => {
	return await api.post('/agentic-flow/create', { dataId, flowConfig }).then(res => res.data);
}

export const getAgenticFlows = async (dataId: string) => {
	return await api.get(`/agentic-flow/${dataId}`).then(res => res.data);
}

export const getAgenticFlow = async (dataId: string, flowId: string) => {
	return await api.get(`/agentic-flow/${dataId}/${flowId}`).then(res => res.data);
}

export const updateAgenticFlow = async (dataId: string, flowId: string, flowConfig: any) => {
	return await api.put(`/agentic-flow/${dataId}/${flowId}`, { flowConfig }).then(res => res.data);
}

export const deleteAgenticFlow = async (dataId: string, flowId: string) => {
	return await api.delete(`/agentic-flow/${dataId}/${flowId}`).then(res => res.data);
}

export const triggerAgenticFlow = async (dataId: string, flowId: string, inputData?: any, context?: any) => {
	return await api.post(`/agentic-flow/${dataId}/${flowId}/trigger`, { inputData, context }).then(res => res.data);
}

export const executeAgenticFlow = async (flowId: string, inputData?: any, context?: any) => {
	return await api.post('/agentic-flow/execute', { flowId, inputData, context }).then(res => res.data);
}

export const validateAgenticFlow = async (flowConfig: any) => {
	return await api.post('/agentic-flow/validate', { flowConfig }).then(res => res.data);
}

export const getAgenticFlowStatus = async (executionId: string) => {
	return await api.get(`/agentic-flow/status/${executionId}`).then(res => res.data);
}

// Flow History API functions
export const getFlowHistory = async (dataId: string, params?: { limit?: number; offset?: number; status?: string; actionType?: string; triggerType?: string; startDate?: string; endDate?: string }) => {
	const queryParams = new URLSearchParams();
	if (params?.limit) queryParams.append('limit', String(params.limit));
	if (params?.offset) queryParams.append('offset', String(params.offset));
	if (params?.status) queryParams.append('status', params.status);
	if (params?.actionType) queryParams.append('actionType', params.actionType);
	if (params?.triggerType) queryParams.append('triggerType', params.triggerType);
	if (params?.startDate) queryParams.append('startDate', params.startDate);
	if (params?.endDate) queryParams.append('endDate', params.endDate);
	const query = queryParams.toString();
	return await api.get(`/flow-history/${dataId}${query ? `?${query}` : ''}`).then(res => res.data);
}

export const getActionHistory = async (actionId: string, params?: { limit?: number; offset?: number; status?: string; startDate?: string; endDate?: string }) => {
	const queryParams = new URLSearchParams();
	if (params?.limit) queryParams.append('limit', String(params.limit));
	if (params?.offset) queryParams.append('offset', String(params.offset));
	if (params?.status) queryParams.append('status', params.status);
	if (params?.startDate) queryParams.append('startDate', params.startDate);
	if (params?.endDate) queryParams.append('endDate', params.endDate);
	const query = queryParams.toString();
	return await api.get(`/flow-history/action/${actionId}${query ? `?${query}` : ''}`).then(res => res.data);
}

export const getWorkflowHistory = async (workflowId: string, params?: { limit?: number; offset?: number; status?: string; startDate?: string; endDate?: string }) => {
	const queryParams = new URLSearchParams();
	if (params?.limit) queryParams.append('limit', String(params.limit));
	if (params?.offset) queryParams.append('offset', String(params.offset));
	if (params?.status) queryParams.append('status', params.status);
	if (params?.startDate) queryParams.append('startDate', params.startDate);
	if (params?.endDate) queryParams.append('endDate', params.endDate);
	const query = queryParams.toString();
	return await api.get(`/flow-history/workflow/${workflowId}${query ? `?${query}` : ''}`).then(res => res.data);
}

export const getFlowAnalytics = async (dataId: string, params?: { startDate?: string; endDate?: string }) => {
	const queryParams = new URLSearchParams();
	if (params?.startDate) queryParams.append('startDate', params.startDate);
	if (params?.endDate) queryParams.append('endDate', params.endDate);
	const query = queryParams.toString();
	return await api.get(`/flow-history/analytics/${dataId}${query ? `?${query}` : ''}`).then(res => res.data);
}

export const getExecutionDetails = async (executionId: string) => {
	return await api.get(`/flow-history/execution/${executionId}`).then(res => res.data);
}

export const filterExecutions = async (filters: { dataId?: string; actionId?: string; workflowId?: string; status?: string; actionType?: string; triggerType?: string; startDate?: string; endDate?: string; limit?: number; offset?: number }) => {
	return await api.post('/flow-history/filter', filters).then(res => res.data);
}

// Workflow AI API functions
export const generateWorkflowFromNL = async (description: string, context?: any, availableIntegrations?: string[]) => {
	return await api.post('/workflows/ai/generate', { description, context, availableIntegrations }).then(res => res.data);
}

export const optimizeWorkflow = async (workflow: any) => {
	return await api.post('/workflows/ai/optimize', { workflow }).then(res => res.data);
}

export const deleteDataNote = async (data: any) => {
}

export const getDataNotes = async (dataId: any) => {

}

export const removeIntegration = async (integration: string) => {
	await monitoredRequest.post(`/user/integrations/remove`, { integration: integration }).then(() => {
		//Remove integration from user object in store
		dispatch({ type: REMOVE_INTEGRATION, payload: integration })
	})
}

export const retrieveCompanyUserStats = async (companyId: any) => {
	return await noMonitor.post(`/company/user-stats`, companyId).then(res => res.data)
}

// HR Management API Methods
export const getHRDashboard = async () => {
	return await noMonitor.get('/company/hr/dashboard').then(res => res.data)
}

export const getEmployees = async (params?: { departmentId?: string; status?: string }) => {
	const queryParams = new URLSearchParams();
	if (params?.departmentId) queryParams.append('departmentId', params.departmentId);
	if (params?.status) queryParams.append('status', params.status);
	const queryString = queryParams.toString();
	return await noMonitor.get(`/company/hr/employees${queryString ? `?${queryString}` : ''}`).then(res => res.data)
}

export const updateEmployeeHRData = async (employeeId: string, hrData: any) => {
	return await noMonitor.post('/company/hr/employees', { employeeId, hrData }).then(res => res.data)
}

export const getLeaveRequests = async (params?: { status?: string; employeeId?: string }) => {
	const queryParams = new URLSearchParams();
	if (params?.status) queryParams.append('status', params.status);
	if (params?.employeeId) queryParams.append('employeeId', params.employeeId);
	const queryString = queryParams.toString();
	return await noMonitor.get(`/company/hr/leave-requests${queryString ? `?${queryString}` : ''}`).then(res => res.data)
}

export const createLeaveRequest = async (data: { employeeId: string; startDate: string; endDate: string; leaveType: string; reason?: string }) => {
	return await noMonitor.post('/company/hr/leave-requests', data).then(res => res.data)
}

export const updateLeaveRequest = async (requestId: string, data: { status: string; approvedBy?: string }) => {
	return await noMonitor.put(`/company/hr/leave-requests/${requestId}`, data).then(res => res.data)
}

export const getAttendance = async (params?: { startDate?: string; endDate?: string; employeeId?: string }) => {
	const queryParams = new URLSearchParams();
	if (params?.startDate) queryParams.append('startDate', params.startDate);
	if (params?.endDate) queryParams.append('endDate', params.endDate);
	if (params?.employeeId) queryParams.append('employeeId', params.employeeId);
	const queryString = queryParams.toString();
	return await noMonitor.get(`/company/hr/attendance${queryString ? `?${queryString}` : ''}`).then(res => res.data)
}

export const recordAttendance = async (data: { employeeId: string; date: string; checkIn?: string; checkOut?: string; status?: string }) => {
	return await noMonitor.post('/company/hr/attendance', data).then(res => res.data)
}

export const getPayroll = async (params?: { month?: string; year?: string; employeeId?: string }) => {
	const queryParams = new URLSearchParams();
	if (params?.month) queryParams.append('month', params.month);
	if (params?.year) queryParams.append('year', params.year);
	if (params?.employeeId) queryParams.append('employeeId', params.employeeId);
	const queryString = queryParams.toString();
	return await noMonitor.get(`/company/hr/payroll${queryString ? `?${queryString}` : ''}`).then(res => res.data)
}

export const createPayroll = async (data: { employeeId: string; payPeriod: string; baseSalary?: number; deductions?: any[]; bonuses?: any[]; netPay: number }) => {
	return await noMonitor.post('/company/hr/payroll', data).then(res => res.data)
}

// Accounting Software Integration APIs
export const syncPayrollToAccounting = async (payrollId: string, accountingSoftware: 'xero' | 'sage', options?: { createInvoice?: boolean; syncContacts?: boolean }) => {
	return await noMonitor.post(`/company/hr/payroll/${payrollId}/sync-accounting`, {
		accountingSoftware,
		...options
	}).then(res => res.data)
}

export const getAccountingIntegrations = async () => {
	return await noMonitor.get('/company/hr/accounting-integrations').then(res => res.data)
}

export const connectAccountingSoftware = async (software: 'xero' | 'sage', authCode: string) => {
	return await noMonitor.post('/company/hr/accounting-integrations/connect', {
		software,
		authCode
	}).then(res => res.data)
}

export const disconnectAccountingSoftware = async (software: 'xero' | 'sage') => {
	return await noMonitor.delete(`/company/hr/accounting-integrations/${software}`).then(res => res.data)
}

export const getPerformanceReviews = async (params?: { employeeId?: string; status?: string }) => {
	const queryParams = new URLSearchParams();
	if (params?.employeeId) queryParams.append('employeeId', params.employeeId);
	if (params?.status) queryParams.append('status', params.status);
	const queryString = queryParams.toString();
	return await noMonitor.get(`/company/hr/performance-reviews${queryString ? `?${queryString}` : ''}`).then(res => res.data)
}

export const createPerformanceReview = async (data: { employeeId: string; reviewPeriod: string; goals?: any[]; achievements?: any[]; areasForImprovement?: any[]; rating: number; reviewerId?: string }) => {
	return await noMonitor.post('/company/hr/performance-reviews', data).then(res => res.data)
}

export const getStatCounts = async () => {
	return await noMonitor.get(`/user/stat-counts`, {}).then(res => res.data)
}
export const getMeetings = async () => {
	return await noMonitor.post(`/user/meetings`).then(res => res.data)
}

export const getRecentMeetings = async (limit = 10, page = 1) => {
	return await noMonitor.get(`/user/recent-meetings?limit=${limit}&page=${page}`).then(res => res.data)
}

export const getMeetingSchema = async () => {
	return await noMonitor.get('/calendar/get-meeting-schema').then(res => res.data)
}

export const transcribeMeeting = async (audioFile: any, meetingDetails: any) => {
	const formData = new FormData();

	// Add audio file to form data
	if (audioFile) {
		// Handle different file formats
		if (typeof audioFile === 'string') {
			// If it's a URI, we need to fetch it first
			const response = await fetch(audioFile);
			const blob = await response.blob();
			formData.append('audio', blob, 'recording.m4a');
		} else if (audioFile instanceof Blob || audioFile instanceof File) {
			formData.append('audio', audioFile, audioFile.name || 'recording.m4a');
		} else {
			// For React Native, audioFile might be an object with uri
			formData.append('audio', {
				uri: audioFile.file || audioFile.uri,
				type: 'audio/m4a',
				name: 'recording.m4a'
			} as any);
		}
	}

	// Add meeting details (title, participants, location, etc.)
	if (meetingDetails) {
		formData.append('title', meetingDetails.Title || meetingDetails['meeting-title'] || 'Untitled Meeting');

		if (meetingDetails.Participants || meetingDetails['meeting-participants']) {
			formData.append('participants', JSON.stringify(meetingDetails.Participants || meetingDetails['meeting-participants']));
		}

		if (meetingDetails.Location || meetingDetails['meeting-location']) {
			formData.append('location', meetingDetails.Location || meetingDetails['meeting-location']);
		}

		// Add timing information
		if (meetingDetails.startTime) {
			formData.append('startTime', meetingDetails.startTime.toString());
		}
		if (meetingDetails.endTime) {
			formData.append('endTime', meetingDetails.endTime.toString());
		}
	}

	return await monitoredRequest.post('/ai/transcribe-meeting', formData, {
		headers: {
			'Content-Type': 'multipart/form-data'
		},
		timeout: 120000 // 120 second timeout for transcription
	}).then(res => {
		console.log('✅ Transcription completed:', res.data);
		return res.data;
	}).catch(error => {
		console.error('❌ Transcription failed:', error);
		throw error;
	});
}

export const modifyFolder = async (dataId, action) => {
return await monitorFail.post(`/data/modify-folder`, { dataId, action })
}

export const getCallFlows = async (companyId: string) => {
	return await noMonitor.post(`/flows/call-flows`, { companyId }).then(res => res.data)
}

export const createCallFlow = async (flowData: any) => {
	const newflowData = { ...flowData, flowType: 'call' }
	return await monitoredRequest.post(`/flows/create-flow`, { flowData: newflowData })
}

export const getUserPosts = async (platforms?: string[]) => {
	const body = platforms && platforms.length > 0 ? { platforms } : {};
	return await noMonitor.post('/feed/user-feed', body).then(res => {
		return res.data.feed
	});
}

// Competitor Analysis API functions
export const createCompetitor = async (competitorData) => {
	return await noMonitor.post('/competitors', competitorData).then(res => res.data);
}

export const getCompetitors = async () => {
	return await noMonitor.get('/competitors').then(res => res.data);
}

export const getCompetitor = async (competitorId) => {
	return await noMonitor.get(`/competitors/${competitorId}`).then(res => res.data);
}

export const updateCompetitor = async (competitorId, data) => {
	return await noMonitor.put(`/competitors/${competitorId}`, data).then(res => res.data);
}

export const deleteCompetitor = async (competitorId) => {
	return await noMonitor.delete(`/competitors/${competitorId}`).then(res => res.data);
}

export const discoverCompetitorSocial = async (competitorId) => {
	return await noMonitor.post(`/competitors/${competitorId}/discover-social`).then(res => res.data);
}

export const confirmCompetitorSocial = async (competitorId, profileData) => {
	return await noMonitor.post(`/competitors/${competitorId}/confirm-social`, profileData).then(res => res.data);
}

export const getCompetitorSocial = async (competitorId, status) => {
	const params = status ? { status } : {};
	return await noMonitor.get(`/competitors/${competitorId}/social`, { params }).then(res => res.data);
}

export const getSuggestedCompetitors = async () => {
	return await noMonitor.get('/competitors/suggested').then(res => res.data);
}

export const analyzeCompetitorContent = async (competitorId) => {
	return await noMonitor.post(`/competitors/${competitorId}/analyze-content`).then(res => res.data);
}

export const getCompetitorInsights = async (competitorId) => {
	return await noMonitor.get(`/competitors/${competitorId}/insights`).then(res => res.data);
}

export const getCompetitorStrategy = async (competitorId) => {
	return await noMonitor.get(`/competitors/${competitorId}/strategy`).then(res => res.data);
}

export const getCompetitorPosts = async (competitorId, network, limit, offset) => {
	const params = {};
	if (network) params.network = network;
	if (limit) params.limit = limit;
	if (offset) params.offset = offset;
	return await noMonitor.get(`/competitors/${competitorId}/posts`, { params }).then(res => res.data);
}

export const updateBilling = async (plan, platform = 'ios') => {
	// Route to correct endpoint based on platform
	const endpoint = platform === 'android' 
		? '/payment/google-store/subscribe'
		: '/payment/app-store/subscribe';
	
	return await monitorFail.post(endpoint, { ...plan })
}

export const bumpViewCount = async (feedId) => {
	return await noMonitor.post(`/feed/bump-view-count`, { feedId })
}

export const likeComment = async (commentId, postId) => {
	return await noMonitor.post('/feed/like-comment', { commentId, postId })
}

// Feed Boost API Functions
export const getBoostPricing = async () => {
	return await noMonitor.get('/feed/boost/pricing').then(res => res.data)
}

export const createPostBoost = async (postId: string, boostData: {
	boostType: 'basic' | 'premium' | 'enterprise';
	budget: number;
	duration: number;
	targetRadius?: number;
	targetAudience?: {
		ageRange?: { min: number; max: number };
		interests?: string[];
		locations?: string[];
	};
	paymentId?: string;
	transactionId?: string;
}) => {
	return await monitoredRequest.post(`/feed/${postId}/boost`, boostData).then(res => res.data)
}

export const pausePostBoost = async (postId: string) => {
	return await monitoredRequest.put(`/feed/${postId}/boost/pause`).then(res => res.data)
}

export const resumePostBoost = async (postId: string) => {
	return await monitoredRequest.put(`/feed/${postId}/boost/resume`).then(res => res.data)
}

export const cancelPostBoost = async (postId: string) => {
	return await monitoredRequest.delete(`/feed/${postId}/boost`).then(res => res.data)
}

export const getPostBoostAnalytics = async (postId: string) => {
	return await noMonitor.get(`/feed/${postId}/boost/analytics`).then(res => res.data)
}

export const getAllUserBoosts = async () => {
	return await noMonitor.get('/feed/boosts').then(res => res.data)
}

export const trackBoostInteraction = async (postId: string, interactionType: 'clicks' | 'engagement' | 'conversions') => {
	return await noMonitor.post(`/feed/${postId}/boost/track`, { interactionType }).then(res => res.data)
}

export const getBountySchema = async () => {
	return await noMonitor.get('/data/bounty-schema').then(res => res.data)
}

export const createBountyPaymentIntent = async (amount: number, currency: string = 'usd') => {
	return await monitoredRequest.post('/data/create-bounty-payment-intent', { amount, currency }).then(res => res.data)
}

export const createBounty = async (vals: any) => {
	return monitoredRequest.post('/data/post-bounty', vals)
}

export const getBounties = async () => {
	return noMonitor.get('/data/get-bounties').then(res => res.data)
}

export const flagItem = async (contentId, flag) => {
	return await noMonitor.post(`/data/flag-item`, { contentId, flag })
}

export const unflagItem = async (contentId) => {
	return await noMonitor.post(`/data/flag-item`, { contentId, flag: false })
}

export const getFlaggedItems = async () => {
	return await noMonitor.get('/data/get-flagged').then(res => res.data)
}

export const updateDataItemHistory = async (dataId, update) => {
	return await noMonitor.post(`/data/update`, { dataId, update })
}

export const getTrackedItemHistory = async (dataId) => {
	return await noMonitor.post(`/data/get-item-history`, { dataId }).then(res => res.data)
}

export const getExpiredCampaigns = async () => {
	return await noMonitor.get('/sales/expired-campaigns').then(res => res.data)
}

export const getCompletedProjects = async () => {
	return await noMonitor.get('/projects/completed').then(res => {
		console.log(res.data, 'from completed projects');
		return res.data
	})
}

export const addIntegration = async (integration) => {
	return await monitoredRequest.post('/integration/add-integration', integration)
}

export const sendTestEmail = async (templateId, to, subject) => {
	return await monitoredRequest.post('/templates/send-test-email', { templateId, email: to, subject })
}

export const testWhatsApp = async () => {
	return await monitoredRequest.post('/integration/whatsapp/test')
}

export const addSSCal = async (vals) => {
	return await monitoredRequest.post('/calendar/create-calendar', vals)
}

export const getWholeCalendarSchema = async () => {
	return await noMonitor.get('/calendar/schema').then(res => res.data)
}

export const authManMail = async (vals) => {
	return await monitoredRequest.post('/integration/mail/auth', vals)
}

export const getMailBoxes = async () => {
	return await noMonitor.get('/authorisation/mailboxes').then(res => res.data)
}

export const setDefaultMailbox = async (mailboxId: string) => {
	return await monitoredRequest.post('/authorisation/mailboxes/default', { mailboxId }).then(res => res.data)
}

export const deleteMailbox = async (mailboxId: string) => {
	return await monitoredRequest.post('/authorisation/mailboxes/delete', { mailboxId }).then(res => res.data)
}

export const getAffiliateDetails = async () => {
	return await noMonitor.get('/user/affiliate/details').then(res => res.data)
}

export const deleteCalendar = async (calendarId) => {
	console.log(calendarId)
	return await noMonitor.delete(`/calendar/remove-calendar/${calendarId}`);
}

export const analyseDataset = async (dataId) => {
	return await monitoredRequest.post(`/ai/analyse-dataset`, { dataId }, { timeout: 120000 })
}

export const createAIDataModel = async (text: string) => {
	return await monitoredRequest.post('/ai/create-schema', {
		description: text,
		currentSchema: [],
		tags: []
	}, { timeout: 120000 })
}

// Document Creation API Methods
export const createDocumentLayout = async (documentType: string, title: string, description: string) => {
	const mlServiceUrl = AI_API_URL || process.env.EXPO_PUBLIC_AI_API || process.env.EXPO_PUBLIC_AI_MOBILE_API || 'http://localhost:11435';
	console.log('📄 [createDocumentLayout] Using ML service URL:', mlServiceUrl);
	return await fetch(`${mlServiceUrl}/create-document-layout`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			document_type: documentType,
			title: title,
			description: description || ''
		}),
		credentials: 'include'
	}).then(res => res.json());
}

export const generateDocumentContent = async (layoutData: any, userRequirements?: string) => {
	const mlServiceUrl = AI_API_URL || process.env.EXPO_PUBLIC_AI_API || process.env.EXPO_PUBLIC_AI_MOBILE_API || 'http://localhost:11435';
	console.log('📄 [generateDocumentContent] Using ML service URL:', mlServiceUrl);
	return await fetch(`${mlServiceUrl}/generate-document-content`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			layout_data: layoutData,
			user_requirements: userRequirements || ''
		}),
		credentials: 'include'
	}).then(res => res.json());
}

export const refineDocumentDesign = async (contentData: any, designSettings?: any) => {
	const mlServiceUrl = AI_API_URL || process.env.EXPO_PUBLIC_AI_API || process.env.EXPO_PUBLIC_AI_MOBILE_API || 'http://localhost:11435';
	console.log('📄 [refineDocumentDesign] Using ML service URL:', mlServiceUrl);
	return await fetch(`${mlServiceUrl}/refine-document-design`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			content_data: contentData,
			design_settings: designSettings || {}
		}),
		credentials: 'include'
	}).then(res => res.json());
}

export const validateDocument = async (documentData: any) => {
	const mlServiceUrl = AI_API_URL || process.env.EXPO_PUBLIC_AI_API || process.env.EXPO_PUBLIC_AI_MOBILE_API || 'http://localhost:11435';
	console.log('📄 [validateDocument] Using ML service URL:', mlServiceUrl);
	return await fetch(`${mlServiceUrl}/validate-document`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			document_data: documentData
		}),
		credentials: 'include'
	}).then(res => res.json());
}

export const getMapList = async (location) => {
	return await noMonitor.post('/company/list', { location })
}

// Networking API functions
export const getNetworkingBusinesses = async (params = {}) => {
	const queryParams = new URLSearchParams();
	
	Object.keys(params).forEach(key => {
		if (params[key] !== undefined && params[key] !== null) {
			if (Array.isArray(params[key])) {
				params[key].forEach(value => queryParams.append(key, value));
			} else {
				queryParams.append(key, params[key]);
			}
		}
	});
	
	return await noMonitor.get(`/networking/businesses?${queryParams.toString()}`);
}

export const getNetworkingBusinessById = async (id) => {
	return await noMonitor.get(`/networking/businesses/${id}`);
}

// Resume Extraction and Templating API functions
export const extractResume = async (fileData?: string, fileType?: string, linkedinUrl?: string) => {
	if (linkedinUrl) {
		// LinkedIn URL extraction
		return await monitoredRequest.post('/ai/resume/extract', { linkedin_url: linkedinUrl }, { timeout: 600000 });
	}

	if (fileData) {
		// File upload - handle base64 data URL
		let base64Data = fileData;
		if (fileData.includes(',')) {
			base64Data = fileData.split(',')[1];
		}

		// Send as base64 in JSON body for better cross-platform compatibility
		// Use 10 minute timeout for large documents (10+ pages)
		return await monitoredRequest.post('/ai/resume/extract', {
			file_data: base64Data,
			file_type: fileType || 'application/pdf'
		}, { timeout: 600000 });
	}

	throw new Error('Either fileData or linkedinUrl must be provided');
}

export const getResumeTemplates = async () => {
	return await noMonitor.get('/ai/resume/templates');
}

export const applyResumeTemplate = async (templateId: string, resumeData: any) => {
	return await monitoredRequest.post('/ai/resume/apply-template', {
		templateId,
		resumeData
	}, { timeout: 600000 }); // 10 minutes timeout for template processing (handles large resumes)
}

export const searchNetworkingBusinesses = async (query, location = {}) => {
	const params = new URLSearchParams({
		query,
		...location
	});
	
	return await noMonitor.get(`/networking/search?${params.toString()}`);
}

export const testNotice = async() => {
	console.log('testing notice');
	return await monitorFail.get('/test/force').then(res => {
		return true
	})
}

export const getDocumentTemplates = async () => {
	return await noMonitor.get('/templates/documents').then(res => { return res.data.documents })
}

// Document Folder APIs
export const createDocumentFolder = async (name: string, parentId?: string | null) => {
	return await monitoredRequest.post('/documents/create-folder', { name, parentId }).then(res => res.data)
}

export const getDocumentFolders = async (parentId?: string | null) => {
	return await noMonitor.post('/documents/get-folders', { parentId }).then(res => res.data)
}

export const deleteDocumentFolder = async (folderId: string) => {
	return await monitoredRequest.delete(`/documents/delete-folder/${folderId}`).then(res => res.data)
}

export const moveDocumentToFolder = async (documentId: string, folderId: string | null) => {
	return await monitoredRequest.post('/documents/move-document', { documentId, folderId }).then(res => res.data)
}

// User Document APIs
export const uploadUserDocument = async (fileData: {
	name: string;
	type: string;
	size?: number;
	fileData?: string;
	uri?: string;
	folderId?: string | null;
}) => {
	return await monitoredRequest.post('/documents/upload', fileData).then(res => res.data)
}

export const getUserUploadedDocuments = async (folderId?: string | null) => {
	return await noMonitor.post('/documents/get-documents', { folderId }).then(res => res.data)
}

export const deleteUserDocument = async (documentId: string) => {
	return await monitoredRequest.delete(`/documents/delete-document/${documentId}`).then(res => res.data)
}

export const cancelApplePlan = async () => {
	await monitoredRequest.post('/payment/app-store/cancel')
}

export const cancelPlayPlan = async () => {
}

export const cancelStripePlan = async () => {
	return await monitoredRequest.post('/stripe/cancel')
}

// Schema stats tracking functions
export const trackDataView = async (dataId: string) => {
	return await noMonitor.post(`/data/track-schema-view/${dataId}`).then(res => res.data);
}

export const trackDataSchemaInteraction = async (dataId: string, action: string) => {
	return await noMonitor.post(`/data/track-schema-interaction/${dataId}`, { action }).then(res => res.data);
}

export const getDataStats = async (dataId: string) => {
	return await noMonitor.get(`/data/schema-stats/${dataId}`).then(res => res.data);
}

// Utility Functions APIs
export const saveUtility = async (utilityData) => {
	return await noMonitor.post('/utilities', utilityData).then(res => res.data);
}

export const getUtilitiesForData = async (dataId: string) => {
	return await noMonitor.get(`/utilities/data/${dataId}`).then(res => res.data);
}

export const getUtility = async (utilityId: string) => {
	return await noMonitor.get(`/utilities/${utilityId}`).then(res => res.data);
}

export const updateUtility = async (utilityId: string, updates: any) => {
	return await noMonitor.put(`/utilities/${utilityId}`, updates).then(res => res.data);
}

export const deleteUtility = async (utilityId: string) => {
	return await noMonitor.delete(`/utilities/${utilityId}`).then(res => res.data);
}

export const linkUtilitiesToData = async (dataId: string, utilityIds: string[]) => {
	return await noMonitor.put(`/data/${dataId}/utilities`, { utilityIds }).then(res => res.data);
}

export const executeUtility = async (utilityId: string, dataItemId?: string) => {
	return await noMonitor.post(`/utilities/${utilityId}/execute`, { dataItemId }).then(res => res.data);
}

export const getWhatsAppProfile = async () => {
	return await noMonitor.get('/social-media/profile/whatsapp').then(res => res.data)
}

export const getWhatsAppMessages = async () => {
	return await noMonitor.get('/integration/whatsapp/messages').then(res => res.data)
}

export const getWhatsappTemplates = async () => {
	return await noMonitor.get('/integration/whatsapp/templates').then(res => res.data)
}

export const updateWhatsAppConversationModelSettings = async (conversationModelId: string) => {
	return await monitoredRequest.put('/whatsapp/conversation-model/settings', { conversationModelId })
}

export const sendTemplateMessage = async (request: {
	to: string;
	templateName: string;
	templateLanguage: string;
	parameters?: any[];
	businessPhoneNumberId: string;
}) => {
	return await monitoredRequest.post('/whatsapp/send/template', request)
}

export const verifyCouponCode = async (code:string) => {
	return await noMonitor.post('/promo/verify', {code})
}

export const verifyOTPCode = async (email?: string, code?: string) => {
	return await api.post('/validate-email-otp', { email, otpCode: code }).then(res => res.data)
}

export const getStripeProfile = async ()=> {
	return await noMonitor.get('/stripe/connect/profile').then(res => res.data);
}

export const syncShopifyProducts = async () => {
	return await monitoredRequest.post('/integrations/shopify/sync-products').then(res => res.data);
}

// Product Sync API Functions
export const getProductSyncStatus = async (dataId: string) => {
	return await monitoredRequest.get(`/products/sync/status/${dataId}`).then(res => res.data);
}

export const pullProductsFromIntegration = async (dataId: string, integrationType: string) => {
	return await monitoredRequest.post(`/products/sync/pull/${dataId}`, { integrationType }).then(res => res.data);
}

export const pushProductsToIntegration = async (dataId: string, integrationType: string) => {
	return await monitoredRequest.post(`/products/sync/push/${dataId}`, { integrationType }).then(res => res.data);
}

export const resolveProductConflict = async (itemId: string, resolution: 'local' | 'external' | 'merge', integrationType?: string) => {
	return await monitoredRequest.post('/products/sync/resolve-conflict', { itemId, resolution, integrationType }).then(res => res.data);
}

export const getAvailableIntegrations = async () => {
	return await monitoredRequest.get('/products/sync/available-integrations').then(res => res.data);
}

// Stripe Sales Statistics API Functions
export const getStripeSalesStats = async () => {
	return await noMonitor.get('/stripe/sales/stats').then(res => res.data);
}

export const getStripeMonthlySalesOverview = async (months?: number) => {
	const params = months ? { months } : {};
	return await noMonitor.get('/stripe/sales/monthly-overview', { params }).then(res => res.data);
}

export const getStripeRecentTransactions = async (limit?: number) => {
	const params = limit ? { limit } : {};
	return await noMonitor.get('/stripe/sales/recent-transactions', { params }).then(res => res.data);
}

export const getStripePaymentMethodStats = async () => {
	return await noMonitor.get('/stripe/sales/payment-methods').then(res => res.data);
}

// PayStack Sales Statistics API functions
export const getPayStackSalesStats = async () => {
	return await noMonitor.get('/paystack/sales/stats').then(res => res.data);
}

export const getPayStackMonthlySalesOverview = async (months?: number) => {
	const params = months ? { months } : {};
	return await noMonitor.get('/paystack/sales/monthly-overview', { params }).then(res => res.data);
}

export const getPayStackRecentTransactions = async (limit?: number) => {
	const params = limit ? { limit } : {};
	return await noMonitor.get('/paystack/sales/recent-transactions', { params }).then(res => res.data);
}

export const getPayStackPaymentMethodStats = async () => {
	return await noMonitor.get('/paystack/sales/payment-methods').then(res => res.data);
}

export const getXProfile = async () => {
	return await noMonitor.get('/integration/x/profile').then(res => res.data);
}

export const getInstagramProfile = async () => {
	return await noMonitor.get('/social-media/profile/instagram').then(res => res.data);
}

export const getInstagramMedia = async (limit?: number) => {
	const params = limit ? { limit } : {};
	return await noMonitor.get('/social-media/instagram/media', { params }).then(res => res.data);
}

export const getFacebookProfile = async () => {
	return await noMonitor.get('/social-media/profile/facebook').then(res => res.data);
}

export const getYoutubeProfile = async () => {
	return await noMonitor.get('/social-media/profile/youtube').then(res => res.data);
}

export const getStoreSettings = async (userId: string) => {
	return await noMonitor.get(`/store/settings/${userId}`).then(res => {
		// Backend returns { success: true, data: {...} }
		return res.data?.data || res.data || {};
	}).catch(err => {
		console.error('[getStoreSettings] API error:', err);
		throw err;
	});
}

export const updateStoreSettings = async (userId: string, settings: any) => {
	return await noMonitor.put(`/store/settings/${userId}`, settings).then(res => {
		// Backend returns { success: true, data: {...} }
		return res.data?.data || res.data || {};
	}).catch(err => {
		console.error('[updateStoreSettings] API error:', err);
		throw err;
	});
}

export const getYoutubeVideos = async () => {
	console.log('🎬 API: getYoutubeVideos called');
	console.log('🌐 API: Making request to /social-media/youtube/videos');

	try {
		const response = await noMonitor.get('/social-media/youtube/videos');
		console.log('✅ API: YouTube videos response received:', {
			status: response.status,
			statusText: response.statusText,
			data: response.data
		});
		return response.data;
	} catch (error: any) {
		console.error('❌ API: getYoutubeVideos error:', {
			message: error?.message,
			status: error?.response?.status,
			statusText: error?.response?.statusText,
			data: error?.response?.data,
			url: error?.config?.url,
			method: error?.config?.method
		});
		throw error;
	}
}

export const getLinkedInProfile = async () => {
	return await noMonitor.get('/integration/linkedin/profile').then(res => res.data);
}

export const scheduleToFeed = async (data) => {
	return await monitoredRequest.post('/feed/post/schedule-test', data).then(res => res.data)
}

export const scheduleMultipleSocialPosts = async (data) => {
	return await monitoredRequest.post('/ai/schedule-multiple-social-posts', data, { timeout: 120000 }).then(res => res.data)
}

export const cancelScheduledPost = async (postId) => {
	return await monitoredRequest.delete(`/ai/schedule-social-post/${postId}`).then(res => res.data)
}

export const getScheduledPosts = async (userId) => {
	return await monitoredRequest.get(`/ai/scheduled-posts/${userId}`).then(res => res.data)
}

export const updateScheduledPost = async (postId, data) => {
	return await monitoredRequest.put(`/ai/scheduled-posts/${postId}`, data).then(res => res.data)
}

export const getAffiliateCode = async () => {
	return await noMonitor.get('/user/affiliate/get-code').then(res => res.data)
}

export const getWordpressProfile = async () => {
	return await noMonitor.get('/integration/wordpress/profile').then(res => res.data)
}

export const getWordPressBlogDetails = async () => {
	return await noMonitor.get('/integration/wordpress/get-posts').then(res => res.data)
}

export const buildProductContent = async (productId: string, previousHtml: string) => {
	return await noMonitor.post(`/ai/build-product-content/${productId}`, { previousHtml }, { timeout: 120000 }).then(res => res.data)
}

export const getSlackProfile = async () => {
	return await noMonitor.get('/integration/slack/profile').then(res => res.data);
}

// Hugging Face integration functions
export const getHuggingFaceProfile = async () => {
	return await noMonitor.get('/integration/huggingface/profile').then(res => res.data);
}

export const storeHuggingFaceToken = async (token: string) => {
	return await monitoredRequest.post('/integration/huggingface/token', { token }).then(res => res.data);
}

export const removeHuggingFaceToken = async () => {
	return await monitoredRequest.delete('/integration/huggingface/token').then(res => res.data);
}

export const getHuggingFaceModels = async () => {
	return await noMonitor.get('/integration/huggingface/models').then(res => res.data);
}

export const exportDataModelToHF = async (dataModelId: string, options: { datasetName: string }) => {
	return await monitoredRequest.post('/integration/huggingface/export-data', { dataModelId, ...options }, { timeout: 120000 }).then(res => res.data);
}

export const initiateHuggingFaceTraining = async (datasetId: string, config?: any) => {
	return await monitoredRequest.post('/integration/huggingface/train', { datasetId, config }, { timeout: 120000 }).then(res => res.data);
}

export const getHuggingFaceTrainingHistory = async () => {
	return await noMonitor.get('/integration/huggingface/training-history').then(res => res.data);
}

export const getHuggingFaceTrainingStatus = async (trainingId: string) => {
	return await noMonitor.get(`/integration/huggingface/training/${trainingId}`).then(res => res.data);
}

// PayStack integration functions
export const getPayStackProfile = async () => {
	return await noMonitor.get('/integration/paystack/profile').then(res => res.data);
}

export const storePayStackKeys = async (publicKey: string, secretKey: string) => {
	return await monitoredRequest.post('/integration/paystack/keys', { publicKey, secretKey }).then(res => res.data);
}

export const removePayStackKeys = async () => {
	return await monitoredRequest.delete('/integration/paystack/keys').then(res => res.data);
}

export const connectPlatform = async (platform: string) => {
	return await monitoredRequest.get(`/social-media/integrations/connect/${platform}`).then(res => res.data)
}

export const connectPlatformCallback = async (code: string, identifier: string, codeVerifier: string, verifier?: string) => {
	console.log('[connectPlatformCallback] Sending to backend:', {
		identifier,
		hasCode: !!code,
		codePreview: code ? `${code.substring(0, 10)}...` : 'missing',
		redirectUri: verifier || 'missing',
		redirectUriLength: verifier?.length || 0
	});
	return await monitoredRequest.post(`/social-media/integrations/auth-callback`, { code, identifier, codeVerifier, redirectUri: verifier }).then(res => res.data).catch(e => {
		console.error("[authCallback] error occurred:", e)
	})
}

export const getAINotes = async (dataId: string) => {
	return await noMonitor.get(`/ai/analyse-dataset/${dataId}`, { timeout: 120000 }).then((res) => {
		if (res.success) {
			dispatch({ type: SHOW_AUTH_MODAL, payload: { content: res.data, type: 'ai-notes' } })
		}
	})
}

export const changePublishSettings = async (integration: string) => {
	return await noMonitor.get('/integration/publish-settings/' + integration).then(res => res.data)
}

export const registerLinkedIn = async (code, redirectUri) => {
	return await monitoredRequest.post('/sso/linkedin/login', { code, redirectUri })
}

// Report application errors and crashes
export const reportError = async (errorReport) => {
	try {
		return await monitoredRequest.post('/errors/report', errorReport);
	} catch (error) {
		console.error('Failed to report error:', error);
		// Fallback to local storage or console logging
		if (Platform.OS === 'web') {
			localStorage.setItem(`error_report_${Date.now()}`, JSON.stringify(errorReport));
		}
		throw error;
	}
}

// Usage tracking functions
const trackUsage = async (itemId, action = 'viewed', metadata = {}) => {
	try {
		if (!itemId) return null;
		const response = await api.post(`/data/track-usage/${itemId}`, { action, metadata });
		return response.data;
	} catch (error) {
		console.error('Error tracking usage:', error);
		return null;
	}
};

const getUsageStats = async (itemId, detailed = false) => {
	try {
		if (!itemId) return null;
		const response = await api.get(`/data/usage-stats/${itemId}?detailed=${detailed}`);
		return response.data;
	} catch (error) {
		console.error('Error getting usage stats:', error);
		return null;
	}
};

const getMostUsedItems = async (options: any = {}) => {
	try {
		const { dataId, limit = 10, timeframe = 'all' } = options;
		const params = new URLSearchParams();
		if (dataId) params.append('dataId', dataId);
		params.append('limit', limit.toString());
		params.append('timeframe', timeframe);
		const response = await api.get(`/data/most-used?${params.toString()}`);
		return response.data;
	} catch (error) {
		console.error('Error getting most used items:', error);
		return null;
	}
};

const bulkTrackUsage = async (items) => {
	try {
		if (!Array.isArray(items) || items.length === 0) return null;
		const response = await api.post('/data/bulk-track-usage', { items });
		return response.data;
	} catch (error) {
		console.error('Error bulk tracking usage:', error);
		return null;
	}
};

// Timeline CRM API functions
const getTimelineData = async (dataId) => {
	try {
		const response = await api.get(`/timeline/${dataId}`);
		return response.data;
	} catch (error) {
		console.error('Error fetching timeline data:', error);
		return null;
	}
};

const addContactNote = async (dataId, noteData) => {
	try {
		const response = await api.post(`/timeline/${dataId}/notes`, noteData);
		return response.data;
	} catch (error) {
		console.error('Error adding contact note:', error);
		throw error;
	}
};

const addContactTask = async (dataId, taskData) => {
	try {
		const response = await api.post(`/timeline/${dataId}/tasks`, taskData);
		return response.data;
	} catch (error) {
		console.error('Error adding contact task:', error);
		throw error;
	}
};

const toggleTask = async (dataId, taskId, completed) => {
	try {
		const response = await api.patch(`/timeline/${dataId}/tasks/${taskId}`, { completed });
		return response.data;
	} catch (error) {
		console.error('Error toggling task:', error);
		throw error;
	}
};

const logInteraction = async (dataId, interactionData) => {
	try {
		const response = await api.post(`/timeline/${dataId}/interactions`, interactionData);
		return response.data;
	} catch (error) {
		console.error('Error logging interaction:', error);
		throw error;
	}
};

const updateContactMetrics = async (dataId, metrics) => {
	try {
		const response = await api.patch(`/timeline/${dataId}/metrics`, metrics);
		return response.data;
	} catch (error) {
		console.error('Error updating contact metrics:', error);
		throw error;
	}
};

const analyzeCustomerHealth = async (dataId) => {
	try {
		const response = await api.post(`/ai/analyze-customer-health/${dataId}`, {}, { timeout: 120000 });
		return response.data;
	} catch (error) {
		console.error('Error analyzing customer health:', error);
		throw error;
	}
};

const getCachedCustomerHealthAnalysis = async (dataId) => {
	try {
		const response = await api.get(`/ai/customer-health-analysis/${dataId}`, { timeout: 120000 });
		return response.data;
	} catch (error) {
		console.error('Error getting cached customer health analysis:', error);
		throw error;
	}
};

const getCRMTimelineFolders = async () => {
	try {
		const response = await api.get('/data/crm-folders');
		return response.data;
	} catch (error) {
		console.error('Error fetching CRM folders:', error);
		return null;
	}
};

// Product Management API functions
const getProductDetails = async (productId) => {
	try {
		const response = await noMonitor.get(`/products/${productId}`);
		return response.data;
	} catch (error) {
		console.error('Error fetching product details:', error);
		return null;
	}
};

const addProductSupplier = async (productId, supplierData) => {
	try {
		const response = await api.post(`/products/${productId}/suppliers`, supplierData);
		return response.data;
	} catch (error) {
		console.error('Error adding supplier:', error);
		throw error;
	}
};

const getProductSuppliers = async (productId) => {
	try {
		const response = await noMonitor.get(`/products/${productId}/suppliers`);
		return response.data;
	} catch (error) {
		console.error('Error fetching suppliers:', error);
		return null;
	}
};

const updateProductSupplier = async (productId, supplierId, updateData) => {
	try {
		const response = await api.put(`/products/${productId}/suppliers/${supplierId}`, updateData);
		return response.data;
	} catch (error) {
		console.error('Error updating supplier:', error);
		throw error;
	}
};

const deleteProductSupplier = async (productId, supplierId) => {
	try {
		const response = await api.delete(`/products/${productId}/suppliers/${supplierId}`);
		return response.data;
	} catch (error) {
		console.error('Error deleting supplier:', error);
		throw error;
	}
};

const updateProductPricing = async (productId, pricingData) => {
	try {
		const response = await api.put(`/products/${productId}/pricing`, pricingData);
		return response.data;
	} catch (error) {
		console.error('Error updating pricing:', error);
		throw error;
	}
};

const addQualityCheck = async (productId, qualityData) => {
	try {
		const response = await api.post(`/products/${productId}/quality-checks`, qualityData);
		return response.data;
	} catch (error) {
		console.error('Error adding quality check:', error);
		throw error;
	}
};

const getProductAnalytics = async (productId) => {
	try {
		const response = await noMonitor.get(`/products/${productId}/analytics`);
		return response.data;
	} catch (error) {
		console.error('Error fetching analytics:', error);
		return null;
	}
};

const getProductInventory = async (productId) => {
	try {
		const response = await noMonitor.get(`/products/${productId}/inventory`);
		return response.data;
	} catch (error) {
		console.error('Error fetching inventory:', error);
		return null;
	}
};

const updateProductInventory = async (productId, inventoryData) => {
	try {
		const response = await api.put(`/products/${productId}/inventory`, inventoryData);
		return response.data;
	} catch (error) {
		console.error('Error updating inventory:', error);
		throw error;
	}
};

export const registerAndPay = async (data) => {
	await api.post('/checkout/register', { ...data })
}

// AI Email Template Generation APIs
export const generateEmailTemplateWithAI = async (description: string, options: {
	referenceTemplateIds?: string[];
	templateType?: string;
	tone?: string;
} = {}) => {
	try {
		const response = await api.post('/ai/email-template/generate', {
			description,
			...options
		}, { timeout: 120000 });
		console.log('✅ Email template generated:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to generate email template:', error);
		throw error;
	}
};

export const suggestEmailTemplates = async (description: string, limit: number = 5) => {
	try {
		console.log('💡 Suggesting email templates for:', description);
		const response = await api.post('/ai/email-template/suggest', {
			description,
			limit
		}, { timeout: 120000 });
		console.log('✅ Template suggestions received:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to suggest templates:', error);
		throw error;
	}
};

export const generateDocumentTemplateWithAI = async (description: string, options: {
	referenceTemplateIds?: string[];
	documentType?: string;
	tone?: string;
} = {}) => {
	try {
		console.log('📄 Generating document template with AI:', { description, options });
		const response = await monitoredRequest.post('/ai/document-template/generate', {
			description,
			...options
		}, { timeout: 120000 });
		console.log('✅ Document template generated:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to generate document template:', error);
		throw error;
	}
};

// Function to get all tasks for a project - placed before main export
export const getProjectTasks = async (projectId: string, globalId: string) => {
	try {
		console.log('📋 Getting tasks for project:', { projectId, globalId });
		const response = await noMonitor.get(`/projects/${globalId}/tasks/${projectId}`);
		console.log('📋 Tasks response:', response.data);
		return response;
	} catch (error) {
		console.error('❌ Failed to fetch project tasks:', error);
		throw error;
	}
};

// API Key Management Methods
export const createAPIKey = async (keyData: any) => {
	try {
		console.log('🔑 Creating API key:', keyData);
		const response = await monitoredRequest.post('/keys/create', keyData);
		console.log('🔑 API key created:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to create API key:', error);
		throw error;
	}
};

export const getAPIKeys = async () => {
	try {
		console.log('🔑 Fetching API keys');
		const response = await noMonitor.get('/keys');
		console.log('🔑 API keys fetched:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to fetch API keys:', error);
		throw error;
	}
};

export const deleteAPIKey = async (keyId: string) => {
	try {
		console.log('🔑 Deleting API key:', keyId);
		const response = await monitoredRequest.delete(`/keys/${keyId}`);
		console.log('🔑 API key deleted:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to delete API key:', error);
		throw error;
	}
};

export const toggleAPIKey = async (keyId: string) => {
	try {
		console.log('🔑 Toggling API key:', keyId);
		const response = await monitoredRequest.patch(`/keys/${keyId}/toggle`);
		console.log('🔑 API key toggled:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to toggle API key:', error);
		throw error;
	}
};

// AI Model API Key Management Functions
export const createAIModelKey = async (keyData: { provider: string; modelName: string; apiKey: string }) => {
	try {
		console.log('🤖 Creating AI model API key:', { provider: keyData.provider, modelName: keyData.modelName });
		const response = await monitoredRequest.post('/ai-model-keys/create', keyData);
		console.log('🤖 AI model API key created:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to create AI model API key:', error);
		throw error;
	}
};

export const getAIModelKeys = async () => {
	try {
		console.log('🤖 Fetching AI model API keys');
		const response = await noMonitor.get('/ai-model-keys');
		console.log('🤖 AI model API keys fetched:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to fetch AI model API keys:', error);
		throw error;
	}
};

export const updateAIModelKey = async (keyId: string, updates: { isDefault?: boolean; isActive?: boolean; modelName?: string }) => {
	try {
		console.log('🤖 Updating AI model API key:', keyId, updates);
		const response = await monitoredRequest.patch(`/ai-model-keys/${keyId}`, updates);
		console.log('🤖 AI model API key updated:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to update AI model API key:', error);
		throw error;
	}
};

export const deleteAIModelKey = async (keyId: string) => {
	try {
		console.log('🤖 Deleting AI model API key:', keyId);
		const response = await monitoredRequest.delete(`/ai-model-keys/${keyId}`);
		console.log('🤖 AI model API key deleted:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to delete AI model API key:', error);
		throw error;
	}
};

export const toggleAIModelKey = async (keyId: string) => {
	try {
		console.log('🤖 Toggling AI model API key:', keyId);
		const response = await monitoredRequest.patch(`/ai-model-keys/${keyId}/toggle`);
		console.log('🤖 AI model API key toggled:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to toggle AI model API key:', error);
		throw error;
	}
};

export const regenerateAPIKey = async (keyId: string) => {
	try {
		console.log('🔑 Regenerating API key:', keyId);
		const response = await monitoredRequest.post(`/keys/${keyId}/regenerate`);
		console.log('🔑 API key regenerated:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to regenerate API key:', error);
		throw error;
	}
};

// OAuth Token Management Methods
export const getOAuthTokens = async () => {
	try {
		console.log('🔐 Fetching OAuth tokens');
		const response = await noMonitor.get('/oauth/tokens');
		console.log('🔐 OAuth tokens fetched:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to fetch OAuth tokens:', error);
		throw error;
	}
};

export const revokeOAuthToken = async (tokenIndex: number) => {
	try {
		console.log('🔐 Revoking OAuth token:', tokenIndex);
		const response = await monitoredRequest.delete(`/oauth/tokens/${tokenIndex}`);
		console.log('🔐 OAuth token revoked:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to revoke OAuth token:', error);
		throw error;
	}
};

// Webhook Management Methods
export const createWebhook = async (webhookData: any) => {
	try {
		console.log('🔗 Creating webhook:', webhookData);
		const response = await monitoredRequest.post('/webhooks/create', webhookData);
		console.log('🔗 Webhook created:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to create webhook:', error);
		throw error;
	}
};

export const getWebhooks = async () => {
	try {
		console.log('🔗 Fetching webhooks');
		const response = await noMonitor.get('/webhooks');
		console.log('🔗 Webhooks fetched:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to fetch webhooks:', error);
		throw error;
	}
};

export const getAvailableEvents = async () => {
	try {
		console.log('🔗 Fetching available webhook events');
		const response = await noMonitor.get('/webhooks/events');
		console.log('🔗 Available events fetched:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to fetch available events:', error);
		throw error;
	}
};

export const updateWebhook = async (webhookId: string, updateData: any) => {
	try {
		console.log('🔗 Updating webhook:', webhookId, updateData);
		const response = await monitoredRequest.put(`/webhooks/${webhookId}`, updateData);
		console.log('🔗 Webhook updated:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to update webhook:', error);
		throw error;
	}
};

export const deleteWebhook = async (webhookId: string) => {
	try {
		console.log('🔗 Deleting webhook:', webhookId);
		const response = await monitoredRequest.delete(`/webhooks/${webhookId}`);
		console.log('🔗 Webhook deleted:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to delete webhook:', error);
		throw error;
	}
};

export const toggleWebhook = async (webhookId: string) => {
	try {
		console.log('🔗 Toggling webhook:', webhookId);
		const response = await monitoredRequest.patch(`/webhooks/${webhookId}/toggle`);
		console.log('🔗 Webhook toggled:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to toggle webhook:', error);
		throw error;
	}
};

export const regenerateWebhookSecret = async (webhookId: string) => {
	try {
		console.log('🔗 Regenerating webhook secret:', webhookId);
		const response = await monitoredRequest.post(`/webhooks/${webhookId}/regenerate-secret`);
		console.log('🔗 Webhook secret regenerated:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to regenerate webhook secret:', error);
		throw error;
	}
};

export const testWebhook = async (webhookId: string) => {
	try {
		console.log('🔗 Testing webhook:', webhookId);
		const response = await monitoredRequest.post(`/webhooks/${webhookId}/test`);
		console.log('🔗 Webhook test result:', response.data);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to test webhook:', error);
		throw error;
	}
};

// User Stats Trends Methods
export const getUserStatsTrends = async (startDate: string, endDate: string, interval: string = 'day') => {
	try {
		console.log('📊 Fetching user stats trends:', { startDate, endDate, interval });
		console.log('📊 API endpoint: /data/user-stats-trends');
		const response = await noMonitor.post('/data/user-stats-trends', {
			startDate,
			endDate,
			interval
		});
		console.log('📊 User stats trends fetched:', response.data);
		console.log('📊 Response status:', response.status);
		return response.data;
	} catch (error) {
		console.error('❌ Failed to fetch user stats trends:', error);
		console.error('❌ Error details:', error.response?.data || error.message);
		throw error;
	}
};

// Chat Management API Functions
export const saveChat = async (chatData: {
	userId: string;
	companyId: string;
	messages: Array<{ role: 'user' | 'assistant' | 'system'; content: string; timestamp?: Date }>;
	metadata?: {
		editorConnected?: boolean;
		sessionDuration?: number;
		aiModel?: string;
		[key: string]: any;
	};
}) => {
	return await monitoredRequest.post('/chats/save', chatData).then(res => res.data);
};

export const getUserChats = async (userId: string, options: {
	page?: number;
	limit?: number;
	isArchived?: boolean;
	sortBy?: 'lastMessageAt' | 'createdAt' | 'title';
	sortOrder?: 'asc' | 'desc';
} = {}) => {
	const params = new URLSearchParams();
	if (options.page) params.append('page', options.page.toString());
	if (options.limit) params.append('limit', options.limit.toString());
	if (options.isArchived !== undefined) params.append('isArchived', options.isArchived.toString());
	if (options.sortBy) params.append('sortBy', options.sortBy);
	if (options.sortOrder) params.append('sortOrder', options.sortOrder);

	const queryString = params.toString();
	return await noMonitor.get(`/chats/user/${userId}${queryString ? `?${queryString}` : ''}`).then(res => res.data);
};

export const getChatById = async (chatId: string, userId: string) => {
	return await noMonitor.get(`/chats/${chatId}?userId=${userId}`).then(res => res.data);
};

export const updateChatTitle = async (chatId: string, title: string, userId: string) => {
	return await monitoredRequest.put(`/chats/${chatId}/title`, { title, userId }).then(res => res.data);
};

export const toggleChatArchive = async (chatId: string, isArchived: boolean, userId: string) => {
	return await monitoredRequest.patch(`/chats/${chatId}/archive`, { isArchived, userId }).then(res => res.data);
};

export const deleteChat = async (chatId: string, userId: string) => {
	return await monitoredRequest.delete(`/chats/${chatId}?userId=${userId}`).then(res => res.data);
};

// Dashboard Management API Functions
export const suggestDashboardComponents = async (dataSourceIds: string[]) => {
	return await monitoredRequest.post('/dashboards/suggest-components', { dataSourceIds }).then(res => res.data);
};

export const createDashboard = async (dashboardData: {
	title: string;
	description?: string;
	dataSourceIds: string[];
	layout?: {
		type?: 'grid' | 'freeform';
		columns?: number;
		spacing?: number;
	};
	components: Array<{
		componentId: string;
		componentType: string;
		config: any;
		dataMapping?: any;
		position?: { x: number; y: number };
		size?: { width: number; height: number };
	}>;
}) => {
	return await monitoredRequest.post('/dashboards/create', dashboardData).then(res => res.data);
};

export const getUserDashboards = async (userId: string, options: {
	page?: number;
	limit?: number;
	isArchived?: boolean;
} = {}) => {
	const params = new URLSearchParams();
	if (options.page) params.append('page', options.page.toString());
	if (options.limit) params.append('limit', options.limit.toString());
	if (options.isArchived !== undefined) params.append('isArchived', options.isArchived.toString());

	const queryString = params.toString();
	return await noMonitor.get(`/dashboards/user/${userId}${queryString ? `?${queryString}` : ''}`).then(res => res.data);
};

export const getDashboard = async (dashboardId: string) => {
	return await noMonitor.get(`/dashboards/${dashboardId}`).then(res => res.data);
};

export const updateDashboard = async (dashboardId: string, updates: {
	title?: string;
	description?: string;
	layout?: any;
	components?: any[];
	dataSourceIds?: string[];
	tags?: string[];
}) => {
	return await monitoredRequest.put(`/dashboards/${dashboardId}`, updates).then(res => res.data);
};

export const deleteDashboard = async (dashboardId: string) => {
	return await monitoredRequest.delete(`/dashboards/${dashboardId}`).then(res => res.data);
};

export const getUserChatStats = async (userId: string) => {
	return await noMonitor.get(`/chats/user/${userId}/stats`).then(res => res.data);
};

export const addMessageToChat = async (chatId: string, message: {
	role: 'user' | 'assistant' | 'system';
	content: string;
}, userId: string) => {
	return await monitoredRequest.post(`/chats/${chatId}/message`, { ...message, userId }).then(res => res.data);
};

// AI Chat Title Generation API Functions
export const generateChatTitle = async (firstMessage: string, messages: Array<{ role: string; content: string }> = []) => {
	return await noMonitor.post('/ai/chat/generate-title', { firstMessage, messages }, { timeout: 120000 }).then(res => res.data);
};


// Academy API Functions
// Course Management
export const getAcademyCourses = async (params: {
	page?: number;
	limit?: number;
	category?: string;
	level?: string;
	isPublic?: boolean;
	isPublished?: boolean;
	author?: string;
	search?: string;
	sortBy?: string;
	sortOrder?: 'asc' | 'desc';
} = {}) => {
	return await noMonitor.get('/academy/courses', { params }).then(res => res.data);
};

export const getAcademyCourse = async (courseId: string, trackView: boolean = false, deviceType: string = 'desktop', source: string = 'direct') => {
	return await noMonitor.get(`/academy/courses/${courseId}`, {
		params: { trackView, deviceType, source }
	}).then(res => res.data);
};

export const createAcademyCourse = async (courseData: any) => {
	return await monitoredRequest.post('/academy/courses', courseData).then(res => res.data);
};

export const updateAcademyCourse = async (courseId: string, updateData: any) => {
	return await monitoredRequest.put(`/academy/courses/${courseId}`, updateData).then(res => res.data);
};

export const deleteAcademyCourse = async (courseId: string) => {
	return await monitoredRequest.delete(`/academy/courses/${courseId}`).then(res => res.data);
};

export const publishAcademyCourse = async (courseId: string) => {
	return await monitoredRequest.patch(`/academy/courses/${courseId}/publish`, { action: 'publish' }).then(res => res.data);
};

export const unpublishAcademyCourse = async (courseId: string) => {
	return await monitoredRequest.patch(`/academy/courses/${courseId}/publish`, { action: 'unpublish' }).then(res => res.data);
};

export const archiveAcademyCourse = async (courseId: string) => {
	return await monitoredRequest.patch(`/academy/courses/${courseId}/archive`, { action: 'archive' }).then(res => res.data);
};

export const unarchiveAcademyCourse = async (courseId: string) => {
	return await monitoredRequest.patch(`/academy/courses/${courseId}/archive`, { action: 'unarchive' }).then(res => res.data);
};

export const getAcademyCourseAnalytics = async (courseId: string) => {
	return await noMonitor.get(`/academy/courses/${courseId}/analytics`).then(res => res.data);
};

export const trackAcademyCourseView = async (courseId: string, trackingData: {
	duration?: number;
	source?: string;
	deviceType?: string;
	location?: { country?: string; region?: string };
}) => {
	return await monitoredRequest.post(`/academy/courses/${courseId}/track-view`, trackingData).then(res => res.data);
};

export const addAcademyCourseReview = async (courseId: string, reviewData: {
	rating: number;
	comment?: string;
}) => {
	return await monitoredRequest.post(`/academy/courses/${courseId}/reviews`, reviewData).then(res => res.data);
};

// Course Voting
export const voteAcademyCourse = async (courseId: string, voteType: 'upvote' | 'downvote') => {
	return await monitoredRequest.post(`/academy/courses/${courseId}/vote`, { voteType }).then(res => res.data);
};

export const removeAcademyCourseVote = async (courseId: string) => {
	return await monitoredRequest.delete(`/academy/courses/${courseId}/vote`).then(res => res.data);
};

export const getAcademyCourseVoting = async (courseId: string) => {
	return await noMonitor.get(`/academy/courses/${courseId}/voting`).then(res => res.data);
};

// Module Management
export const addAcademyModule = async (courseId: string, moduleData: any) => {
	return await monitoredRequest.post(`/academy/courses/${courseId}/modules`, moduleData).then(res => res.data);
};

export const getAcademyModule = async (courseId: string, moduleId: string, trackView: boolean = false) => {
	return await noMonitor.get(`/academy/courses/${courseId}/modules/${moduleId}`, {
		params: { trackView }
	}).then(res => res.data);
};

export const updateAcademyModule = async (courseId: string, moduleId: string, updateData: any) => {
	return await monitoredRequest.put(`/academy/courses/${courseId}/modules/${moduleId}`, updateData).then(res => res.data);
};

export const deleteAcademyModule = async (courseId: string, moduleId: string) => {
	return await monitoredRequest.delete(`/academy/courses/${courseId}/modules/${moduleId}`).then(res => res.data);
};

export const reorderAcademyModules = async (courseId: string, moduleOrders: Record<string, number>) => {
	return await monitoredRequest.patch(`/academy/courses/${courseId}/modules/reorder`, { moduleOrders }).then(res => res.data);
};

export const trackAcademyModuleView = async (courseId: string, moduleId: string, duration: number = 0) => {
	return await monitoredRequest.post(`/academy/courses/${courseId}/modules/${moduleId}/track-view`, { duration }).then(res => res.data);
};

export const trackAcademyModuleCompletion = async (courseId: string, moduleId: string, completionTime: number = 0) => {
	return await monitoredRequest.post(`/academy/courses/${courseId}/modules/${moduleId}/complete`, { completionTime }).then(res => res.data);
};

export const updateAcademyModuleContent = async (courseId: string, moduleId: string, content: string, contentType?: string) => {
	return await monitoredRequest.put(`/academy/courses/${courseId}/modules/${moduleId}/content`, { content, contentType }).then(res => res.data);
};

export const addAcademyModuleResource = async (courseId: string, moduleId: string, resourceData: any) => {
	return await monitoredRequest.post(`/academy/courses/${courseId}/modules/${moduleId}/resources`, resourceData).then(res => res.data);
};

export const removeAcademyModuleResource = async (courseId: string, moduleId: string, resourceId: string) => {
	return await monitoredRequest.delete(`/academy/courses/${courseId}/modules/${moduleId}/resources/${resourceId}`).then(res => res.data);
};

// Public Academy Routes
export const getPublicAcademyCourses = async (params: {
	page?: number;
	limit?: number;
	category?: string;
	level?: string;
	search?: string;
	sortBy?: string;
	sortOrder?: 'asc' | 'desc';
	isPublic?: boolean;
	isPublished?: boolean;
} = {}) => {
	return await noMonitor.get('/academy/public/courses', { params }).then(res => res.data);
};

export const getPublicAcademyCourse = async (courseId: string, trackView: boolean = false, deviceType: string = 'desktop', source: string = 'direct') => {
	return await noMonitor.get(`/academy/public/courses/${courseId}`, {
		params: { trackView, deviceType, source }
	}).then(res => res.data);
};

export const getPublicAcademyModule = async (courseId: string, moduleId: string, trackView: boolean = false) => {
	return await noMonitor.get(`/academy/public/courses/${courseId}/modules/${moduleId}`, {
		params: { trackView }
	}).then(res => res.data);
};

export const getPublicAcademyCourseStats = async (courseId: string) => {
	return await noMonitor.get(`/academy/public/courses/${courseId}/stats`).then(res => res.data);
};

// Public Learning Route - Access course via /learn/:id URL
export const getAcademyCourseByLearnUrl = async (courseId: string) => {
	return await noMonitor.get(`/academy/learn/${courseId}`).then(res => res.data);
};

// Search and Discovery
export const searchAcademyCourses = async (params: {
	q?: string;
	category?: string;
	level?: string;
	sortBy?: 'relevance' | 'newest' | 'oldest' | 'rating' | 'popular' | 'enrollments';
	page?: number;
	limit?: number;
}) => {
	return await noMonitor.get('/academy/search', { params }).then(res => res.data);
};

export const getAcademyCoursesByCategory = async (category: string, params: {
	page?: number;
	limit?: number;
	sortBy?: 'popular' | 'rating' | 'newest';
} = {}) => {
	return await noMonitor.get(`/academy/categories/${category}`, { params }).then(res => res.data);
};

export const getFeaturedAcademyCourses = async (limit: number = 10) => {
	return await noMonitor.get('/academy/featured', { params: { limit } }).then(res => res.data);
};

export const getTrendingAcademyCourses = async (limit: number = 10, timeRange: '1d' | '7d' | '30d' = '7d') => {
	return await noMonitor.get('/academy/trending', { params: { limit, timeRange } }).then(res => res.data);
};

// Analytics and Tracking
export const trackAcademyEngagement = async (courseId: string, engagementData: {
	action: 'view' | 'enroll' | 'complete' | 'review' | 'share';
	moduleId?: string;
	duration?: number;
	metadata?: any;
}) => {
	return await monitoredRequest.post(`/academy/courses/${courseId}/track-engagement`, engagementData).then(res => res.data);
};

export const getAcademyUserProgress = async (courseId: string) => {
	return await noMonitor.get(`/academy/courses/${courseId}/progress`).then(res => res.data);
};

export const updateAcademyUserProgress = async (courseId: string, progressData: {
	moduleId: string;
	completed: boolean;
	progress: number; // 0-100
	timeSpent: number;
}) => {
	return await monitoredRequest.post(`/academy/courses/${courseId}/progress`, progressData).then(res => res.data);
};

// Course Enrollment
export const enrollInAcademyCourse = async (courseId: string) => {
	return await monitoredRequest.post(`/academy/courses/${courseId}/enroll`).then(res => res.data);
};

export const unenrollFromAcademyCourse = async (courseId: string) => {
	return await monitoredRequest.delete(`/academy/courses/${courseId}/enroll`).then(res => res.data);
};

export const getAcademyEnrollments = async (params: {
	page?: number;
	limit?: number;
	status?: 'active' | 'completed' | 'dropped';
} = {}) => {
	return await noMonitor.get('/academy/enrollments', { params }).then(res => res.data);
};

// Course Content Upload
export const uploadAcademyCourseThumbnail = async (courseId: string, imageData: string, fileName: string, mimeType: string) => {
	return await monitoredRequest.post(`/academy/courses/${courseId}/upload-thumbnail`, {
		imageData,
		fileName,
		mimeType
	}).then(res => res.data);
};

export const uploadAcademyCourseBanner = async (courseId: string, imageData: string, fileName: string, mimeType: string) => {
	return await monitoredRequest.post(`/academy/courses/${courseId}/upload-banner`, {
		imageData,
		fileName,
		mimeType
	}).then(res => res.data);
};

export const createDocumentTemplate = async (templateData: any) => {
	return await monitoredRequest.post('templates/documents/post-template', templateData).then(res => res.data);
};

export const updateDocumentTemplate = async (templateId: string, templateData: any) => {
	return await monitoredRequest.put(`/templates/documents/${templateId}`, templateData).then(res => res.data);
};

export const uploadAcademyModuleResource = async (courseId: string, moduleId: string, fileData: FormData) => {
	return await monitoredRequest.post(`/academy/courses/${courseId}/modules/${moduleId}/upload-resource`, fileData, {
		headers: { 'Content-Type': 'multipart/form-data' }
	}).then(res => res.data);
};

export const generateChatTitleSuggestions = async (firstMessage: string, messages: Array<{ role: string; content: string }> = []) => {
	return await noMonitor.post('/ai/chat/generate-title-suggestions', { firstMessage, messages }, { timeout: 120000 }).then(res => res.data);
};

// AI Image Enhancement
export const enhanceProductImage = async (imageUrl?: string, imageBase64?: string, enhancementType: string = 'upscale', scale: number = 2, prompt?: string, style?: string) => {
	return await monitoredRequest.post('/ai/enhance-product-image', {
		imageUrl,
		imageBase64,
		enhancementType,
		scale,
		prompt,
		style
	}, { timeout: 120000 }).then(res => res.data);
};

// AI Image Analysis using LLaVA 13B
export const analyzeImage = async (options: {
	image?: string; // base64 encoded image
	imageUrl?: string; // URL to image
	imageFile?: File | Blob; // File object for upload
	prompt?: string; // Custom analysis prompt
	stream?: boolean; // Whether to stream the response
}) => {
	const { image, imageUrl, imageFile, prompt, stream } = options;

	// If imageFile is provided, use FormData
	if (imageFile) {
		const formData = new FormData();
		formData.append('image', imageFile);
		if (prompt) formData.append('prompt', prompt);
		if (stream) formData.append('stream', stream.toString());

		return await monitoredRequest.post('/ai/analyze-image', formData, {
			timeout: 120000,
			headers: { 'Content-Type': 'multipart/form-data' }
		}).then(res => res.data);
	}

	// Otherwise, send as JSON
	return await monitoredRequest.post('/ai/analyze-image', {
		image,
		imageUrl,
		prompt,
		stream
	}, { timeout: 120000 }).then(res => res.data);
};

// Check image analysis service health
export const checkImageAnalysisHealth = async () => {
	return await noMonitor.get('/ai/analyze-image/health').then(res => res.data);
};


/**
 * Trim context entries to fit within limits (keeping most recent entries)
 * Limits: 500k tokens, 500k bytes, 10k entries
 */
const trimContextEntries = (entries: any[]): { trimmed: any[], originalCount: number } => {
	const MAX_PAYLOAD_TOKENS = 500000;
	const MAX_PAYLOAD_BYTES = 500000;
	const MAX_ENTRIES = 10000;

	// Calculate total size
	let totalTokens = 0;
	let totalBytes = 0;
	for (const entry of entries) {
		totalTokens += entry.tokenCount || 0;
		totalBytes += entry.byteCount || 0;
	}

	// If already within limits, return as-is
	if (totalTokens <= MAX_PAYLOAD_TOKENS && totalBytes <= MAX_PAYLOAD_BYTES && entries.length <= MAX_ENTRIES) {
		return { trimmed: entries, originalCount: entries.length };
	}

	// Start from the end (most recent entries) and work backwards
	let trimmedTokens = 0;
	let trimmedBytes = 0;
	const trimmedEntries: any[] = [];

	// Process entries in reverse order (most recent first)
	for (let i = entries.length - 1; i >= 0; i--) {
		const entry = entries[i];
		const entryTokens = entry.tokenCount || 0;
		const entryBytes = entry.byteCount || 0;

		// Check if adding this entry would exceed limits
		if (
			trimmedEntries.length >= MAX_ENTRIES ||
			trimmedTokens + entryTokens > MAX_PAYLOAD_TOKENS ||
			trimmedBytes + entryBytes > MAX_PAYLOAD_BYTES
		) {
			break;
		}

		trimmedEntries.unshift(entry); // Add to beginning to maintain chronological order
		trimmedTokens += entryTokens;
		trimmedBytes += entryBytes;
	}

	if (trimmedEntries.length < entries.length) {
		console.log(`[ContextSummary] Client-side trimming: ${entries.length} → ${trimmedEntries.length} entries (${totalTokens} → ${trimmedTokens} tokens, ${totalBytes} → ${trimmedBytes} bytes)`);
	}

	return { trimmed: trimmedEntries, originalCount: entries.length };
};

export const summarizeContext = async (contextEntries: any[]) => {
	// Trim entries on client side to prevent 413 errors
	const { trimmed, originalCount } = trimContextEntries(contextEntries);

	return await api.post('/ai/context/summarize', { entries: trimmed }, { timeout: 120000 })
		.then(res => {
			// Validate response structure
			if (!res.data || !res.data.success) {
				const error = new Error(res.data?.error || 'Invalid response from summarization service');
				(error as any).response = { status: res.status, data: res.data };
				throw error;
			}
			return res.data;
		})
		.catch(error => {
			// Handle specific error cases
			if (error.response?.status === 403) {
				const premiumError = new Error(error.response?.data?.error || 'Premium subscription required');
				(premiumError as any).requiresPremium = true;
				(premiumError as any).response = error.response;
				throw premiumError;
			}
			if (error.response?.status === 413) {
				// If still getting 413, the payload is too large even after trimming
				// This shouldn't happen, but handle it gracefully
				const payloadError = new Error('Payload too large even after trimming. Please reduce context size.');
				(payloadError as any).response = error.response;
				throw payloadError;
			}
			if (error.response?.status === 503) {
				const serviceError = new Error(error.response?.data?.error || 'Service temporarily unavailable');
				(serviceError as any).retryable = true;
				(serviceError as any).response = error.response;
				throw serviceError;
			}
			console.error('Failed to summarize context:', error);
			throw error;
		});
}
// summarizeContext is exported from apisObject below


const apisObject = {
	getBounties,
	updateDataItemHistory,
	getBountySchema,
	addDataDescription,
	deleteAccount,
	addUserToDepartment,
	createAffiliateCode,
	aiCommentAnalysis,
	uploadGlobalAsset,
	getCustomerPortal,
	getStoreInfo,
	addUserToCompany,
	getCompanyList,
	revokeInvitation,
	acceptInvitation,
	rejectInvitation,
	addCompany,
	getDataTypes,
	switchCompany,
	getActivityStreamLog,
	getAllUsers,
	getStream,
	logoutUser,
	registerUser,
	setCurrentUser,
	setUserLoading,
	retrieveQueriesByID,
	insertQueryHistory,
	getLocalWeather,
	postAvatar,
	sendUserReg,
	getLocalNews,
	getUserConversations,
	getConversation,
	postMessage,
	unlockUser,
	getCompanyInformation,
	getCompanyInformationName,
	authoriseLinkedIn,
	companyCheck,
	getLargeProfile,
	postLogo,
	quoteToday,
	mailLogin,
	getProject,
	getDefaultWebStats,
	getAllGmail,
	getGmailCount,
	getGDriveItemsList,
	getGDriveFolderContent,
	postDeskImage,
	createPaymentIntent,
	genKnowledge,
	refreshAchievements,
	addTwofa,
	retrieveCompanyBillingInfo,
	passwordReset,
	dispatchAppAction,
	validatePasswordReset,
	getLoginMessages,
	getData,
	getProjects,
	getProjectsPaginated,
	addProjects,
	updateProject,
	getProjectSchema,
	getAddNetworkSchema,
	getTaskSchema,
	getCalendarSchema,
	retrieveUserBillingInfo,
	getProducts,
	getProductById,
	getSession,
	getUserCompanies,
	getSampleSchemas,
	saveData,
	getDataContent,
	createDatabaseConnection,
	getDatabaseConnections,
	getDatabaseConnection,
	updateDatabaseConnection,
	deleteDatabaseConnection,
	testDatabaseConnection,
	getDatabaseTables,
	importDatabaseSchema,
	getSentInvoices,
	handleOAuth,
	paymentSheet,
	generateProductPaymentLink,
	getSubscriptions,
	getIntegrations,
	getIntegrationSuggestions,
	createIntegrationRequest,
	getIntegrationRequests,
	getPopularIntegrationRequests,
	getIntegrationRequestStats,
	voteOnIntegrationRequest,
	updateIntegrationRequestStatus,
	authoriseGMail,
	addDataItem,
	getProfileSchema,
	getUserSocialMedia,
	updateUserSocialMedia,
	addUserTags,
	updateUserProfile,
	updateUserContext,
	updateUserDepartment,
	assignUserToDepartment,
	getUserBranding,
	updateUserBranding,
	uploadBrandingImage,
	updateNotificationToken,
	getAvatarById,
	addTask,
	searchUsers,
	getReport,
	markRead,
	getTags,
	addCalendarEntry,
	updateCalendarEntry,
	removeCalendarEntry,
	getCalendarContent,
	getProductList,
	getWPSchema,
	addWorkPackage,
	deleteDataItem,
	deleteProject,
	getProfileCompletion,
	getProjectCount,
	getDataCount,
	saveNewsItem,
	getSavedNews,
	addCalendar,
	sendGoogleMail,
	addStickyNote,
	modifyStickyNote,
	removeStickyNote,
	getStickyNotes,
	markNoteComplete,
	setNoteColor,
	getInitialModels,
	identifyOnboardingModels,
	manipulateHierarchy,
	postInitialModel,
	getGoogleCalendarList,
	getStandardisedReports,
	validate2FA,
	validateOTP,
	getDataSchema,
	setTutorial,
	getRoomCounts,
	getRoom,
	updateTC,
	getActivityLog,
	getAchievementHistory,
	shareDataItem,
	getUser,
	getInvitations,
	getInvitationCount,
	getRemainingProfile,
	getPublicData,
	acceptCompanyInvitation,
	updateDataItem,
	uploadData,
	setPublicDataSwipeTutorial,
	setInvitationSwipeTutorial,
	addLocation,
	hideFirstTutorial,
	saveEmailTemplate,
	getEmailTemplates,
	getInvoiceTemplates,
	getSentInvoices,
	getInvoiceById,
	getDocumentTemplates,
	getInitialTemplates,
	deletePost,
	getEmailTemplate,
	editEmailTemplate,
	deleteEmailTemplate,
	getServiceList,
	getUserById,
	getPublicProfile,
	trackFormView,
	trackFormSubmission,
	trackDataContribution,
	trackDataInteraction,
	changeWPStatus,
	changeWorkPackageTitle,
	deleteWorkPackage,
	changeTaskTitle,
	changeTaskDescription,
	deleteTask,
	changeTaskStatus,
	moveTask,
	bulkMoveTasks,
	changeProjectTitle,
	changeProjectDescription,
	shareProject,
	addFavourite,
	getDataFavorites,
	refreshUser,
	addTagsToData,
	generateBusinessCard,
	addLocalCalendars,
	addDataImage,
	getDataContentById,
	getAddInvoiceTemplateSchema,
	postInvoiceTemplate,
	getFeedbackSchema,
	getFeedback,
	postFeedback,
	getProductServiceList,
	searchRegEx,
	getIntegrationApiAction,
	createStorePaymentIntent,
	getStripeId,
	getStoreSettings,
	updateStoreSettings,
	registerStripeLocation,
	registerStripeReader,
	confirmPaymentIntent,
	getAIForm,
	getFeed,
	getExternalAds,
	trackAdImpression,
	trackAdClick,
	postToFeed,
	addLikeToPost,
	addShareToPost,
	removeLikeFromPost,
	removeShareFromPost,
	addCommentToPost,
	getAICommentAnalysis,
	getExtendedData,
	getUserHierarchy,
	getCompanySchema,
	storeDeviceToken,
	getDataFolders,
	getFolderStats,
	addPOIsToDataModel,
	scrapeArticleToKnowledge,
	replaceSchema,
	getDataItemById,
	retrieveProduct,
	getCheckoutSession,
	createSubscriptionPaymentSheet,
	changeSubscription,
	syncCalendars,
	syncCalendarEvents,
	getCalendars,
	makeDataPublic,
	updateWorkPackage,
	getKnowledgeBase,
	getInternalKnowledgePaginated,
	genEmail,
	getContributionChart,
	getAIReportQuery,
	searchMail,
	googleAuthCallback,
	buildLink,
	sendInvoice,
	getInventoryData,
	getAddRoomSchema,
	getSalesStats,
	getCrmCount,
	getActiveCampaigns,
	postNewCampaign,
	getDefaultEmailTemplates,
	postInternalKnowledge,
	postExternalKnowledge,
	getInternalKnowledge,
	deleteKnowledgeArticle,
	saveKnowledgeContent,
	publishKnowledgeArticle,
	getKnowledgeContent,
	getTeam,
	getCRMFolders,
	getTemplates,
	updateWorkPackageDue,
	getCampaign,
	changeTaskNotes,
	searchDataRegEx,
	handleChangeTaskDescription,
	handleChangeTaskTitle,
	changeTaskPosition,
	modifyTask,
	googleOAuth,
	getDataTemplateCount,
	updateEmailTemplate,
	setEmailGlobal,
	getGlobalAssets,
	getTemplateById,
	getTargetAudience,
	revokeGoogleScope,
	searchCompanies,
	googleMapSearch,
	googleMapSearchPaginated,
	getUserRecents,
	addRecent,
	getTrending,
	getUserStats,
	getUserStatsData,
	getApiCallHistory,
	getApiCallsOverTime,
	getConnectedIntegrationsCount,
	getKnowledgeByViews,
	cloneData,
	transferData,
	cancelUserCreation,
	userLoginHistory,
	getCrmItems,
	uploadDataImage,
	getAllFolderStats,
	getMainCharts,
	requestDataDownload,
	getStartupSchemas,
	startCampaign,
	changeDataTitle,
	requestLastPage,
	getProductsInternal,
	requestNextPage,
	searchGmail,
	addDepartment,
	setCompanyName,
	addCompanyLocation,
	resetCompanyPositions,
	updateCalendarSettings,
	postDataNote,
	deleteDataNote,
	updateDataActions,
	removeIntegration,
	retrieveCompanyUserStats,
	getAffiliateHistory,
	getStatCounts,
	getMeetings,
	getRecentMeetings,
	getMeetingSchema,
	transcribeMeeting,
	modifyFolder,
	getCallFlows,
	createCallFlow,
	getUserPosts,
	// Competitor Analysis APIs
	createCompetitor,
	getCompetitors,
	getCompetitor,
	updateCompetitor,
	deleteCompetitor,
	discoverCompetitorSocial,
	confirmCompetitorSocial,
	getCompetitorSocial,
	getSuggestedCompetitors,
	analyzeCompetitorContent,
	getCompetitorInsights,
	getCompetitorStrategy,
	getCompetitorPosts,
	updateBilling,
	bumpViewCount,
	likeComment,
	// Feed Boost functions
	getBoostPricing,
	createPostBoost,
	pausePostBoost,
	resumePostBoost,
	cancelPostBoost,
	getPostBoostAnalytics,
	getAllUserBoosts,
	trackBoostInteraction,
	createBounty,
	flagItem,
	getFlaggedItems,
	getTrackedItemHistory,
	getExpiredCampaigns,
	getCompletedProjects,
	addIntegration,
	sendTestEmail,
	testWhatsApp,
	addSSCal,
	getWholeCalendarSchema,
	authManMail,
	getMailBoxes,
	setDefaultMailbox,
	deleteMailbox,
	getAffiliateDetails,
	deleteCalendar,
	analyseDataset,
	getMapList,
	testNotice,
	extractResume,
	getResumeTemplates,
	applyResumeTemplate,
	cancelApplePlan,
	cancelPlayPlan,
	cancelStripePlan,
	getWhatsAppProfile,
	getWhatsappTemplates,
	updateWhatsAppConversationModelSettings,
	sendTemplateMessage,
	moveDataItem,
	verifyCouponCode,
	getStripeProfile,
	syncShopifyProducts,
	getStripeSalesStats,
	getStripeMonthlySalesOverview,
	getStripeRecentTransactions,
	getStripePaymentMethodStats,
	getPayStackSalesStats,
	getPayStackMonthlySalesOverview,
	getPayStackRecentTransactions,
	getPayStackPaymentMethodStats,
	getXProfile,
	getLinkedInProfile,
	scheduleToFeed,
	scheduleMultipleSocialPosts,
	cancelScheduledPost,
	getScheduledPosts,
	updateScheduledPost,
	getAffiliateCode,
	getWordpressProfile,
	getWordPressBlogDetails,
	buildProductContent,
	deleteData,
	createDocumentTemplate,
	updateDocumentTemplate,
	getSlackProfile,
	connectPlatform,
	connectPlatformCallback,
	getAINotes,
	changePublishSettings,
	getInstagramProfile,
	getInstagramMedia,
	registerLinkedIn,
	getFacebookProfile,
	getYoutubeProfile,
	getYoutubeVideos,
	clearModalTimeout,
	reportError,
	trackUsage,
	getUsageStats,
	getMostUsedItems,
	bulkTrackUsage,
	// Timeline CRM functions
	getTimelineData,
	addContactNote,
	addContactTask,
	toggleTask,
	logInteraction,
	updateContactMetrics,
	analyzeCustomerHealth,
	getCachedCustomerHealthAnalysis,
	getCRMTimelineFolders,
	// Product Management functions
	getProductDetails,
	addProductSupplier,
	getProductSuppliers,
	updateProductSupplier,
	deleteProductSupplier,
	updateProductPricing,
	addQualityCheck,
	getProductAnalytics,
	getProductInventory,
	updateProductInventory,
	trackDataView,
	getDataStats,
	// Utility Functions
	saveUtility,
	getUtilitiesForData,
	getUtility,
	updateUtility,
	deleteUtility,
	linkUtilitiesToData,
	executeUtility,
	// Advanced Analytics
	getDataInsights,
	getFolderComparison,
	getPredictiveAnalytics,
	getPlanProducts,
	getPlanSubscriptions,
	createNewAppSubscription,
	registerAndPay,
	// Networking APIs
	getNetworkingBusinesses,
	getNetworkingBusinessById,
	searchNetworkingBusinesses,
	verifyOTPCode,
	// Modern Project Management APIs
	moveKanbanTask,
	bulkKanbanActions,
	getKanbanColumns,
	getGanttData,
	updateGanttTimeline,
	manageTaskDependencies,
	getProjectAnalytics,
	getProgressAnalytics,
	getTeamPerformanceAnalytics,
	getTimeTrackingAnalytics,
	addTeamMember,
	inviteTeamMember,
	updateTeamMemberRole,
	removeTeamMember,
	uploadProjectFiles,
	getProjectFiles,
	deleteProjectFile,
	downloadProjectFile,
	updateProjectSettings,
	getProjectTasks,
	bulkCreateTasks,
	addTaskComment,
	getTaskComments,
	logTaskTime,
	sendDocumentEmail,
	getDocumentShareHistory,
	// Custom Views API
	getCustomViews,
	getCustomView,
	saveCustomView,
	deleteCustomView,
	duplicateCustomView,
	getCustomViewTemplates,
	// AI Project Creation
	createAIProject,
	generateAIProjectFromDescription,
	getAIProjectTemplates,
	// Paginated Data APIs
	getDataContentPaginated,
	getDataFoldersPaginated,
	getAllMessagingUsers,
	createAPIKey,
	getAPIKeys,
	deleteAPIKey,
	toggleAPIKey,
	createAIDataModel,
	regenerateAPIKey,
	createAIModelKey,
	getAIModelKeys,
	updateAIModelKey,
	deleteAIModelKey,
	toggleAIModelKey,
	getOAuthTokens,
	revokeOAuthToken,
	createWebhook,
	getWebhooks,
	getAvailableEvents,
	updateWebhook,
	deleteWebhook,
	toggleWebhook,
	regenerateWebhookSecret,
	testWebhook,
	getUserStatsTrends,
	// Chat Management
	saveChat,
	getUserChats,
	getChatById,
	updateChatTitle,
	toggleChatArchive,
	deleteChat,
	getUserChatStats,
	addMessageToChat,
	// AI Chat Title Generation
	generateChatTitle,
	generateChatTitleSuggestions,
	// AI Image Enhancement
	enhanceProductImage,
	// AI Email Template Generation
	generateEmailTemplateWithAI,
	trackDataSchemaInteraction,
	suggestEmailTemplates,
	// AI Document Template Generation
	generateDocumentTemplateWithAI,
	// Academy Course Management
	createAcademyCourse,
	updateAcademyCourse,
	getAcademyCourse,
	publishAcademyCourse,
	unpublishAcademyCourse,
	updateAcademyModule,
	updateAcademyModuleContent,
	generateAIImage: async (prompt: string) => {
		return await noMonitor.post('/ai/canvas/generate-image', { prompt }).then(res => res.data);
	},
	getUserDatasForFlow: async (flowId: string) => {
		return await noMonitor.get(`/ai/flow/${flowId}/user-datas`).then(res => res.data);
	},
	getCalendarsForFlow: async (flowId: string) => {
		return await noMonitor.get(`/ai/flow/${flowId}/calendars`).then(res => res.data);
	},
	getCalendarSlots: async (flowId: string, calendarId: string, date: string, duration: number, buffer: number) => {
		return await noMonitor.get(`/ai/flow/${flowId}/calendar/${calendarId}/slots`, {
			params: { date, duration, buffer }
		}).then(res => res.data);
	},
	createFlowAppointment: async (flowId: string, sessionId: string, appointmentData: any) => {
		return await noMonitor.post(`/ai/flow/${flowId}/test/${sessionId}/appointment`, appointmentData).then(res => res.data);
	},
	// Flow CRUD Operations
	createFlow: async (flowData: any) => {
		return await noMonitor.post('/flows/create-flow', { flowData }).then(res => res.data);
	},
	updateFlow: async (flowId: string, flowData: any) => {
		return await noMonitor.put(`/flows/update-flow/${flowId}`, { flowData }).then(res => res.data);
	},
	// Workflow Management APIs
	getWorkflows: async (params?: { active?: boolean; category?: string; tags?: string }) => {
		const queryParams = new URLSearchParams();
		if (params?.active !== undefined) queryParams.append('active', String(params.active));
		if (params?.category) queryParams.append('category', params.category);
		if (params?.tags) queryParams.append('tags', params.tags);
		const query = queryParams.toString();
		return await api.get(`/workflows${query ? `?${query}` : ''}`).then(res => res.data);
	},
	getWorkflow: async (workflowId: string) => {
		return await api.get(`/workflows/${workflowId}`).then(res => res.data);
	},
	createWorkflow: async (workflowData: any) => {
		return await api.post('/workflows', workflowData).then(res => res.data);
	},
	updateWorkflow: async (workflowId: string, workflowData: any) => {
		return await api.put(`/workflows/${workflowId}`, workflowData).then(res => res.data);
	},
	deleteWorkflow: async (workflowId: string) => {
		return await api.delete(`/workflows/${workflowId}`).then(res => res.data);
	},
	executeWorkflow: async (workflowId: string, triggerData?: any) => {
		return await api.post(`/workflows/${workflowId}/execute`, { triggerData }).then(res => res.data);
	},
	getWorkflowExecutions: async (workflowId: string, params?: { limit?: number; status?: string }) => {
		const queryParams = new URLSearchParams();
		if (params?.limit) queryParams.append('limit', String(params.limit));
		if (params?.status) queryParams.append('status', params.status);
		const query = queryParams.toString();
		return await api.get(`/workflows/${workflowId}/executions${query ? `?${query}` : ''}`).then(res => res.data);
	},
	retryWorkflowExecution: async (workflowId: string, executionId: string) => {
		return await api.post(`/workflows/${workflowId}/retry/${executionId}`).then(res => res.data);
	},
	scheduleWorkflow: async (workflowId: string, cronExpression: string, timezone?: string) => {
		return await api.post(`/workflows/${workflowId}/schedule`, { cronExpression, timezone }).then(res => res.data);
	},
	unscheduleWorkflow: async (workflowId: string) => {
		return await api.delete(`/workflows/${workflowId}/schedule`).then(res => res.data);
	},
	getWorkflowWebhooks: async (workflowId: string) => {
		return await api.get(`/workflows/${workflowId}/webhooks`).then(res => res.data);
	},
	// Agentic Flow APIs
	createAgenticFlow: async (dataId: string, flowConfig: any) => {
		return await api.post('/agentic-flow/create', { dataId, flowConfig }).then(res => res.data);
	},
	getAgenticFlows: async (dataId: string) => {
		return await api.get(`/agentic-flow/${dataId}`).then(res => res.data);
	},
	getAgenticFlow: async (dataId: string, flowId: string) => {
		return await api.get(`/agentic-flow/${dataId}/${flowId}`).then(res => res.data);
	},
	updateAgenticFlow: async (dataId: string, flowId: string, flowConfig: any) => {
		return await api.put(`/agentic-flow/${dataId}/${flowId}`, { flowConfig }).then(res => res.data);
	},
	deleteAgenticFlow: async (dataId: string, flowId: string) => {
		return await api.delete(`/agentic-flow/${dataId}/${flowId}`).then(res => res.data);
	},
	triggerAgenticFlow: async (dataId: string, flowId: string, inputData?: any, context?: any) => {
		return await api.post(`/agentic-flow/${dataId}/${flowId}/trigger`, { inputData, context }).then(res => res.data);
	},
	executeAgenticFlow: async (flowId: string, inputData?: any, context?: any) => {
		return await api.post('/agentic-flow/execute', { flowId, inputData, context }).then(res => res.data);
	},
	validateAgenticFlow: async (flowConfig: any) => {
		return await api.post('/agentic-flow/validate', { flowConfig }).then(res => res.data);
	},
	getAgenticFlowStatus: async (executionId: string) => {
		return await api.get(`/agentic-flow/status/${executionId}`).then(res => res.data);
	},
	// Flow History APIs
	getFlowHistory: async (dataId: string, params?: { limit?: number; offset?: number; status?: string; actionType?: string; triggerType?: string; startDate?: string; endDate?: string }) => {
		const queryParams = new URLSearchParams();
		if (params?.limit) queryParams.append('limit', String(params.limit));
		if (params?.offset) queryParams.append('offset', String(params.offset));
		if (params?.status) queryParams.append('status', params.status);
		if (params?.actionType) queryParams.append('actionType', params.actionType);
		if (params?.triggerType) queryParams.append('triggerType', params.triggerType);
		if (params?.startDate) queryParams.append('startDate', params.startDate);
		if (params?.endDate) queryParams.append('endDate', params.endDate);
		const query = queryParams.toString();
		return await api.get(`/flow-history/${dataId}${query ? `?${query}` : ''}`).then(res => res.data);
	},
	getActionHistory: async (actionId: string, params?: { limit?: number; offset?: number; status?: string; startDate?: string; endDate?: string }) => {
		const queryParams = new URLSearchParams();
		if (params?.limit) queryParams.append('limit', String(params.limit));
		if (params?.offset) queryParams.append('offset', String(params.offset));
		if (params?.status) queryParams.append('status', params.status);
		if (params?.startDate) queryParams.append('startDate', params.startDate);
		if (params?.endDate) queryParams.append('endDate', params.endDate);
		const query = queryParams.toString();
		return await api.get(`/flow-history/action/${actionId}${query ? `?${query}` : ''}`).then(res => res.data);
	},
	getWorkflowHistory: async (workflowId: string, params?: { limit?: number; offset?: number; status?: string; startDate?: string; endDate?: string }) => {
		const queryParams = new URLSearchParams();
		if (params?.limit) queryParams.append('limit', String(params.limit));
		if (params?.offset) queryParams.append('offset', String(params.offset));
		if (params?.status) queryParams.append('status', params.status);
		if (params?.startDate) queryParams.append('startDate', params.startDate);
		if (params?.endDate) queryParams.append('endDate', params.endDate);
		const query = queryParams.toString();
		return await api.get(`/flow-history/workflow/${workflowId}${query ? `?${query}` : ''}`).then(res => res.data);
	},
	getFlowAnalytics: async (dataId: string, params?: { startDate?: string; endDate?: string }) => {
		const queryParams = new URLSearchParams();
		if (params?.startDate) queryParams.append('startDate', params.startDate);
		if (params?.endDate) queryParams.append('endDate', params.endDate);
		const query = queryParams.toString();
		return await api.get(`/flow-history/analytics/${dataId}${query ? `?${query}` : ''}`).then(res => res.data);
	},
	getExecutionDetails: async (executionId: string) => {
		return await api.get(`/flow-history/execution/${executionId}`).then(res => res.data);
	},
	filterExecutions: async (filters: { dataId?: string; actionId?: string; workflowId?: string; status?: string; actionType?: string; triggerType?: string; startDate?: string; endDate?: string; limit?: number; offset?: number }) => {
		return await api.post('/flow-history/filter', filters).then(res => res.data);
	},
	// Workflow AI APIs
	generateWorkflowFromNL: async (description: string, context?: any, availableIntegrations?: string[]) => {
		return await api.post('/workflows/ai/generate', { description, context, availableIntegrations }).then(res => res.data);
	},
	optimizeWorkflow: async (workflow: any) => {
		return await api.post('/workflows/ai/optimize', { workflow }).then(res => res.data);
	},
	blockCalendarDates: async (payload: {
		calendarId: string;
		startDate: string;
		endDate: string;
		reason?: string;
		notes?: string;
	}) => {
		if (!payload?.calendarId || !payload?.startDate || !payload?.endDate) {
			return dispatch({
				type: SHOW_AUTH_MODAL,
				data: {
					content: 'Calendar, start date and end date are required to block dates',
					type: 'error'
				}
			});
		}

		return await monitoredRequest
			.post('/calendar/block-dates', payload)
			.then(res => res.data)
			.catch(error => {
				const errorMessage = error?.response?.data?.error || 'Could not block dates';
				dispatchErrorModal(errorMessage, error?.response?.data);
				throw error;
			});
	},
	createMultiDayEvent: async (payload: {
		calendarId: string;
		title?: string;
		startDate: string;
		endDate: string;
		allDay?: boolean;
		description?: string;
		location?: string;
		attendees?: any[];
		availability?: string;
		notes?: string;
		color?: string;
	}) => {
		if (!payload?.calendarId || !payload?.startDate || !payload?.endDate) {
			return dispatch({
				type: SHOW_AUTH_MODAL,
				data: {
					content: 'Please select a calendar and provide both start and end dates',
					type: 'error'
				}
			});
		}

		return await monitoredRequest
			.post('/calendar/create-multi-day-event', payload)
			.then(res => res.data)
			.catch(error => {
				const errorMessage = error?.response?.data?.error || 'Could not create multi-day event';
				dispatchErrorModal(errorMessage, error?.response?.data);
				throw error;
			});
	},
	// Document Folder APIs
	createDocumentFolder,
	getDocumentFolders,
	deleteDocumentFolder,
	moveDocumentToFolder,
	// User Document APIs
	uploadUserDocument,
	getUserUploadedDocuments,
	deleteUserDocument,
	// Context Window APIs
	summarizeContext,

	getContextStats: async () => {
		return await api.get('/ai/context/stats', { timeout: 120000 })
			.then(res => res.data)
			.catch(error => {
				console.error('Failed to get context stats:', error);
				throw error;
			});
	},
	updateContextSettings: async (settings: any) => {
		return await api.post('/ai/context/settings', settings, { timeout: 120000 })
			.then(res => res.data)
			.catch(error => {
				console.error('Failed to update context settings:', error);
				throw error;
			});
	},
	// Hugging Face Integration APIs
	exportDataModelToHF,
	getHuggingFaceProfile,
	storeHuggingFaceToken,
	removeHuggingFaceToken,
	getHuggingFaceModels,
	initiateHuggingFaceTraining,
	getHuggingFaceTrainingHistory,
	getHuggingFaceTrainingStatus,
	// PayStack Integration APIs
	getPayStackProfile,
	storePayStackKeys,
	removePayStackKeys,
	getHRDashboard,
	getEmployees,
	getLeaveRequests,
	getAttendance,
	getPayroll,
	getPerformanceReviews,
	syncPayrollToAccounting,
	getAccountingIntegrations,
	connectAccountingSoftware,
	disconnectAccountingSoftware,
	// Spec Registry APIs
	getEnrichedSpec: async (integrationId: string, version?: string) => {
		const url = version
			? `/spec-registry/${integrationId}?version=${version}`
			: `/spec-registry/${integrationId}`;

		return await api.get(url)
			.then(res => res.data)
			.catch(error => {
				console.error('Failed to get enriched spec:', error);
				throw error;
			});
	},
	getSpecDiff: async (integrationId: string, fromVersion: string, toVersion: string) => {
		return await api.get(
			`/spec-registry/${integrationId}/diff?from=${fromVersion}&to=${toVersion}`
		)
			.then(res => res.data)
			.catch(error => {
				console.error('Failed to get spec diff:', error);
				throw error;
			});
	},
	getIntegrationEndpointData: async (
		integrationId: string,
		endpointPath: string,
		method: string = 'GET',
		body?: any
	) => {
		// This would call the actual integration endpoint
		// Implementation depends on how integrations are proxied
		return await api.request({
			method,
			url: `/integrations/${integrationId}/proxy${endpointPath}`,
			data: body
		})
			.then(res => res.data)
			.catch(error => {
				console.error('Failed to fetch integration endpoint data:', error);
				throw error;
			});
	},
	getSpecVersions: async (integrationId: string) => {
		return await api.get(`/spec-registry/${integrationId}/versions`)
			.then(res => res.data.versions)
			.catch(error => {
				console.error('Failed to get spec versions:', error);
				throw error;
			});
	},
	saveDismissedSavePrompts,
	getDismissedSavePrompts,
	// Notification Log APIs
	getUserNotificationLogs: async (limit: number = 500) => {
		return await api.get(`/notification-logs/user?limit=${Math.min(limit, 500)}`)
			.then(res => res.data)
			.catch(error => {
				console.error('Failed to get user notification logs:', error);
				throw error;
			});
	},
	getCompanyNotificationLogs: async (limit: number = 5000) => {
		return await api.get(`/notification-logs/company?limit=${Math.min(limit, 5000)}`)
			.then(res => res.data)
			.catch(error => {
				console.error('Failed to get company notification logs:', error);
				throw error;
			});
	},
	// Dashboard Management APIs
	suggestDashboardComponents,
	createDashboard,
	getUserDashboards,
	getDashboard,
	updateDashboard,
	deleteDashboard,
	// Document Creation APIs
	createDocumentLayout,
	generateDocumentContent,
	refineDocumentDesign,
	validateDocument,
	// Generic HTTP methods for dynamic API calls
	post: async (url: string, data?: any, config?: any) => {
		return await noMonitor.post(url, data, config).then(res => res.data);
	},
	get: async (url: string, config?: any) => {
		return await noMonitor.get(url, config).then(res => res.data);
	},
}

export { clearModalTimeout };
export default apisObject;

